import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api, { logAnalyticsEvent, isNetworkShapedError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Upload, Trash2, Loader2, Play, AlertCircle, AlertTriangle, Clock, Crown, ImageIcon, X, Info, UserPlus, ShoppingCart, ArrowRight, Mail, RefreshCw, ArrowLeft, Video, Search, Lock, ListChecks, Pencil, RotateCw, Sparkles } from 'lucide-react';
// tutor-2 (D.1): el atajo "Preguntar al tutor" del menu de tres puntos.
import { tutorVisible, useTutorDeLaSesion } from '../lib/tutorDeLaSesion';
import { documentosElegibles } from '../components/tutor/BasadoEn';
import { toast } from 'sonner';
import { useSecurity } from '../hooks/useSecurity';
import { redirectToGoogleLogin } from '../lib/googleAuth';
import { ordenarIdiomas } from '../lib/ordenDeIdiomas';
import { redirectToAppleLogin } from '../lib/appleAuth';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { useNativeSocialLogin } from '../hooks/useNativeSocialLogin';
import { useSoltarAlRegresar } from '../hooks/useSoltarAlRegresar';
import { INNER_SCROLL_CLEARANCE_CLASS, PANE_FULL_BLEED_HEIGHT_CLASS } from '../lib/bottomNavMetrics';
import { trackEvent } from '../lib/analytics';
import { retryErrorToast } from '../lib/retryErrorToast';
import { insufficientCreditsCopy } from '../lib/insufficientCreditsCopy';
import CreditPackModal from '../components/CreditPackModal';
import PackSuccessModal from '../components/PackSuccessModal';
import AutoSplitModal from '../components/AutoSplitModal';
import LegalAcceptance from '../components/LegalAcceptance';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { usePendingSummary, notifyPendingChanged } from '../hooks/usePendingSummary';
import { shouldRefetchOnRefocus } from '../hooks/refocusThrottle';
import { subscribePlanChanged, consumeRecentPlanChange } from '../lib/planSync';
import { formatDateLong } from '../lib/dateFormat';
// OCR-ALUMNO (PR #347, §C): las fotos del alumno — el panel y sus helpers puros.
import PanelFotos from '../components/PanelFotos';
import VentanaDeFotos from '../components/panel/VentanaDeFotos';
import RenglonDelParcial from '../components/panel/RenglonDelParcial';
import { ACCEPT_SUBIDA, MAX_FOTOS, esFoto, nombrePrellenado, alternarEnOrden, redimensionarFoto } from '../lib/fotosAlumno';
import DowngradeBanner, { notifyDocsChanged } from '../components/DowngradeBanner';
import AvisoMudanza, { AvisoMarca } from '../components/panel/AvisoMudanza';
// LAPIDA post-t7-2c (D1, #640): murio el import de `AppStoreBloque`: el Panel ya no monta su tarjeta.
// LAPIDA post-t7-2c (E1): murio el aviso de referidos (`AvisoReferidos`) y con el la vista de
// administrador del Panel; en su sitio iba el cuadro de opinion (E2, Anexo C.1).
// LAPIDA del remate de post-t7-2c (R1, "1. si" de Carlos del 15-oct): el cuadro de opinion SALE del Panel y vive en
// su pantalla propia, "Gana creditos gratis" (pages/EarnCreditsPage.js, renglon del menu lateral). El Panel ya no lo
// monta ni pide su estado, y arriba pinta lo de antes de la 2c sin el cuadro.
import { sortLibraryDocs } from '../lib/sortLibraryDocs';
// COS-31 — el cierre POR EVENTO del aviso de pendientes continuables (el porqué
// del conjunto `S` y de su limpieza vive entero en ese archivo).
import {
  readPendingBannerDismissal, savePendingBannerDismissal,
  clearPendingBannerDismissal, pendingBannerIsSilenced,
} from '../lib/pendingBannerDismissal';
import {
  readZeroCreditsDismissal, saveZeroCreditsDismissal,
  zeroCreditsCycleId, zeroCreditsIsSilenced,
} from '../lib/zeroCreditsDismissal';
import { computeHonesto } from '../lib/processingProgress';
// BARRA HONESTA (#372): el muro `full` de un archivo de TEXTO también descarta.
import { classifyWall } from '../components/autoSplitWall';
// INFRA-3 (#381): el transporte NEUTRO por rebanadas (no es el del banco del profe:
// `lib/subidasDelBanco.js` queda prohibido en el consumidor). Pide `lib/api` por dentro.
import { subirPorRebanadas } from '../lib/transporteRebanadas';
// Queue helpers + the guest-funnel restore sequence moved to lib (same code, now
// unit-testable). writeAutoSplitQueue behaves exactly as before.
import { writeAutoSplitQueue, prepareGuestSignup, mergeRestoredQueue, GUEST_FUNNEL_FLAG_KEY } from '../lib/guestSplitFunnel';
import { useAutoSplitRestore, useGuestRestoreVeil } from '../hooks/useAutoSplitRestore';
import { readScreenCache, writeScreenCache } from '../lib/screenDataCache';
import { DashboardSkeleton, SkeletonBlock } from '../components/skeletons';
// F6.7b — la cara confiable: red reactiva (hook único), presencia local viva y
// la tabla de estados pura que decide el aviso honesto por tarjeta.
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useLocalPresence } from '../hooks/useLocalPresence';
import { isExplicitlyOffline } from '../lib/offline/networkVerdict';
import { arranquePendiente } from '../lib/offline/arranquePendiente';
import { log as diagLog } from '../lib/diag/readyChainLog';
import { useReportarCapa } from '../lib/pager/pane';
import { CARD_DELIVERY, computeCardDelivery, isCopiedToDevice } from '../lib/offline/deliveryState';
import { gruposSinRed, huellaDeCards, idLocalDeCard, cardsDeLosGrupos } from '../lib/warehouse/cardsDelGrupo';
// PAGER-1 — el contrato montada/activa (la doctrina completa vive en el módulo).
import { useRaizActiva } from '../lib/pager/activacion';
// NOTIFICACIONES-1 (#464): el dialogo del permiso del push, en la PRIMERA subida. Una
// linea al inicio de cada una de las cuatro puertas de aqui (la quinta es Pendientes).
import { pedirPermisoSiToca } from '../lib/push';
// t7-1b (T4): la tarjeta de cada grupo del alumno, arriba de sus documentos, y la
// unica puerta del frontend al material de sus grupos (pide `lib/api` al llamar).
import GruposDelAlumno from '../components/GruposDelAlumno';
import { cargarMaterialDelGrupo } from '../lib/materialDelGrupo';
// remates-1-3-0b (Bloque X1): si el material de los grupos falla en un montaje SIN grupos
// que pintar, se vuelve a pedir UNA vez, a los 5 s, sin recargar la pagina.
export const REINTENTO_MATERIAL_MS = 5000;
// series-7 (#490): la tarjeta padre de las partes de un documento partido. Se deriva de
// la MISMA lista que el Panel ya recibe (`lib/partes.js`), sin ninguna llamada nueva, y
// cada parte de adentro es la tarjeta de documento de este archivo.
import TarjetaPadreDePartes from '../components/TarjetaPadreDePartes';
import TemasDeLaParteModal from '../components/TemasDeLaParteModal';
import { useEsTactil } from '../lib/useEsTactil';
// LAPIDA dashboard-rediseno (A5): el chip "Procesar" (components/ChipProcesar.js) ya no se monta:
// el boton "Click to process" va en el pie de la tarjeta, a la derecha.
import { agruparPartes } from '../lib/partes';
import { tipoDeMaterial } from '../lib/tipoDeMaterial';
// ajustes-1-3-0 (Bloque C3, #501): la puerta a GET /api/documents/temas, que la tarjeta padre
// llama UNA vez en la primera tecla de su buscador (nunca al cargar el Panel).
import { pedirTemasDeDocumentos } from '../lib/buscarPorTema';
// dashboard-rediseno (15-oct-2026): las piezas del Panel nuevo (components/panel/), la Preparacion
// de cada tarjeta y Rename (los hace el ayudante del servidor: aqui se usan) y los creditos de la
// sesion (los pinta la barra de arriba: el Panel solo publica lo que ya pide).
import { usePreparacionDelPanel, invalidarPreparacion } from '../lib/preparacionDelPanel';
import { renombrar } from '../lib/renombrarDocumento';
import { publicarCreditos } from '../lib/creditosDeLaSesion';
import { leerNombreDeParte, numeroDeParte } from '../lib/partes';
import { ROTULO_DEL_TIPO } from '../components/IconoDeMaterial';
import {
  REJILLA_DEL_PANEL, AVISO_LARGO, RENGLON, RENGLON_DOS, RENGLON_DOS_CON_BOTON, BOTON_CHICO, LIGA_RENGLON,
  BOTON_PIE_SECUNDARIO, BOTON_PIE_PRINCIPAL, COLOR_BARRA_PROCESANDO, COLOR_BARRA_QUIETA, PISTA, BORDE_POR_PROCESAR,
  TRAZO_ICONO, TRAZO_ICONO_INTERNA, AreaDe44, nombreConCortes, ICONO_VER_TEMAS, BAJA_DEL_RENGLON_DOS,
} from '../components/panel/anatomia';
import CascaronDeTarjeta from '../components/panel/CascaronDeTarjeta';
import MenuDeTresPuntos from '../components/panel/MenuDeTresPuntos';
import Flotante from '../components/panel/Flotante';
import ChipDeEstado from '../components/panel/ChipDeEstado';
import { Medidor, MedidorDePreparacion } from '../components/panel/Medidor';
import VentanaRenombrar from '../components/panel/VentanaRenombrar';
import AvisoDelPanel, { BOTON_AVISO_PRINCIPAL } from '../components/panel/AvisoDelPanel';
import TarjetaParaAgregar from '../components/panel/TarjetaParaAgregar';
import CampoDeYoutube from '../components/panel/CampoDeYoutube';
import RecuadroNoCargaron from '../components/panel/RecuadroNoCargaron';
import { claveDelTipo, nombreDelTipo, conteosDe, renglonDelTipo, IconoDelTipo, SEPARADOR_DEL_RENGLON, renglonConEstilo } from '../components/panel/tipoDelDocumento';
// estilos-1 (Bloque C): el estilo de examen de la sesion (la pastilla, la hoja, el renglon del tipo y lo que viaja
// en las cuatro subidas). Con la perilla apagada nada se pinta y `cuerpoDeEstilo()` da `{}`.
import { useEstilos, cargarEstilosSiFalta, cuerpoDeEstilo, nombreCortoDelDocumento } from '../lib/estilos';
import { SelectorDeEstilo } from '../components/panel/EstiloDeExamen';
import { useReacomodo, reducirMovimiento } from '../components/panel/useReacomodo';
// post-t7-2b remate (R2, 5.3): el 429 del freno en el idioma del usuario (registro del invitado).
import { avisoDelFreno } from '../lib/avisoDelFreno';

// ── Auto-split sessionStorage keys & polling constants ──
const AUTOSPLIT_INTENT_KEY = 'studybuddy_auto_split_intent';
// ajustes-1-3-0 (Bloque B, ajuste 11, #503): tras el regreso de una compra en la APP, cuanto
// tiempo se vigilan el plan y los creditos para repedir el sobre UNA vez mas si cambian.
export const REGRESO_VIGILANCIA_MS = 5000;
// La huella de los creditos que el Panel ya observa (usage): si cambia, la compra se acredito.
function creditosDe(u) {
  if (!u) return '';
  return `${u.remaining ?? ''}|${u.pack_credits ?? ''}`;
}
const PACK_POLL_INTERVAL_MS = 2000;
const PACK_POLL_MAX_ATTEMPTS = 15;  // 30s total
// Future "Pendiente de procesar" section route — the page itself ships in Phase 3.
// Phase 2 only links here (save-pending confirmation, free-space, duplicate notice).
const PENDING_ROUTE = '/pending';

function stripAutoSplitUrlParams() {
  const params = new URLSearchParams(window.location.search);
  let touched = false;
  for (const key of ['from_auto_split', 'pack_purchased', 'credits_purchased', 'session_id', 'pack_cancelled']) {
    if (params.has(key)) { params.delete(key); touched = true; }
  }
  if (!touched) return;
  const qs = params.toString();
  const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
  window.history.replaceState({}, '', url);
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Hardcoded plan-limit numbers passed into translation slots for the credits tooltip and convert-modal bodies.
// Kept in-file (G.2=c decision): dynamic plan limits come from props/usage, but upgrade-path nudge numbers stay here.
//
// APP-1 B6 (firma #403) — Y AHORA SÓLO SIRVE PARA ESO. Esta tabla alimentaba también el
// tope PROPIO del usuario en el tooltip de créditos, que es un número del motor: sale de
// `MONTHLY_PROCESSING_LIMITS[effective_plan]` y viaja en `GET /documents/usage`. Tener
// dos fuentes para el mismo número es la trampa que produjo el 5-contra-3 de la lectura
// del 11-sep. Lo que queda aquí es lo que el cliente NO puede saber de otra forma: el
// tope del plan de AL LADO, que es un empujón, no una cuenta.
const PLAN_LIMIT_LABELS = {
  free: 5,
  pro: 50,
};
const MAX_BATCH_DOCS = 5;
// Max upload file size — MUST match backend config.MAX_UPLOAD_SIZE_MB. If these drift,
// the client and server reject at different thresholds and users see inconsistent behavior.
const MAX_UPLOAD_SIZE_MB = 50;
const GUEST_LIFETIME_CAP = 6;
// (REMATE-E: PRO_PRICE_LABEL se fue con el aviso de suscripción — era su único uso.)
// ── Densidad del chrome en MOBILE (<md) — hallazgo on-device (TestFlight):
// el chrome superior se comía el alto útil: el dropzone del guest con 0 docs
// salía cortado y con docs solo se veían ~2 tarjetas. Estas constantes aprietan
// SOLO la densidad <md; cada una lleva su contraparte md:/sm: con el valor
// histórico, así ≥md queda idéntico. La partición de scroll de F4.3d (chrome
// fijo + lista como única zona scrolleable) no cambia — solo baja el alto del
// chrome.
// dashboard-rediseno: la separacion entre bloques del documento, 18 px en el telefono y 28 desde
// 768 (decia 20 y 32). LAPIDA: `GUEST_BANNER_PAD` y `EMPTY_DROPZONE_PAD` se fueron con el aviso del
// invitado y la zona de soltar de antes (ahora `components/panel/AvisoDelPanel.js` y la zona grande).
const CHROME_VGAP = 'space-y-[18px] md:space-y-7';

/* ── TANDA 11 (firma #178) — LA SALIDA SUAVE DEL BORRADO ─────────────────────
 *
 * EL DEFECTO: al tocar el bote, la tarjeta desaparecía de golpe y toda la lista
 * saltaba hacia arriba en el mismo cuadro. En una lista donde borrar NO pide
 * confirmación (ver R-1, más abajo), ese salto es lo único que el usuario ve del
 * borrado — y un salto no cuenta nada: no dice cuál se fue ni de dónde.
 *
 * EL CONTRATO FIRMADO: la tarjeta se desvanece y colapsa en poco más de un cuarto
 * de segundo, las de abajo suben con ella, el borrado sigue SIN confirmación, y si
 * el servidor falla la tarjeta REGRESA con el toast de error de siempre.
 *
 * ── POR QUÉ ASÍ Y NO DE OTRA FORMA ──────────────────────────────────────────
 *
 * CERO LIBRERÍAS NUEVAS. El repo trae una librería de animación instalada que no
 * importa ni un solo archivo de `src/`: estrenarla aquí sería meter una dependencia
 * viva —y su forma de pensar— para un fundido de 260 ms. El molde que se copia es
 * el de `components/BootSplash.js`, el único sitio del repo donde un nodo se
 * despide antes de irse: estado local, temporizador y estilo en línea.
 *
 * EL COLAPSO SE HACE CON UNA REJILLA DE UNA SOLA FILA, y no midiendo alturas con
 * JS. La altura de una tarjeta no es un número que se pueda escribir: depende del
 * nombre del archivo, de cuántas etiquetas de estado trae, del panel de imágenes
 * abierto y del ancho de la pantalla. Medirla obligaría a leer el nodo antes de
 * cada borrado y a repintar con ese número, que es justo el tipo de código que se
 * desincroniza. Una fila que va de su tamaño natural a cero se anima sola: el
 * navegador conoce las dos puntas y no hay que decirle ninguna.
 *
 * Todo lo que se anima va en el ESTILO EN LÍNEA y no en clases, por dos razones:
 * la regla 5.6 de la casa (una clase escrita en un comentario la publica el
 * escáner) y que la duración sale de UNA constante, no de un número repetido en
 * media docena de sitios.
 *
 * EL MARGEN TAMBIÉN SE VA A CERO, o la tarjeta colapsada dejaría su rendija
 * abierta y el cierre se vería a medias. El estilo en línea le gana a la clase
 * por peso, y al regresar la propiedad se retira y el aire vuelve solo.
 *
 * ── TANDA 12 (firma #184) — Y EL AIRE ES DE LA TARJETA, NO DE LA LISTA ──────
 *
 * EL FRENÓN: hasta aquí el aire lo repartía el CONTENEDOR con un separador entre
 * hermanos, o sea que vivía como margen del hermano SIGUIENTE. La tarjeta que se
 * despedía anulaba el margen de ARRIBA —el suyo prestado— pero el de abajo era de
 * la vecina; y al desmontarse, ese margen cambiaba de dueño de golpe y sin
 * transición: un brinquito. Peor al borrar la PRIMERA, porque la que sigue pasaba
 * a ser primera y perdía su margen entero de un cuadro al otro.
 *
 * AHORA CADA ENVOLTURA LLEVA SU PROPIO AIRE, abajo, y el contenedor no reparte
 * nada. En reposo el resultado es idéntico al de siempre porque la ÚLTIMA lo cede
 * (`:last-child`, que es estructura y no un índice contado a mano): entre tarjetas
 * hay el mismo hueco, y después de la última sigue sin haber ninguno. Como el aire
 * es de la que se va, se cierra DENTRO de su mismo colapso de 260 ms, y cuando el
 * desmontaje llega no queda un solo margen que cambie de mano — ni siquiera en la
 * primera posición.
 *
 * Por eso lo que se anima es el margen de ABAJO y no el de arriba: el de arriba ya
 * no existe.
 *
 * EL HIJO INTERIOR ES OBLIGATORIO, no decorativo: una rejilla de una fila reparte
 * a UN hijo; los tres nodos de la tarjeta (móvil, escritorio y el panel de
 * imágenes) se meten en una caja común para que la fila los mida juntos. Esa caja
 * lleva su piso de altura en cero —sin él la rejilla se niega a encoger por debajo
 * del contenido— y recorta lo que sobra mientras se cierra.
 *
 * ── DÓNDE VA LA MARCA, Y POR QUÉ NO EN EL NODO DE FUERA ─────────────────────
 *
 * La marca del documento (su `data-testid`) se queda en la caja INTERIOR, que es
 * la que sigue teniendo por hijos directos a las tarjetas. Puesta en el nodo de
 * fuera, «el primer hijo del documento» pasaría a ser esta caja y no la tarjeta
 * —y hay pruebas del repo que tapean justo así—. Es un detalle de nada y es lo
 * que hace que esta tanda no mueva ni una prueba ajena: desde fuera, el árbol
 * bajo la marca del documento es EXACTAMENTE el de antes; lo único que se añadió
 * queda por ENCIMA de ella. (Se descubrió al revés: la primera versión puso la
 * marca fuera, dos pruebas se pusieron rojas y una TERCERA se quedó verde por
 * accidente, que es lo de verdad peligroso — su tap ya no llegaba a nada y su
 * `no navegó a ningún lado` se cumplía sola.)
 */
/* dashboard-rediseno (A15, 15-oct-2026): LA SALIDA SUAVE, EN LA REJILLA.
 *
 * LAPIDA: lo de arriba describe el colapso de una fila (de 260 ms, con su margen) de cuando la
 * lista era una columna. En la rejilla del Panel nuevo la tarjeta que se va baja a 28 % de
 * opacidad y se encoge a 97 % en 200 ms; despues se quita (con la lista fresca, como antes) y
 * las demas se acomodan en 200 ms (`components/panel/useReacomodo.js`). Con "reducir
 * movimiento", sin animacion. El orden del borrado (la despedida antes de pedir, esperar a la
 * despedida Y al servidor, la tarjeta que vuelve si falla) no cambia.
 *
 * La envoltura de fuera es la CELDA de la rejilla (`data-celda`, la que mide el reacomodo) y la
 * de dentro conserva la marca del documento (`doc-<id>`) y `data-saliendo`, como antes.
 */
const DOC_EXIT_MS = 200;
export const OPACIDAD_SALIENDO = 0.28;
export const ESCALA_SALIENDO = 0.97;

function SalidaSuave({ saliendo, testid, celda, children }) {
  const quieto = reducirMovimiento();
  return (
    <div
      data-celda={celda}
      style={{
        opacity: saliendo ? OPACIDAD_SALIENDO : 1,
        transform: saliendo ? `scale(${ESCALA_SALIENDO})` : undefined,
        transition: quieto ? undefined : `opacity ${DOC_EXIT_MS}ms ease-out, transform ${DOC_EXIT_MS}ms ease-out`,
      }}
    >
      <div data-testid={testid} data-saliendo={saliendo ? 'true' : undefined}>
        {children}
      </div>
    </div>
  );
}

// dashboard-rediseno (A4): el aviso del escaneado es UNA llave con tres oraciones; en la tarjeta
// van las dos primeras y en el panel del boton de informacion la ultima (la de la letra a mano),
// sin repetir lo que ya dice la tarjeta. Se parte en el ultimo ". " del texto ya traducido (los
// dos idiomas tienen esa forma); sin corte, todo va en la tarjeta y no hay boton.
export function partirAvisoDeLectura(texto) {
  const s = typeof texto === 'string' ? texto : '';
  const corte = s.lastIndexOf('. ');
  if (corte < 0) return [s, null];
  return [s.slice(0, corte + 1), s.slice(corte + 2)];
}

const STATUS_KEY_MAP = {
  ready: 'ready',
  processing: 'processing',
  error: 'error',
  uploaded: 'uploaded',
  archived: 'archived',
};

// ajustes-1-3-0 (Bloque D, #506): el boton "Procesar" chico de la tarjeta "Listo para
// procesar", a la derecha del chip. El vestido es el del "Importar" del Panel (el boton de
// YouTube: naranja en pildora, con su apreton), copiado como texto; el tamano es el del chip
// (21 px de alto, letra de 10) para que el renglon del chip no crezca ni un pixel.
// remates-1-3-0b (P2, #513): el boton "Procesar" del por_confirmar, a TODO lo ancho de la tarjeta,
// debajo del renglon del chip, con el naranja de los botones de la casa; 44 px en tactil y el alto
// de la casa (40) en computadora. Decia BOTON_PROCESAR_CHICO (D, #506): la pildora de 21 px junto al chip.
// remates-1-3-0c (Q3, W5): BOTON_PROCESAR_ANCHO se retiro. Vuelve a ser un CHIP, en el renglon del
// chip de estado y a su derecha, alargado en computadora y a lo ancho del renglon en el telefono:
// components/ChipProcesar.js (Carlos: "como estaba antes en forma de chip pero a lo largo").
// remates-1-3-0b (P1, #512): la frase que se le pega al aviso de "Documento recibido..." (y a los de
// "Procesar" del documento grande): el servidor sigue solo, asi que el alumno puede irse.
// Se lee al MOMENTO (IS_APP_BUILD es una compuerta de tiempo de ejecucion en el repo), no al cargar.
const llaveDeSalida = () => (IS_APP_BUILD ? 'dashboard:toasts.leave_app' : 'dashboard:toasts.leave_tab');

// t7-3 remate 8 (#591, C56): "Tema: A" con uno y "Temas: A, B" con dos o mas. La tarjeta de las
// partes le pasa a cada hija SOLO el resumen ya armado (`resumenDeTemas`: hasta dos temas unidos
// por coma y "..." si hay mas), no la lista; la cuenta se lee de ese resumen: con "..." son tres o
// mas, y si no, uno por pieza. Si quien llama pasa la cuenta de verdad (un numero), manda esa.
// Limite declarado: un solo tema cuyo nombre lleve coma y espacio se contaria como dos.
const SEPARADOR_DEL_RESUMEN = ', ';
const SUSPENSIVOS_DEL_RESUMEN = '...';
function cuantosTemasDelResumen(resumen, cuantos) {
  if (Number.isFinite(cuantos) && cuantos > 0) return cuantos;
  const texto = typeof resumen === 'string' ? resumen : '';
  if (!texto) return 0;
  if (texto.endsWith(SUSPENSIVOS_DEL_RESUMEN)) return 3;
  return texto.split(SEPARADOR_DEL_RESUMEN).length;
}

// #165 — LOS `error_code` QUE ESTA PANTALLA SABE TRADUCIR.
//
// Lista blanca a proposito, y no un `t()` directo sobre el codigo: si el backend estrena
// un codigo que este bundle todavia no conoce, `t()` pintaria la clave sin traducir
// («dashboard:errors.lo_que_sea») en la tarjeta del usuario. Con la lista, un codigo
// desconocido cae al `error_message` crudo — feo, pero legible y en el idioma del
// backend, que es exactamente el comportamiento de siempre.
//
// Cada entrada tiene su clave bajo `errors` en los DOS locales.
const CODIGOS_DE_ERROR_TRADUCIDOS = new Set([
  'insufficient_material',
  'insufficient_material_refunded',
  // #167 (tanda 8): los otros seis desenlaces que dejan la tarjeta en rojo. Sin esta
  // lista el backend escribe el codigo y la tarjeta lo ignora — el letrero se quedaria
  // en ingles, que es justo lo que la firma vino a quitar.
  'split_processing_failed',
  'split_processing_failed_refunded',
  'all_attempts_failed_refunded',
  'unexpected_error_refunded',
  'max_retries_reached',
  'retry_failed',
  'processing_interrupted',
  'pipeline_unexpected_error',
]);

// series-7 (#493): los tres codigos del camino de YouTube y su aviso. Otro sitio (Y1),
// YouTube sin video (el aviso de hoy) y el video que no se pudo leer (Y2: privado, sin
// transcripcion o el proveedor fallo; el servidor los nombra con UN codigo a proposito).
const AVISO_DE_YOUTUBE = {
  doc_youtube_not_youtube: 'dashboard:youtube.no_es_youtube',
  doc_youtube_invalid_url: 'dashboard:youtube.invalid_url',
  doc_youtube_unreadable: 'dashboard:youtube.no_se_pudo_leer',
};

// series-7 (#492): la ventana del documento grande trae en `affordability.series7`
// cuantas partes no caben ni en Pendientes (`overflow`, el renglon U6). Sin la llave
// (un servidor viejo) o en cero, no hay desborde.
function desbordaPendientes(sobre) {
  const s7 = sobre && sobre.affordability && sobre.affordability.series7;
  return !!s7 && typeof s7.overflow === 'number' && s7.overflow > 0;
}

// series-7 (#492, revision adversarial del bloque Panel, hallazgo 2): EL CARRIL DE OJOS
// NO DESBORDA. Un escaneado o un paquete de fotos (el placeholder que leyo con los ojos,
// con su huella de bytes) no puede guardar "lo que quepa": sus partes heredan la huella,
// y volver a subir el MISMO archivo choca en la puerta de duplicados antes del
// reencuentro, asi que lo que no cupiera se perderia y el renglon U6 mentiria. Por eso,
// en ese carril, el servidor sigue con su 409 `pending_overcap` de hoy aunque llegue el
// booleano (routes/documents.py, la guarda del 409 mira `bytes_hash`); abrirlo pide
// relajar esa puerta, y eso es firma de Carlos. Lo coherente aqui: con desborde, el sobre
// de ese carril lleva la marca `sin_desborde: true` (con ella la ventana pinta la de hoy,
// con su muro: lo decide AutoSplitModal.js) y el Panel no manda el booleano. El sobre
// viaja ENTERO, con su `series7`. Sin desborde, ni marca: la ventana nueva, que el
// servidor acepta. La condicion es la del servidor (`bytes_hash`, que viaja en la lista)
// y ademas `lectura === 'ojos'`, por si la huella dejara de viajar: el unico costo es que
// un placeholder de ojos sin huella (anterior a #348) ve la ventana de hoy. Fuera de ese
// caso devuelve el MISMO sobre (la misma referencia): todo sigue como estaba.
function esPlaceholderDeOjos(sobre, docs) {
  if (!sobre || !sobre.documento_base_id || !Array.isArray(docs)) return false;
  const ph = docs.find((d) => d && d.id === sobre.documento_base_id);
  return !!ph && (ph.lectura === 'ojos' || !!ph.bytes_hash);
}

function sobreParaLaVentana(sobre, docs) {
  if (!desbordaPendientes(sobre) || !esPlaceholderDeOjos(sobre, docs)) return sobre;
  return { ...sobre, sin_desborde: true };
}

// post-t7-2b (#615): `codigoNoSalio` = el servidor dijo `email_sent: false` y NO mando el
// codigo (con la llave de Resend puesta el codigo ya nunca viaja). En vez de "Codigo enviado a
// ..." se dice el texto ratificado y el boton de reenviar sigue; un reenvio lo actualiza.
function ConvertVerifyStep({ email, expectedCode, codigoNoSalio, onSuccess, onBack, onFailLimit, verifyGuestConversion, resendGuestConversion }) {
  const { t, ready } = useReadyTranslation(['dashboard', 'common']);
  const [noSalio, setNoSalio] = React.useState(!!codigoNoSalio);
  const [code, setCode] = React.useState(['', '', '', '', '', '']);
  const [loading, setLoading] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const inputRefs = React.useRef([]);
  const codeString = code.join('');

  const focusInput = (i) => inputRefs.current[i]?.focus();

  const handleChange = (index, value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return;
    const next = [...code];
    next[index] = digits[digits.length - 1];
    setCode(next);
    if (index < 5) focusInput(index + 1);
  };
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const next = [...code]; next[index - 1] = ''; setCode(next); focusInput(index - 1);
    } else if (e.key === 'ArrowLeft' && index > 0) focusInput(index - 1);
    else if (e.key === 'ArrowRight' && index < 5) focusInput(index + 1);
    else if (e.key === 'Enter' && code.every(d => d !== '')) { e.preventDefault(); handleVerify(); }
  };
  const handlePaste = (e) => {
    e.preventDefault();
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!digits.length) return;
    const next = [...code];
    for (let i = 0; i < digits.length; i++) next[i] = digits[i];
    setCode(next);
    focusInput(Math.min(digits.length, 5));
  };
  const handleVerify = async () => {
    if (loading || codeString.length !== 6) return;
    setLoading(true);
    try {
      await verifyGuestConversion(codeString);
      onSuccess();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || t('dashboard:toasts.convert_verify.verify_failed');
      // post-t7-2b remate (R2, 5.3): el aviso va en el idioma del usuario.
      toast.error(avisoDelFreno(err, t) || detail);
      // post-t7-2c (D6): volver al formulario lo decide el `error_code` del 429 que el servidor ya manda en
      // `/auth/verify-guest-conversion` (`auth_too_many_attempts_start_over`); el `detail` ya viene traducido y
      // en espanol no decia "too many". Sin codigo, el texto como respaldo, como antes.
      const codigo = err.response?.data?.error_code;
      const volver = codigo
        ? codigo === 'auth_too_many_attempts_start_over'
        : typeof detail === 'string' && detail.toLowerCase().includes('too many');
      if (status === 429 && volver) {
        onFailLimit?.();
      } else {
        setCode(['', '', '', '', '', '']);
        focusInput(0);
      }
    } finally { setLoading(false); }
  };

  if (!ready) return null;

  return (
    <div className="space-y-5">
      <button onClick={onBack} className="flex items-center gap-2 text-[#6a6a6a] hover:text-white transition h-11 text-sm" data-testid="convert-verify-back-btn">
        <ArrowLeft className="w-4 h-4" /> {t('common:back')}
      </button>
      <div className="text-center">
        <div className="w-14 h-14 bg-[#FF6B00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail className="w-7 h-7 text-[#FF6B00]" />
        </div>
        <h3 className="text-lg font-semibold text-white" data-testid="convert-verify-title">{t('dashboard:convert_verify.title')}</h3>
        <p className="text-[#6a6a6a] mt-1 text-sm" data-testid="convert-verify-subtitle">
          {noSalio ? t('common:prod.codigo.nosalio') : (
            <Trans
              i18nKey="dashboard:convert_verify.body"
              values={{ email }}
              components={{ 1: <span key="1" className="text-white font-medium" /> }}
            />
          )}
        </p>
      </div>

      {expectedCode && (
        <div className="p-3 rounded-xl bg-[rgba(255,107,0,0.08)] border border-[rgba(255,107,0,0.15)] text-center">
          <p className="text-xs text-[#6a6a6a] mb-1">{t('dashboard:convert_verify.dev_mode_label')}</p>
          <p className="text-xl font-mono font-bold text-[#FF6B00]" data-testid="convert-dev-code">{expectedCode}</p>
        </div>
      )}

      <div className="flex justify-center gap-2">
        {code.map((digit, i) => (
          <input key={i} ref={el => inputRefs.current[i] = el}
            type="text" inputMode="numeric" pattern="[0-9]*"
            value={digit}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            onClick={(e) => e.target.select()}
            className="w-11 h-12 text-center text-xl font-bold bg-[#0e0e0e] border-2 border-[#262626] rounded-xl text-white focus:border-[#FF6B00] focus:ring-1 focus:ring-[#FF6B00]/30 transition-colors outline-none"
            data-testid={`convert-verify-input-${i}`}
          />
        ))}
      </div>

      <Button disabled={loading || codeString.length !== 6}
        onClick={handleVerify}
        className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white h-10 rounded-xl font-semibold"
        data-testid="convert-verify-submit-btn">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('dashboard:convert_verify.submit_btn')}
      </Button>

      <div className="text-center">
        <p className="text-xs text-[#6a6a6a] mb-2">{t('dashboard:convert_verify.didnt_receive')}</p>
        <button type="button" disabled={resending}
          onClick={async () => {
            setResending(true);
            try {
              const res = await resendGuestConversion();
              // post-t7-2b (#615): un reenvio que tampoco salio (y sin codigo) no dice
              // "reenviado". Una respuesta sin `email_sent` (un servidor viejo) sigue como antes.
              if (res && res.email_sent === false && !res.code) {
                setNoSalio(true);
                toast.error(t('common:prod.codigo.nosalio'));
                return;
              }
              setNoSalio(false);
              toast.success(t('dashboard:toasts.convert_verify.resent'));
            } catch (err) {
              toast.error(avisoDelFreno(err, t) || err.response?.data?.detail || t('dashboard:toasts.convert_verify.resend_failed'));
            } finally { setResending(false); }
          }}
          className="text-[#FF6B00] hover:text-[#ff8c42] text-sm font-medium inline-flex items-center gap-1.5 transition h-11"
          data-testid="convert-resend-btn">
          {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {t('dashboard:convert_verify.resend_btn')}
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  /* PAGER-1 — ¿EL USUARIO ESTÁ VIENDO ESTA RAÍZ? (ver lib/pager/activacion.js)
     Sin proveedor arriba vale `true`, que es la verdad de hoy: el router monta
     una sola raíz y montarse ES ser vista. Por eso el comportamiento queda
     IDÉNTICO y esta tanda no estrena ni un proveedor en producción — lo que
     cambia es que cada efecto de abajo ya declara si depende de la ACTIVACIÓN
     o sólo del montaje. En PAGER-2 el proveedor lo pone el pager por encima de
     esta pantalla y esta misma línea empieza a devolver false cuando toque. */
  const activa = useRaizActiva();
  // remates-1-3-0b (P2): tactil = la app, o un puntero grueso (la web en el telefono).
  const punteroGrueso = useEsTactil();
  const esTactil = IS_APP_BUILD || punteroGrueso;
  // remates-1-3-0b (P3): la ventana "Ver temas" de un documento normal (la de las partes).
  const [temasDeDoc, setTemasDeDoc] = useState(null);
  const volverATemas = useRef(null);
  const { t, ready, i18n } = useReadyTranslation(['dashboard', 'common', 'auth']);
  const { user, convertGuest, verifyGuestConversion, resendGuestConversion, cancelGuestConversion } = useAuth();
  // tutor-2 (D.1): "Preguntar al tutor" en el menu de tres puntos, solo con la perilla encendida para esta cuenta.
  const tutorDelPanel = useTutorDeLaSesion();
  const verTutorEnElPanel = tutorVisible(tutorDelPanel, user);
  // F6.4b: el fallback de bodega dentro de fetchDocs necesita el user.id sin
  // meter `user` a las deps del useCallback (recrearlo re-dispararía los
  // effects de fetch — comportamiento online debe quedar idéntico).
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  // estilos-1 (Bloque C): UNA lectura de `GET /estilos` por sesion, al entrar al Panel ya activo y con sesion.
  const estilos = useEstilos();
  const idUsuarioDeEstilos = user?.id;
  useEffect(() => {
    if (!activa || !idUsuarioDeEstilos) return;
    cargarEstilosSiFalta(userRef.current || { id: idUsuarioDeEstilos });
  }, [activa, idUsuarioDeEstilos]);
  // Cache de pantalla (F4.3b): snapshot UNA vez al montar. Fresco → se pinta al
  // instante y el fetch inicial se omite; viejo/vacío → skeleton + fetch. El
  // Dashboard conserva TODA su maquinaria (poll de processing, repoll de
  // upgrade, refetch on-focus) — el cache solo decide el primer pintado y se
  // mantiene al día con el efecto write-through de abajo.
  const [dashSeed] = useState(() => readScreenCache('dashboard'));
  const dashSeedFresh = Boolean(dashSeed?.fresh);
  const [docs, setDocs] = useState(dashSeedFresh ? dashSeed.data.docs : []);
  const [loading, setLoading] = useState(!dashSeedFresh);
  // dashboard-rediseno (I2): la lista no cargo y no hay nada que ensenar. Sustituye al aviso
  // flotante "Failed to load documents": se pinta el recuadro en lugar de la lista. Una recarga
  // que falla con una lista ya a la vista no lo enciende (los documentos si cargaron).
  const [fallaDeCarga, setFallaDeCarga] = useState(false);
  const [reintentandoCarga, setReintentandoCarga] = useState(false);
  const listaBuenaRef = useRef(dashSeedFresh);
  // F6.4b: true cuando el último fetch vivo cayó por red y la pantalla pintó
  // desde la bodega — enciende el chip "Sin conexión" y deshabilita los CTAs
  // que requieren red (upload, YouTube, comprar créditos). Un fetch vivo
  // exitoso lo apaga y todo vuelve a la normalidad solo.
  const [offlineData, setOfflineData] = useState(false);
  // F6.7b (fase 2) — el chip y los CTAs ya no dependen SOLO de que un fetch
  // haya caído: el dictamen del vigía los prende y los apaga en vivo, sin
  // navegar. `offlineData` sigue siendo la otra mitad de la verdad (el dato
  // que se está pintando vino de la bodega), y las dos se suman: cualquiera
  // de las dos encendida = modo offline.
  const net = useNetworkStatus();
  const isOffline = offlineData || net.isOffline;
  // remates-1-3-0b (P4, #515): en la app sin conexion, el bote de TODO documento (normal, parte, en
  // la pestana de candados) se pinta apagado y al tocarlo sale el aviso de hoy; decia solo las hijas (C8).
  const boteSinConexion = IS_APP_BUILD && isOffline;
  // F6.7b (fase 1) — el inventario del teléfono, vivo. Decide el aviso honesto
  // por tarjeta; hasta que `presenceRead` sea true no se pinta ningún aviso.
  const { presenceById, presenceRead, refresh: refreshPresence } = useLocalPresence();
  // Docs cuyo reintento manual está en vuelo (el aviso no cambia, solo se
  // bloquea el re-tap; si el reintento falla, el aviso PERMANECE sin toasts).
  const [retryingDoc, setRetryingDoc] = useState({});
  // TANDA 11 (firma #178) — los documentos que están DESPIDIÉNDOSE. Un id entra
  // aquí al tocar su bote y sale cuando la lista fresca ya llegó (o al instante
  // si el servidor falló y la tarjeta tiene que volver). Es un conjunto y no un
  // solo id a propósito: nada impide tocar dos botes seguidos, y con un id suelto
  // el segundo toque cancelaría la salida del primero. El molde entero, con sus
  // porqués, vive en el letrero de `SalidaSuave`, arriba.
  const [saliendo, setSaliendo] = useState(() => new Set());
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  // OCR-ALUMNO (§C1/§C2): el paquete de fotos que el alumno está armando. Elegir o
  // soltar imágenes NO sube nada: abre el panel; el envío es explícito («Subir N fotos»).
  // `fotosPanel` es la cuadrícula (en el orden en que llegaron); `ordenFotos` los ids
  // incluidos, en el orden numerado (la ref es el espejo síncrono para el tope de 5).
  const [fotosPanel, setFotosPanel] = useState([]);
  const [ordenFotos, setOrdenFotos] = useState([]);
  const [errorFotos, setErrorFotos] = useState(null);
  const [subiendoFotos, setSubiendoFotos] = useState(false);
  const [nombreFotos, setNombreFotos] = useState('');
  const fotosPanelRef = useRef([]);
  const fotoIdRef = useRef(0);
  const [processing, setProcessing] = useState({});
  const [regeneratingFlashcards, setRegeneratingFlashcards] = useState({});
  const [planInfo, setPlanInfo] = useState(dashSeedFresh ? dashSeed.data.planInfo : null);
  // Active / Locked tabs above the doc list. Tabs render only when the user has >=1 archived
  // doc; otherwise the list is identical to a user with no locked docs (zero change for them).
  const [docTab, setDocTab] = useState('active'); // 'active' | 'locked'
  const [insufficientCredits, setInsufficientCredits] = useState(null);
  // Anti-duplicate: an archived copy of this exact doc exists (recover via upgrade, or delete
  // + re-upload). Holds the 409 duplicate_archived detail ({ archived_doc_id }).
  const [duplicateArchived, setDuplicateArchived] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processProgress, setProcessProgress] = useState({});
  const [nowTick, setNowTick] = useState(Date.now());  // re-render tick for the progress bar
  const [docImages, setDocImages] = useState({});
  const [convertModal, setConvertModal] = useState(null); // null | 'lifetime' | 'save' | 'credits'
  const [convertForm, setConvertForm] = useState({ name: '', email: '', password: '' });
  const [converting, setConverting] = useState(false);
  const [convertStep, setConvertStep] = useState('form'); // 'form' | 'verify'
  // AS-5 — conversión de invitado por login nativo. Al volver con sesión, el modal se
  // cierra: la conversión ya ocurrió EN SITIO y el Panel se repinta con la cuenta nueva
  // (el `user` del contexto ya cambió). No navega a ningún lado — el usuario ya está en
  // la pantalla que quería.
  const cerrarModalDeConversion = useCallback(() => {
    setConvertModal(null);
    setConvertStep('form');
  }, []);
  const { pendiente: pendienteSocial, entrarConApple, entrarConGoogle, soltar: soltarSocial } = useNativeSocialLogin({ onSuccess: cerrarModalDeConversion });
  // TANDA 25 (bloque E) — la señal de carga de los dos botones sociales WEB de este
  // modal, la TERCERA superficie de la firma #226. Mismo patrón exacto que LoginPage y
  // SignupPage: se toca «Continuar con Google» y no pasa nada durante 1-3 segundos
  // mientras el navegador viaja al proveedor.
  //
  // ES UN ESTADO PROPIO Y NO EL DEL HOOK NATIVO: `pendienteSocial` lo gobierna
  // `useNativeSocialLogin`, que en web se va en la primera línea de su handler. Los dos
  // bloques son excluyentes (`IS_APP_BUILD`), así que nunca conviven.
  //
  // Remate de estilos-1 (R6): lo de "no se apaga nunca: al volver con el boton de
  // atras, el navegador re-monta el Panel" no es cierto con la cache de ida y vuelta
  // (bfcache): el Panel regresa CON su estado y la ruedita se quedaba pegada, igual que
  // en LoginPage y SignupPage (Bloque E). Hoy el regreso (pageshow con persisted,
  // popstate, o la pestana o la app que vuelve a estar visible) apaga las dos ruedas,
  // la de la web y la nativa, con la MISMA pieza que esas dos pantallas.
  const [pendienteWeb, setPendienteWeb] = useState(null);
  const irAProveedorWeb = useCallback((proveedor) => {
    // Primero el estado, luego el redirect: al revés, `window.location.href` puede
    // llevarse la página antes de que React pinte el spinner.
    setPendienteWeb(proveedor);
    if (proveedor === 'apple') redirectToAppleLogin();
    else redirectToGoogleLogin();
  }, []);
  useSoltarAlRegresar(useCallback(() => {
    setPendienteWeb(null);
    if (soltarSocial) soltarSocial();
  }, [soltarSocial]));
  const [convertPendingEmail, setConvertPendingEmail] = useState('');
  const [convertExpectedCode, setConvertExpectedCode] = useState('');
  // post-t7-2b (#615): el correo con el codigo no salio y el codigo no vino.
  const [convertCodigoNoSalio, setConvertCodigoNoSalio] = useState(false);
  const [loadingImages, setLoadingImages] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [usage, setUsage] = useState(dashSeedFresh ? dashSeed.data.usage : null);
  // dashboard-rediseno (4.2): pintado desde la semilla de la cache, la barra de arriba recibe ese
  // mismo plan y uso (la semilla salio de una respuesta con red). Una vez: es la foto del montaje.
  useEffect(() => {
    if (dashSeedFresh) publicarCreditos({ planInfo: dashSeed.data.planInfo, usage: dashSeed.data.usage, sinRed: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = React.useRef(null);
  // Header upload input — always rendered; the F3 expired-screen CTA clicks it.
  const headerUploadInputRef = React.useRef(null);
  const dragCounterRef = React.useRef(0);
  const localStartRef = React.useRef({});  // last-resort per-doc start when backend anchors are absent
  // BARRA HONESTA (#370): por documento, el sello más nuevo visto, la etapa y el máximo
  // pintado (la barra nunca retrocede). Se pierde al re-montar, igual que `localStartRef`.
  const honestoRef = React.useRef({});
  // #405 — EL PORCENTAJE PINTADO LA ULTIMA VEZ QUE EL DOCUMENTO NO ESPERABA TURNO.
  // Mientras `esperando` es true la barra se queda donde estaba: no se borra y tampoco
  // avanza. Se pierde al re-montar, igual que sus dos hermanos de arriba.
  const pctQuietoRef = React.useRef({});
  const { fingerprint, getFingerprint, getTurnstileToken, turnstileRef } = useSecurity();
  // PAGER-1 — la instancia del PANEL sigue la activación de su raíz: dormida no
  // fetchea, no reacciona a foco y no emite SCR.STALE/SCR.EMPTY. Las instancias
  // del chasis (nav, Sidebar) no pasan nada y quedan intactas.
  const { hasProcessable, processableGroupCount, groups: pendingGroups, hasSummary: hasPendingSummary,
    avisosDocumentoEntero, snapshot: pendingSnapshot } = usePendingSummary({ activo: activa });
  /* ── COS-31 — LA MÁQUINA DEL CIERRE POR EVENTO ────────────────────────────
     `S` es el conjunto de grupos continuables AHORA: la identidad del aviso.
     La semántica completa (y el porqué de cada rama) está en
     lib/pendingBannerDismissal.js; aquí sólo se cablea. */
  const processableGroupIds = React.useMemo(
    () => (pendingGroups || []).filter(g => g.processable).map(g => g.group).filter(Boolean),
    [pendingGroups]
  );
  const processableIdsKey = processableGroupIds.join('|');
  const [pendingBannerDismissed, setPendingBannerDismissed] = useState(readPendingBannerDismissal);
  /* Cuando `S` se vacía, el cierre guardado se limpia — así el siguiente aviso
     genuino (el mismo grupo tras un reset de créditos, o uno nuevo) SÍ aparece.
     El gate es `hasSummary`, no el conteo pelado: mientras el resumen carga (o
     si la lectura falló) `groups` viene vacío sin que eso signifique "ya no hay
     nada que continuar", y limpiar ahí borraría el cierre en cada montaje. Es
     la misma regla EH-4(a) que gobierna a las otras superficies de Pendientes:
     sólo una respuesta EXITOSA del servidor autoriza esa afirmación. */
  useEffect(() => {
    if (!hasPendingSummary || processableGroupIds.length > 0) return;
    clearPendingBannerDismissal();
    setPendingBannerDismissed(prev => (prev === null ? prev : null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPendingSummary, processableIdsKey]);
  const pendingBannerSilenced = pendingBannerIsSilenced(processableGroupIds, pendingBannerDismissed);
  /* ── R-1 — EL FOCO NO PUEDE ATERRIZAR EN UN BOTE DE BASURA ──────────────
     Hallazgo on-device de Carlos (build #59): al tocar la X, el ícono de
     borrar del documento de HASTA ARRIBA quedaba resaltado. La X es el
     ÚLTIMO enfocable de la card del aviso y, en cuanto el aviso se
     desmonta, el siguiente enfocable del documento es justamente ese botón
     de borrar — que en este Panel NO pide confirmación (`handleDelete`
     llama a `api.delete` de una): un Enter/espacio de más ahí borra el
     documento sin vuelta.

     Por eso el cierre MANDA el foco a un ancla neutra en vez de dejarlo al
     azar del navegador. El ancla es el h1 del saludo (`dashboard-title`):
     existe siempre, no dispara nada, y con `tabIndex={-1}` no entra en el
     orden de tabulación — sólo puede recibir foco programático. Es además
     el destino que un lector de pantalla anuncia con sentido ("Hola,
     Carlos"), en vez del silencio de un div vacío. `preventScroll` para que
     mandar el foco arriba no mueva la pantalla bajo el dedo.

     Cinturón: si por lo que sea no hay ancla, se apaga el foco actual
     (blur → body). Lo único inaceptable es que quede en un destructivo. */
  const focoNeutroRef = React.useRef(null);
  const dismissPendingBanner = () => {
    const ancla = focoNeutroRef.current;
    if (ancla && typeof ancla.focus === 'function') ancla.focus({ preventScroll: true });
    else if (typeof document !== 'undefined' && typeof document.activeElement?.blur === 'function') {
      document.activeElement.blur();
    }
    savePendingBannerDismissal(processableGroupIds);
    setPendingBannerDismissed(processableGroupIds);
  };
  /* ── TANDA I (B3) — EL CIERRE DEL AVISO DE CRÉDITOS AGOTADOS, POR EVENTO ──
     Mismo cableado que el de arriba, con una identidad más simple: el CICLO de
     créditos (`credit_reset_date`). La semántica y el porqué de que sea un solo
     valor y no un conjunto están en lib/zeroCreditsDismissal.js; aquí sólo se
     cablea. No necesita el efecto de autolimpieza que COS-31 sí necesita: al
     cambiar el ciclo, el valor guardado deja de casar y el cierre muere solo.
     El foco va al MISMO ancla neutra por la misma razón (R-1): debajo de este
     aviso viven los botones de borrar documento, que no piden confirmación. */
  const zeroCreditsCycle = zeroCreditsCycleId(usage);
  const [zeroCreditsDismissed, setZeroCreditsDismissed] = useState(readZeroCreditsDismissal);
  const zeroCreditsSilenced = zeroCreditsIsSilenced(zeroCreditsCycle, zeroCreditsDismissed);
  const dismissZeroCreditsBanner = () => {
    const ancla = focoNeutroRef.current;
    if (ancla && typeof ancla.focus === 'function') ancla.focus({ preventScroll: true });
    else if (typeof document !== 'undefined' && typeof document.activeElement?.blur === 'function') {
      document.activeElement.blur();
    }
    saveZeroCreditsDismissal(zeroCreditsCycle);
    setZeroCreditsDismissed(zeroCreditsCycle);
  };
  /* ── TANDA I (delta) — LA COMPETENCIA ENTRE LOS DOS AVISOS, RESUELTA ──────
     FIRMA DE CARLOS: cuando los dos son elegibles a la vez, GANA EL DE
     CRÉDITOS. El censo de B3 había dejado constancia de que HOY SE APILAN, sin
     `else if` ni prioridad declarada, y de la ventana de contradicción que eso
     abre: el de créditos dice "te quedaste sin créditos, compra un paquete" y
     justo debajo el de pendientes dice "ya puedes continuar tus documentos" —
     dos promesas opuestas sobre el mismo bolsillo, apiladas.

     LA ELEGIBILIDAD, EN UNA SOLA EXPRESIÓN. `zeroCreditsEligible` son las tres
     condiciones de siempre —hay `usage`, no quedan créditos, no es invitado— y
     es LA MISMA para los dos avisos: la que puede encender el de créditos y la
     que aparta al de pendientes. Se calcula una vez y la leen los dos, así que
     no hay forma de mover una condición y olvidar la otra.

     TANDA N (N3) — "UNO U OTRO", Y ÉSTE ES EL VOLTEO. La tanda I metía
     `!zeroCreditsSilenced` DENTRO de la elegibilidad, con lo que la X del aviso
     de créditos no sólo lo callaba: REVELABA al de pendientes. Carlos re-firmó
     al revés: silenciado el de créditos, NO APARECE NADA en su lugar. Por eso
     el silenciado sale de la elegibilidad y se aplica SÓLO al renderizar el de
     créditos (`zeroCreditsBannerVisible`), mientras la puerta del de pendientes
     lee la elegibilidad CRUDA (`zeroCreditsEligible`). Cerrar el que gana lo
     calla a él y a nadie más.

     LO QUE NO CAMBIÓ, y por qué esta forma lo conserva:
       · CRÉDITOS GANA cuando los dos son elegibles: la puerta de pendientes
         sigue leyendo la misma expresión, sólo que sin el silenciado.
       · CICLO NUEVO REVIVE CRÉDITOS: el silenciado sigue siendo por ciclo
         (lib/zeroCreditsDismissal.js) y sigue muriendo solo al cambiar de
         `credit_reset_date`. Al revivir, el de pendientes ya estaba apartado.
       · EL DE PENDIENTES CONSERVA SU PROPIA X Y SU PROPIA PERSISTENCIA para
         cuando le toque salir — o sea, cuando créditos NO es elegible. Eso es
         justo lo que separa esta regla del mutante "el de pendientes ya no
         existe", y tiene su rojo (Dashboard.tandaN.test.js). */
  const zeroCreditsEligible = !!usage && usage.remaining === 0 && !user?.is_guest;
  const zeroCreditsBannerVisible = zeroCreditsEligible && !zeroCreditsSilenced;
  // LAPIDA dashboard-rediseno (barra superior, "Panel de creditos"): aqui vivian el estado y la
  // medida del panel del icono de informacion de los creditos. El texto pasa TAL CUAL al panel
  // del chip de creditos de la barra de arriba (lo pinta el chasis), y el icono se quita.
  const [showPackModal, setShowPackModal] = useState(false);
  const [justProcessedIds, setJustProcessedIds] = useState(new Set());
  // Guest split funnel F3: friendly "your document expired" screen — only shown when
  // the user came from the guest funnel (flag in sessionStorage) and the claim/restore 404'd.
  const [guestDocExpired, setGuestDocExpired] = useState(false);
  // Auto-split state: queue of preview_split payloads awaiting modal interaction.
  // First item is currently displayed; advance by shifting it off.
  const [autoSplitQueue, setAutoSplitQueue] = useState([]);
  const [autoSplitConfirmLoading, setAutoSplitConfirmLoading] = useState(false);
  // True while the CURRENT head's affordability is not yet confirmed-fresh (i.e. a prior
  // doc in the batch just consumed balances and we're refetching this head). The modal
  // shows a neutral loading body instead of the stale cached numbers, so the user never
  // sees old→correct. The first popup of a batch is never marked stale (its preview was
  // just computed), so a single popup feels instant.
  const [autoSplitHeadStale, setAutoSplitHeadStale] = useState(false);
  const [packPurchaseFromAutoSplit, setPackPurchaseFromAutoSplit] = useState(false);
  // When user opens CreditPackModal from inside AutoSplitModal (Comprar paquete CTA),
  // suppress AutoSplitModal so the pack selector is unobstructed. Reset when the pack
  // modal closes (cancel path) — the Stripe redirect path naturally resets via page reload.
  const [autoSplitModalSuppressed, setAutoSplitModalSuppressed] = useState(false);
  // ── YouTube import ──
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeLoading, setYoutubeLoading] = useState(false);
  // dashboard-rediseno (F4): los errores del link van en rojo debajo del campo (decian un aviso
  // flotante); los demas desenlaces (creditos, duplicados, el generico) siguen como hoy.
  const [errorYoutube, setErrorYoutube] = useState(null);
  const [langModal, setLangModal] = useState(null); // null | { url, langs: string[] }
  const [langSearch, setLangSearch] = useState('');
  const dashboardViewedRef = useRef(false);

  // ── Document-level drag & drop ──
  // Captures drag events on the ENTIRE page, not just the dashboard div.
  // PAGER-1: son CUATRO listeners de `document`, y el `drop` SUBE ARCHIVOS. Con
  // la raíz dormida no se registran (si no, soltar un archivo en cualquier
  // pestaña lo subiría por el Panel invisible); al activarse se registran, y al
  // desactivarse el cleanup los quita.
  useEffect(() => {
    if (!activa) return undefined;
    const onDragEnter = (e) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (e.dataTransfer?.types?.includes('Files')) setIsDragging(true);
    };
    const onDragOver = (e) => { e.preventDefault(); };
    const onDragLeave = (e) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current <= 0) { dragCounterRef.current = 0; setIsDragging(false); }
    };
    const onDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      dragCounterRef.current = 0;
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length > 0) uploadFiles(files);
    };
    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
    };
  }, [activa]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open registration modal from ?register=true (post-session conversion)
  // PAGER-1: la URL es del CHASIS, no de la pestaña. Sólo la raíz ACTIVA la lee.
  useEffect(() => {
    if (!activa) return;
    if (searchParams.get('register') === 'true' && user?.is_guest) {
      setConvertModal('save');
      searchParams.delete('register');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, user, activa]);

  // Dashboard viewed tracking (once per page load)
  /* PAGER-1 — UNA POR VISITA, Y LA VISITA ES LA ACTIVACIÓN (firmado).
     `dashboard_viewed` es un ESCALÓN DEL EMBUDO (routes/admin.py, contado por
     usuarios únicos), así que su unidad tiene que seguir siendo "el usuario
     llegó a esta pantalla". Pre-montada, un ref por instancia de componente
     mediría "arranques de app" y —peor— con la raíz que ya no se desmonta
     emitiría UNA sola vez en toda la vida de la app. Por eso el ref se resetea
     en el cleanup DE LA DESACTIVACIÓN: cada vuelta a la pestaña vuelve a contar
     como visita, que es lo que el embudo ya suponía. Con `activa` fija en true
     esto corre exactamente igual que hoy: emite al montar y el cleanup sólo
     ocurre al desmontar. */
  useEffect(() => {
    if (!activa) return undefined;
    if (!dashboardViewedRef.current) {
      dashboardViewedRef.current = true;
      logAnalyticsEvent('dashboard_viewed');
    }
    return () => { dashboardViewedRef.current = false; };
  }, [activa]);

  // F6.7b (fase 2) — EL ATAJO OFFLINE. Con DICTAMEN explícito del vigía (un
  // evento `offline` recibido, jamás una sospecha) no tiene sentido mandar tres
  // peticiones a que agoten el timeout del sistema: axios no fija timeout
  // propio (api.js:18), así que en modo avión esas tres tardan lo que el SO
  // decida — los ~10 s que el usuario pasa mirando el esqueleto. Aquí la
  // pantalla pinta lo local de INMEDIATO. Devuelve true si logró pintar.
  //
  // Solo se toma el atajo sin señal: con red, local-primero NO cancela nada —
  // el camino vivo de abajo corre exactamente como hoy.
  // camara-pills (6.3 a): con el ARRANQUE PENDIENTE (la app pinto de la bodega y `/auth/me` sigue en
  // vuelo, `lib/offline/arranquePendiente.js`) tambien se pinta lo local, y sin pedir nada: el arranque
  // todavia no sabe si hay red ni si la sesion sigue viva. Pero NO es "sin conexion": el chip no se
  // enciende. Cuando `/auth/me` contesta, el usuario fresco vuelve a pedir lo vivo (efecto de montaje).
  const paintFromLocal = useCallback(async () => {
    const pendiente = IS_APP_BUILD && arranquePendiente();
    if (!IS_APP_BUILD || !(isExplicitlyOffline() || pendiente)) return false;
    try {
      const sources = await import('../lib/warehouse/screenSources');
      const fromWarehouse = await sources.dashboardFromWarehouse(userRef.current?.id);
      if (!fromWarehouse) return false;
      setDocs(fromWarehouse.docs);
      setPlanInfo(fromWarehouse.planInfo);
      setUsage(fromWarehouse.usage);
      // t7-3 (A1): sin red, los grupos de la ultima lista que llego con red (si la hay).
      if (Array.isArray(fromWarehouse.grupos)) setGruposDelAlumno(fromWarehouse.grupos);
      if (!pendiente) setOfflineData(true);
      // dashboard-rediseno: la barra de arriba pinta los creditos con la copia de la bodega.
      publicarCreditos({ planInfo: fromWarehouse.planInfo, usage: fromWarehouse.usage, sinRed: !pendiente });
      listaBuenaRef.current = true;
      setFallaDeCarga(false);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  // t7-1b (T4) y t7-1c (P, #479): EL MATERIAL DE LOS GRUPOS DEL ALUMNO (Anexo A 2 y
  // 3). Solo en la web y solo con cuenta: el invitado y la app ni lo piden ni lo
  // pintan. SIN BRINCO: viaja en la MISMA entrada de cache que la lista (`grupos`,
  // junto a `docs`, `planInfo` y `usage`), asi que tiene su misma frescura. Con la
  // semilla fresca se pinta desde ella en el primer render y no se pide nada (una
  // semilla sin la llave vale como "no hay grupos"); sin ella se pide DENTRO de la
  // misma carga que la lista (`fetchDocs`, abajo) y se fija en el mismo lote: un
  // solo pintado, nunca la lista sin los grupos ni los grupos despues. Se vuelve a
  // pedir en cada recarga del Panel (la de volver a la pestana y las demas de
  // `fetchDocs`); el sondeo de 3 s no pasa por `fetchDocs`, asi que nunca lo pide.
  // Un fallo del material no cambia nada en pantalla: se conservan los grupos que ya
  // se veian (en el primer montaje, sin nada previo, ninguno; ver `fetchDocs`). Si
  // llegan dos respuestas cruzadas, gana la ultima pedida; y una lista vacia sobre
  // otra vacia conserva el mismo arreglo.
  /* t7-3 (A1 y A3, #557 y #558): EN LA APP, cada lista de grupos que llega CON RED se guarda en
     la bodega (la foto con la que se pinta sin red y con la que el cartero decide que cards
     bajar), y si cambio lo que el cartero mira (una card nueva, otra fecha u otro
     `material_rev`) se le avisa UNA vez, con la lista de documentos que ya se tiene en la mano
     (cero peticiones de mas). En la web no hace nada. */
  const huellaDeCardsRef = useRef(null);
  const guardarGruposDeLaApp = useCallback((grupos, documentos) => {
    if (!IS_APP_BUILD || !Array.isArray(grupos)) return;
    const huella = huellaDeCards(grupos);
    const cambio = huella !== huellaDeCardsRef.current;
    huellaDeCardsRef.current = huella;
    import('../lib/warehouse/cardsDelGrupo')
      .then((m) => m.guardarGruposEnBodega(grupos))
      .then(() => {
        if (!cambio) return undefined;
        return import('../lib/warehouse/converge').then((c) => c.convergeWarehouse(
          c.CONVERGE_REASON.GRUPOS, Array.isArray(documentos) ? { documents: documentos } : {}));
      })
      .catch(() => {});
  }, []);
  const [gruposDelAlumno, setGruposDelAlumno] = useState(
    () => (dashSeedFresh && Array.isArray(dashSeed.data.grupos) ? dashSeed.data.grupos : []),
  );
  const gruposPedidoRef = useRef(0);
  // remates-1-3-0b (X1): la tarjeta del grupo se pinta SIEMPRE que el alumno es miembro de
  // un grupo con publicaciones. Hasta hoy, si la ruta fallaba en el PRIMER montaje (red,
  // 5xx, el 429 del freno compartido con /groups/memberships) no habia grupos previos que
  // conservar y la tarjeta faltaba hasta el siguiente refresco. Ahora un fallo sin nada
  // pintado programa UN reintento del material solo (no de la lista), a los 5 s; si la
  // respuesta ya fue superada por otra peticion, se descarta como siempre.
  const gruposDelAlumnoRef = useRef(gruposDelAlumno);
  useEffect(() => { gruposDelAlumnoRef.current = gruposDelAlumno; }, [gruposDelAlumno]);
  const reintentoMaterialRef = useRef(null);
  useEffect(() => () => { if (reintentoMaterialRef.current) clearTimeout(reintentoMaterialRef.current); }, []);
  const reintentarMaterial = useCallback((u, pedido) => {
    if (reintentoMaterialRef.current || gruposDelAlumnoRef.current.length > 0) return;
    reintentoMaterialRef.current = setTimeout(async () => {
      reintentoMaterialRef.current = null;
      if (pedido !== gruposPedidoRef.current) return;
      const grupos = await cargarMaterialDelGrupo(u, null);
      if (grupos !== null && pedido === gruposPedidoRef.current) {
        setGruposDelAlumno((prev) => (prev.length === 0 && grupos.length === 0 ? prev : grupos));
        guardarGruposDeLaApp(grupos, null);
      }
    }, REINTENTO_MATERIAL_MS);
  }, [guardarGruposDeLaApp]);
  // La misma compuerta al pintar: una lista vieja nunca se le pinta a un invitado.
  // t7-3 (A1, #557): la compuerta `IS_APP_BUILD` se retiro: la app pinta la tarjeta del grupo.
  const verGruposDelAlumno = !!user && !user.is_guest;

  const fetchDocs = useCallback(async (silent = false) => {
    // Atajo offline: si pinta, no se lanza ni una petición condenada.
    if (await paintFromLocal()) { setLoading(false); return null; }
    // t7-1c (P, #479): el material de los grupos va en el MISMO viaje que la lista.
    // Lee `userRef` para no meter `user` en las dependencias de `fetchDocs`. Se pide
    // con `null` como respuesta de fallo (red, 5xx, el 429 del freno): con `null` no
    // se toca el estado, asi que se conservan los grupos que ya se veian y la entrada
    // de cache guarda eso mismo, lo que se ve.
    const u = userRef.current;
    const conGrupos = !!u && !u.is_guest;
    const pedidoGrupos = conGrupos ? ++gruposPedidoRef.current : 0;
    try {
      const [docsRes, planRes, usageRes, grupos] = await Promise.all([
        api.get('/documents'),
        api.get('/auth/plan-info'),
        api.get('/documents/usage'),
        conGrupos ? cargarMaterialDelGrupo(u, null) : null,
      ]);
      // Render-first / refresh-after: we only ever set state with fresh data (never blank
      // the list mid-refresh), so a re-derive of the active library + Locked section +
      // counters happens in one paint with no empty frame.
      setDocs(docsRes.data.documents);
      setPlanInfo(planRes.data);
      setUsage(usageRes.data);
      // dashboard-rediseno: el chip de creditos de la barra de arriba lee lo que el Panel ya pide
      // (`/auth/plan-info` y `/documents/usage`): cero llamadas nuevas.
      publicarCreditos({ planInfo: planRes.data, usage: usageRes.data, sinRed: false });
      listaBuenaRef.current = true;
      setFallaDeCarga(false);
      if (conGrupos && pedidoGrupos === gruposPedidoRef.current && grupos !== null) {
        setGruposDelAlumno((prev) => (prev.length === 0 && grupos.length === 0 ? prev : grupos));
        guardarGruposDeLaApp(grupos, docsRes.data.documents);
      } else if (conGrupos && grupos === null && pedidoGrupos === gruposPedidoRef.current) {
        reintentarMaterial(u, pedidoGrupos);
      }
      setOfflineData(false); // F6.4b: la red volvió → normalización sola
      // F6.7b post-smoke — SCR.PAINT: dato FRESCO del servidor en la mano. Es
      // el otro extremo de la medición que a Carlos le faltaba: con NET.UP y
      // esto en el visor, "cuánto tarda la app en volver a la verdad viva tras
      // recuperar la señal" se lee restando dos marcas de tiempo, sin adivinar.
      // Solo el camino VIVO lo emite — pintar de bodega no es dato fresco.
      diagLog('SCR.PAINT', { screen: 'dashboard', docs: docsRes.data.documents.length });
      return { docs: docsRes.data.documents, plan: planRes.data };
    } catch (err) {
      // F6.4b: fetch vivo caído con forma-de-red en nativo → la pantalla se
      // pinta desde la bodega con el MISMO shape ({docs, planInfo, usage});
      // planInfo/usage salen de la última foto conocida (plan_snapshot) o
      // quedan null → los widgets muestran su estado vacío, sin inventar
      // ceros. Sin toast: la pantalla quedó poblada y honesta.
      if (IS_APP_BUILD && isNetworkShapedError(err)) {
        try {
          const sources = await import('../lib/warehouse/screenSources');
          const fromWarehouse = await sources.dashboardFromWarehouse(userRef.current?.id);
          if (fromWarehouse) {
            setDocs(fromWarehouse.docs);
            setPlanInfo(fromWarehouse.planInfo);
            setUsage(fromWarehouse.usage);
            if (Array.isArray(fromWarehouse.grupos)) setGruposDelAlumno(fromWarehouse.grupos);
            setOfflineData(true);
            publicarCreditos({ planInfo: fromWarehouse.planInfo, usage: fromWarehouse.usage, sinRed: true });
            listaBuenaRef.current = true;
            setFallaDeCarga(false);
            return null;
          }
        } catch (whErr) {
          // sin bodega utilizable → camino de error de siempre
        }
      }
      // Silent path (on-focus refetch): a transient blip must not spam a toast.
      // dashboard-rediseno (I2): el aviso flotante "Failed to load documents" ya no sale; sin una
      // lista a la vista se pinta el recuadro "We couldn't load your documents" en su lugar.
      if (!silent && !listaBuenaRef.current) setFallaDeCarga(true);
      return null;
    } finally {
      setLoading(false);
    }
  // dashboard-rediseno (I2): `t` ya no se usa dentro (se fue el aviso flotante), pero se queda en
  // las dependencias para que cambiar de idioma siga recreando `fetchDocs` y volviendo a pedir la
  // lista, como hoy (ver el efecto de montaje, abajo).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, paintFromLocal, reintentarMaterial, guardarGruposDeLaApp]);
  // I2: "Retry" vuelve a pedir la lista como hoy.
  const reintentarCarga = useCallback(async () => {
    setReintentandoCarga(true);
    try { await fetchDocs(); } finally { setReintentandoCarga(false); }
  }, [fetchDocs]);

  // Cache fresco → el fetch de montaje se omite UNA sola vez (regla F4.3b: el
  // dato no pudo cambiar sin que lo sepamos — los cambios llegan por eventos o
  // los provoca el usuario). Re-runs posteriores del efecto (p. ej. cambio de
  // idioma re-crea fetchDocs) sí refetchean, como siempre.
  // PAGER-1: RED. Una raíz dormida no pide datos — el fetch inicial es lo
  // primero que espera a la activación.
  const skippedSeededFetchRef = useRef(false);
  useEffect(() => {
    if (!activa) return;
    if (!user) return;
    // t7-1c (P, #479): con el cache fresco tampoco se piden los grupos: viajan en él.
    if (dashSeedFresh && !skippedSeededFetchRef.current) { skippedSeededFetchRef.current = true; return; }
    fetchDocs();
  }, [user, fetchDocs, dashSeedFresh, activa]);

  // Write-through: CUALQUIER camino que actualice docs/planInfo/usage (fetch,
  // poll de processing, delete, upload, undo) deja el cache alineado con lo que
  // el usuario está viendo. Un solo punto de escritura.
  // F6.4b (decisión firmada opción A): el MISMO punto persiste la última foto
  // {planInfo, usage} en la bodega (plan_snapshot) — solo en nativo, solo con
  // dato VIVO (offlineData=false: los valores de bodega no se re-escriben a
  // sí mismos) y fire-and-forget (jamás bloquea el render).
  // F6.7b: por el MISMO punto pasa ahora la FOTO LOCAL DE LA LISTA. Mismas dos
  // condiciones que la foto de plan — nativo y dato VIVO (`offlineData` false:
  // una lista que salió de la propia foto jamás se re-escribe sobre sí misma) —
  // y va aparte de `planInfo && usage` a propósito: la lista puede llegar
  // aunque el plan no, y perder la foto por eso sería regalar las tarjetas del
  // próximo arranque sin red.
  // t7-1c (P, #479): la entrada lleva tambien los grupos del alumno, para que tengan
  // la misma frescura que la lista (con invitado es siempre un arreglo vacio: ni se
  // piden ni se pintan; en la app se piden y se pintan desde t7-3, A1).
  useEffect(() => {
    if (loading) return;
    writeScreenCache('dashboard', { docs, planInfo, usage, grupos: gruposDelAlumno });
    if (IS_APP_BUILD && !offlineData) {
      import('../lib/warehouse/snapshots')
        .then((m) => {
          if (planInfo && usage) m.writePlanSnapshot({ planInfo, usage });
          m.writeDocsSnapshot(docs);
        })
        .catch(() => {});
    }
  }, [docs, planInfo, usage, gruposDelAlumno, loading, offlineData]);

  // On-focus / on-visibility silent refetch (throttled). The processing poll below only
  // runs while a doc is processing, so this is what catches DB-side changes on an idle
  // dashboard: the downgrade boundary archiving docs, an undo, or a delete that drops the
  // user back under the tope — all reflected without a manual reload.
  // PAGER-1: foco / `visibilitychange` / eventos de datos → todos hacen RED, y
  // el throttle de 10 s es por instancia (no coordina entre raíces). Sólo la
  // raíz activa los escucha.
  const lastDocsFetchRef = useRef(0);
  useEffect(() => {
    if (!activa) return undefined;
    if (!user) return undefined;
    const onRefocus = () => {
      if (shouldRefetchOnRefocus(Date.now(), lastDocsFetchRef.current, document.visibilityState)) {
        lastDocsFetchRef.current = Date.now();
        fetchDocs(true);
      }
    };
    const onDocsChanged = () => { lastDocsFetchRef.current = Date.now(); fetchDocs(true); };
    window.addEventListener('focus', onRefocus);
    document.addEventListener('visibilitychange', onRefocus);
    window.addEventListener('studybuddy:docs-changed', onDocsChanged);
    // F6.7a — LA MISMA señal que ya repinta refresca también la lista: el
    // downloader emite `local-material-ready` UNA vez al cerrar una corrida con
    // material committeado, y aquí entra por el MISMO manejador que
    // `docs-changed`. Una señal, un repintado, sin brincos. No dispara otra
    // descarga (AuthContext solo escucha docs-changed) → bucle imposible.
    window.addEventListener('studybuddy:local-material-ready', onDocsChanged);
    return () => {
      window.removeEventListener('focus', onRefocus);
      document.removeEventListener('visibilitychange', onRefocus);
      window.removeEventListener('studybuddy:docs-changed', onDocsChanged);
      window.removeEventListener('studybuddy:local-material-ready', onDocsChanged);
    };
  }, [user, fetchDocs, activa]);

  // post-t7-2c (D4, #m): AL UNIRSE A UN GRUPO, LA TARJETA AL INSTANTE. Las tres puertas de unirse (el
  // empujon de la liga, la tarjeta de Ajustes y la pagina de la liga) avisan `studybuddy:grupos-cambiados` al
  // exito del `POST /groups/join`, y aqui se vuelve a pedir SOLO el material de los grupos (aunque ya
  // haya grupos a la vista), sin recargar la pagina. Mismas reglas que la carga de la lista: solo con
  // cuenta, gana la ultima pedida, un fallo (`null`) no cambia nada en pantalla, una lista vacia sobre
  // otra vacia conserva el mismo arreglo, y en la app la lista que llega con red se guarda en la bodega
  // por `guardarGruposDeLaApp` (con los documentos que ya se tienen en la mano: cero peticiones de mas).
  const docsVistosRef = useRef(docs);
  useEffect(() => { docsVistosRef.current = docs; }, [docs]);
  useEffect(() => {
    if (!activa) return undefined;
    const alCambiarLosGrupos = async () => {
      const u = userRef.current;
      if (!u || u.is_guest) return;
      const pedido = ++gruposPedidoRef.current;
      const grupos = await cargarMaterialDelGrupo(u, null);
      if (grupos === null || pedido !== gruposPedidoRef.current) return;
      setGruposDelAlumno((prev) => (prev.length === 0 && grupos.length === 0 ? prev : grupos));
      guardarGruposDeLaApp(grupos, docsVistosRef.current);
    };
    window.addEventListener('studybuddy:grupos-cambiados', alCambiarLosGrupos);
    return () => window.removeEventListener('studybuddy:grupos-cambiados', alCambiarLosGrupos);
  }, [activa, guardarGruposDeLaApp]);

  // F6.7b (fase 2) — la señal VOLVIÓ (dictamen del vigía): un refetch silencioso
  // reincorpora la pantalla a la verdad viva y APAGA el chip solo, sin navegar
  // y sin recargar. Reacción a un hecho, no un reloj: solo corre en el flanco
  // de subida del veredicto (`net.isOnline` pasa a true una vez por evento).
  // PAGER-1: NET.UP es la multiplicación más limpia (invalida seis llaves de
  // cache, y iOS despacha `online` en ráfaga). Sólo la raíz activa reacciona.
  useEffect(() => {
    if (!activa) return;
    if (!user || !net.isOnline) return;
    lastDocsFetchRef.current = Date.now();
    fetchDocs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net.isOnline, user, activa]);

  // F6.7a — convergencia al MONTAR el Panel. No es polling: es la pregunta que
  // esta pantalla debería haber hecho siempre al abrirse ("¿el material que
  // estoy pintando está de verdad en el teléfono?"). Si no hay hueco, no cuesta
  // ni una llamada de red más allá del GET /documents que ya se hizo.
  // PAGER-1: el motivo `dashboard-mount` está en PHOTO_WAKE porque es un ACTO
  // DEL USUARIO y no un reloj (converge.js). Colgado de la activación, el
  // motivo vuelve a ser verdad: se dispara cuando el usuario llega a la
  // pantalla, no cuando el pager la pre-monta.
  useEffect(() => {
    if (!activa) return;
    if (!IS_APP_BUILD || !user) return;
    import('../lib/warehouse/converge')
      .then((c) => c.convergeWarehouse(c.CONVERGE_REASON.DASHBOARD_MOUNT))
      .catch(() => {});
    // Solo al ACTIVARSE con identidad — los refrescos posteriores tienen sus
    // propios momentos (foco, poller, evento post-commit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activa]);

  // Bounded re-poll after an upgrade (the unarchive runs on the Stripe webhook, ~1-2s behind).
  // Reads only, every 2s, max 5 attempts (~10s), stops early once archived drops or the plan
  // changes. Not permanent background polling — only fires on an upgrade signal.
  const repollRef = useRef(null);
  const docsSnapRef = useRef(docs);
  const planSnapRef = useRef(planInfo);
  useEffect(() => { docsSnapRef.current = docs; }, [docs]);
  useEffect(() => { planSnapRef.current = planInfo; }, [planInfo]);
  const startUpgradeRepoll = useCallback(() => {
    if (repollRef.current) return; // already polling — don't stack
    const initialArchived = (docsSnapRef.current || []).filter(d => d.status === 'archived').length;
    const initialPlan = planSnapRef.current?.plan;
    let attempts = 0;
    repollRef.current = setInterval(async () => {
      attempts += 1;
      const res = await fetchDocs(true);
      const newArchived = res ? res.docs.filter(d => d.status === 'archived').length : initialArchived;
      const planChanged = res && initialPlan && res.plan?.plan !== initialPlan;
      if ((res && newArchived < initialArchived) || planChanged || attempts >= 5) {
        clearInterval(repollRef.current);
        repollRef.current = null;
      }
    }, 2000);
  }, [fetchDocs]);

  // Plan-change freshness: same-tab handoff (upgraded on /upgrade, came back) via a durable
  // flag consumed on mount, + live cross-tab signal (BroadcastChannel / storage). Either one
  // kicks the bounded re-poll. Covers "upgrade from any origin" without a manual reload.
  // PAGER-1: arma un `setInterval` que hace RED, y `consumeRecentPlanChange()`
  // es una lectura que CONSUME la bandera — dormida se la robaría a la raíz que
  // el usuario sí está viendo.
  useEffect(() => {
    if (!activa) return undefined;
    if (!user) return undefined;
    if (consumeRecentPlanChange()) startUpgradeRepoll();
    const unsub = subscribePlanChanged(() => startUpgradeRepoll());
    return () => { unsub(); if (repollRef.current) { clearInterval(repollRef.current); repollRef.current = null; } };
  }, [user, startUpgradeRepoll, activa]);

  // If the last locked doc gets unlocked while the Bloqueados tab is open, fall back to Activos.
  useEffect(() => {
    if (docTab !== 'active' && !docs.some(d => d.status === 'archived')) setDocTab('active');
  }, [docs, docTab]);

  // Poll for processing documents + animate progress
  // PAGER-1: `GET /documents` cada 3 s (20/min) MÁS un re-render cada 500 ms que
  // reconcilia la lista entera. Es el timer más caro del repo y hoy muere al
  // salir del Panel; con el pager tiene que morir igual, al DESACTIVARSE.
  useEffect(() => {
    if (!activa) return undefined;
    const processingDocs = docs.filter(d => d.status === 'processing');

    // Re-render tick: while any doc is processing, bump nowTick so the progress bar (a pure
    // function of now − processing_started_at) advances smoothly. No per-doc timer/state, so a
    // tab-change remount re-reads the doc anchor and paints the correct % at once (no restart).
    if (processingDocs.length === 0) return;
    const tick = setInterval(() => setNowTick(Date.now()), 500);

    const interval = setInterval(async () => {
      try {
        const res = await api.get('/documents');
        const newDocs = res.data.documents;

        // Per-doc UI updates (progress bar + justProcessedIds). Status-transition TOASTS
        // are emitted by <NotificationListener /> at App level so they appear on any route
        // (per design Decision #27). Don't duplicate them here.
        processingDocs.forEach(oldDoc => {
          const newDoc = newDocs.find(d => d.id === oldDoc.id);
          if (newDoc && newDoc.status === 'ready' && oldDoc.status === 'processing') {
            // Jump to 100% then clear
            // #412 — EL 100 ES DEL ATERRIZAJE, NO DEL SERVIDOR. En la app, un
            // documento que el servidor acaba de dar por listo pero que todavía
            // no está copiado sigue pintándose como procesando: rematarle la
            // barra en 100 sería justo la promesa que #412 quita. Su 100 llega
            // con `local-material-ready` (efecto de abajo). Sin app —o ya
            // copiado— este camino es el de siempre, byte por byte.
            if (!IS_APP_BUILD || isCopiedToDevice(presenceById.get(oldDoc.id))) {
              setProcessProgress(prev => ({ ...prev, [oldDoc.id]: 100 }));
              setTimeout(() => {
                setProcessProgress(prev => { const n = { ...prev }; delete n[oldDoc.id]; return n; });
              }, 600);
            }
            setJustProcessedIds(prev => new Set([...prev, newDoc.id]));
          } else if (newDoc && newDoc.status === 'partial' && oldDoc.status === 'processing') {
            setProcessProgress(prev => { const n = { ...prev }; delete n[oldDoc.id]; return n; });
            setJustProcessedIds(prev => new Set([...prev, newDoc.id]));
          } else if (newDoc && newDoc.status === 'error' && oldDoc.status === 'processing') {
            setProcessProgress(prev => { const n = { ...prev }; delete n[oldDoc.id]; return n; });
          }
        });

        setDocs(newDocs);
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    return () => { clearInterval(interval); clearInterval(tick); };
    // #412: presenceById entra en las deps porque el snap del 100 lo consulta.
    // No cambia la cadencia: docs ya re-arma este efecto en cada tick.
  }, [docs, t, activa, presenceById]);

  // D19 (Carlos, 15-oct-2026): LA PISTA DE A12 NO DEPENDE DE QUIEN VEA PRIMERO EL PASO A LISTO. Antes solo
  // la marcaba el sondeo de arriba; si el aviso global de NotificationListener lo veia antes, mandaba
  // `studybuddy:docs-changed`, `fetchDocs` traia la lista ya lista y la pista no salia. Ahora se marca en
  // UN solo sitio, venga de donde venga la lista nueva (el sondeo, ese aviso, el foco): lo que estaba
  // procesando en la lista de antes y en la de ahora esta listo o parcial. Dura como hoy, hasta la
  // primera vez que se abre (`sb_doc_studied_`). La primera lista no marca nada.
  const listaAnteriorRef = useRef(null);
  useEffect(() => {
    const antes = listaAnteriorRef.current;
    listaAnteriorRef.current = docs;
    if (!antes) return;
    const procesando = new Set(antes.filter((d) => d.status === 'processing').map((d) => d.id));
    if (procesando.size === 0) return;
    const recien = docs
      .filter((d) => procesando.has(d.id) && (d.status === 'ready' || d.status === 'partial'))
      .map((d) => d.id);
    if (recien.length === 0) return;
    setJustProcessedIds((prev) => (recien.every((id) => prev.has(id)) ? prev : new Set([...prev, ...recien])));
  }, [docs]);

  /* ── #412 — EL 100 DEL ATERRIZAJE ────────────────────────────────────────
     La otra mitad del snap de arriba. Cuando el cartero cierra una corrida con
     material committeado emite UNA vez `local-material-ready` con los ids, y
     ESE es el instante en que un documento pasa a estar de verdad en el
     teléfono: su barra remata en 100 y se apaga 600 ms después, exactamente
     como lo hacía el snap del sondeo. Cero timers nuevos de vigilancia: es un
     evento, no un reloj, y el único `setTimeout` es el mismo apagado de 600 ms
     que este archivo ya usaba.
     No se filtra por `activa`: el material puede aterrizar con el Panel dormido
     y el 100 debe estar puesto —y apagado— antes de que se vuelva a mirar. */
  useEffect(() => {
    if (!IS_APP_BUILD) return undefined;
    const alAterrizar = (e) => {
      const ids = e?.detail?.documentIds;
      if (!Array.isArray(ids) || ids.length === 0) return;
      setProcessProgress(prev => {
        const n = { ...prev };
        for (const id of ids) n[id] = 100;
        return n;
      });
      setTimeout(() => {
        setProcessProgress(prev => {
          const n = { ...prev };
          for (const id of ids) delete n[id];
          return n;
        });
      }, 600);
    };
    window.addEventListener('studybuddy:local-material-ready', alAterrizar);
    return () => window.removeEventListener('studybuddy:local-material-ready', alAterrizar);
  }, []);

  // Strip the ?credits_purchased=1 param on a plain (non-auto-split) pack-purchase return.
  // The auto-split-return effect owns the strip when ?from_auto_split=true is also present.
  // (The legacy pending_credits carril — its banner, unmount cleanup and no-ID
  // process-pending auto-trigger — was removed in Phase 3; the new pending carril resumes
  // only via explicit IDs from the /pending section.)
  // PAGER-1: lee la URL del chasis Y le ESCRIBE (`history.replaceState`), o sea
  // reescribiría la barra de direcciones de la pestaña que el usuario está
  // viendo. Sólo la raíz activa.
  // FESTEJO-PACKS — el retorno de Stripe deja de ser mudo. El refresco silencioso
  // de abajo NO cambia (mismo `replaceState`, mismo `fetchDocs`, mismo orden): lo
  // único nuevo es que además se enciende la palomita, que se pinta ENCIMA del
  // panel ya refrescado. La rama de auto-split sigue saliéndose una línea antes y
  // por lo tanto no festeja: ese retorno tiene su propio flujo (el modal de la
  // partición espera al usuario del otro lado) y taparlo con una palomita sería
  // meter un clic entre el pago y el trabajo que el usuario venía a hacer.
  const [packSuccess, setPackSuccess] = useState(false);
  useEffect(() => {
    if (!activa) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('from_auto_split') === 'true') return;
    if (params.get('credits_purchased') === '1') {
      window.history.replaceState({}, '', window.location.pathname);
      fetchDocs();
      setPackSuccess(true);
    }
  }, [fetchDocs, activa]);

  // ── OCR-ALUMNO (remate R3, firma #330 b): UN ESCANEADO LARGO PREGUNTA ANTES DE COBRAR ──
  // Cuando la lectura con los ojos sale de más de 48,500 letras, el fondo deja el sobre
  // del troceo en el documento (`lectura_estado: "por_confirmar"` + `preview_split`) y
  // se abre EL MISMO modal de siempre con ese sobre. La tarjeta de ese estado la pinta
  // la tanda 1: chip «Listo para procesar», su línea y su botón «Procesar».
  //
  // ── TARJETA DEL ALUMNO, TANDA 2: LA VENTANA SALE UNA SOLA VEZ ────────────────
  // La regla es de TRANSICIÓN, no de presencia: se abre sola en el sondeo en que el
  // documento PASA a «por confirmar» con el Panel abierto, y sólo ahí. Antes se abría
  // «una vez por carga de página», que en la práctica era muchas veces: la ref muere
  // con el componente y el Panel se re-monta al recargar Y al ir a otra pantalla y
  // volver (el router monta una raíz a la vez). El alumno cerraba la ventana, se iba a
  // Estudiar, volvía, y la ventana estaba otra vez encima.
  //
  // Por eso la PRIMERA respuesta de `GET /documents` de cada montaje SIEMBRA el
  // registro —todo lo que ya está «por confirmar» entra sin abrir nada— y de ahí en
  // adelante sólo abre lo que no estaba. La marca no se borra en toda la visita: la
  // única forma de volver a ver la ventana es el botón de la tarjeta.
  const porConfirmarAbiertosRef = useRef(new Set());
  // La foto inicial de `docs` NO es una respuesta del servidor: es `[]`, o el cache
  // fresco de pantalla (que sí es una foto, y por eso se siembra igual). Guardar su
  // identidad es lo que distingue «todavía no ha llegado nada» de «llegó una lista
  // vacía»: un `fetchDocs` que falla no llama a `setDocs`, así que la identidad no
  // cambia y la siembra espera al sondeo que sí traiga datos.
  const docsInicialesRef = useRef(docs);
  const primerSondeoRef = useRef(true);
  const esInvitado = !!user?.is_guest;
  useEffect(() => {
    if (!activa) return;
    if (!dashSeedFresh && docs === docsInicialesRef.current) return;
    // La siembra mira el ESTADO a secas, no el sobre: es estrictamente más amplia que
    // la condición de abrir, así que sólo puede callar, nunca abrir de más.
    const esperando = docs.filter((d) => d.lectura_estado === 'por_confirmar');
    if (primerSondeoRef.current) {
      primerSondeoRef.current = false;
      for (const d of esperando) porConfirmarAbiertosRef.current.add(d.id);
      return;
    }
    // APP-1 B2 (#399): `lectura === 'ojos'` acota este efecto al carril del que
    // siempre fue. Desde esta tanda el texto grande también deja un `por_confirmar`
    // con sobre, y el suyo NO se abre solo en ningún sondeo: su ventana la abre el
    // botón de la tarjeta y nadie más. La diferencia no es capricho — en el escaneado
    // la lectura ACABA de terminar sola, días después de la subida, y la apertura es
    // el aviso de que ya se puede decidir; en el texto el alumno acaba de estar ahí.
    // APP-1 B3 (#400): y `ventana_cerrada` la calla en CUALQUIER aparato. Hasta hoy
    // el «ya la cerré» vivía en el `localStorage` de un navegador, así que la misma
    // transición volvía a abrirla en la PC de un documento cerrado en el teléfono.
    // Sólo puede callar, nunca abrir de más: la regla de `tarjeta-alumno-2` (una sola
    // vez, en la transición) sigue siendo la que manda cuando el campo no está.
    // post-t7-1 (H2.4, H2.5): el documento entero que se quedo en el Panel (Pendientes lleno o invitado) tambien
    // abre su ventana sola, una vez, en la transicion (su sobre no trae `affordability`: no tiene partes)
    const nuevos = esperando.filter((d) => d.lectura === 'ojos' && !d.ventana_cerrada
      && d.preview_split && (d.preview_split.affordability || d.preview_split.documento_entero)
      && !porConfirmarAbiertosRef.current.has(d.id));
    if (nuevos.length === 0) return;
    for (const d of nuevos) porConfirmarAbiertosRef.current.add(d.id);
    setAutoSplitQueue((prev) => {
      const ids = new Set(prev.map((p) => p.temp_upload_id));
      // REMATES (PR #349, A-6 / #339): `guest_signup_required` se decide con el usuario
      // de AHORA, no con el que leyó. El sobre se guardó en el placeholder cuando el
      // alumno era invitado; si ya creó su cuenta (conversión = el mismo usuario), el
      // documento sigue siendo suyo y el modal es el normal, no el funnel.
      const sobres = nuevos
        .map((d) => ({ ...d.preview_split, temp_upload_id: d.preview_split.temp_upload_id || `ph_${d.id}`,
          documento_base_id: d.id, guest_signup_required: esInvitado,
          ...(d.preview_split.documento_entero ? { original_filename: d.filename } : {}) }))
        .filter((p) => !ids.has(p.temp_upload_id));
      return sobres.length ? [...prev, ...sobres] : prev;
    });
  }, [docs, activa, esInvitado, dashSeedFresh]);

  // Cancelar un sobre de placeholder = borrar el documento (la cascada de siempre).
  //
  // APP-1 B4 (#401): AHORA DEVUELVE SI EL SERVIDOR CONFIRMÓ. El `catch` mudo de antes
  // se tragaba el fallo y el llamador pintaba «Documento eliminado» igual, sobre un
  // documento que seguía vivo. El toast verde es una afirmación sobre el servidor, así
  // que tiene que salir del servidor. `true` = 2xx; `false` = cualquier otra cosa.
  const borrarPlaceholderPorConfirmar = useCallback(async (head) => {
    let borrado = false;
    try {
      await api.delete(`/documents/${head.documento_base_id}`);
      borrado = true;
    } catch { /* ya no existe u otra pestaña lo resolvió: el refresco lo dice */ }
    fetchDocs();
    return borrado;
  }, [fetchDocs]);

  // ── Auto-split: restore modal queue on mount (guest split funnel F2) ──
  // useAutoSplitRestore runs restoreAutoSplitState once per user: (1) pending guest
  // claim (non-guest users), (2) the generic sessionStorage queue revalidation, (3)
  // server fallback via /documents/temp-uploads/mine when the queue came up empty.
  // StrictMode-proof (the old inline one-shot-ref effect lost the restore on the dev
  // double-mount — that was the post-signup "modal never reopens" bug) and merge-safe
  // against an upload the user starts while the restore is still in flight.
  // Funnel arrival? Read ONCE at mount — the restore consumes the flag afterwards.
  // Non-funnel users: false forever → they never see the veil (their restore stays silent).
  const [fromFunnelArrival] = useState(() => {
    try { return sessionStorage.getItem(GUEST_FUNNEL_FLAG_KEY) === 'true'; } catch { return false; }
  });

  /* PAGER-1 — el restore hace hasta TRES peticiones (claim, temp-upload,
     temp-uploads/mine), así que espera a la activación. El gate va por la
     IDENTIDAD que se le entrega: sin usuario el hook no arranca, y su
     `restoredForUserRef` —la garantía de "una vez por usuario"— vive dentro del
     hook y NO se toca, así que al activarse corre exactamente una vez. */
  const usuarioParaRestore = activa ? user : null;
  const restoreStatus = useAutoSplitRestore(usuarioParaRestore, api, {
    onExpired: () => {
      // F3: friendly expiry screen instead of the silent drop — funnel users only
      // (the lib only reports expired when the funnel flag was present).
      trackEvent('guest_split_doc_expired', { action: 'shown' });
      setGuestDocExpired(true);
    },
    onRestored: (restored, resumeMethod) => {
      setAutoSplitQueue(prev => {
        const merged = mergeRestoredQueue(prev, restored);
        writeAutoSplitQueue(merged);
        return merged;
      });
      // Track resumption only when it's the guest funnel resuming: a fresh claim, or
      // a restored preview still marked guest (guest reload). Ordinary auth-user queue
      // restores stay untracked, as before.
      const fromGuestFunnel = resumeMethod === 'claim' || restored.some(p => p.guest_signup_required);
      if (resumeMethod && fromGuestFunnel) trackEvent('guest_split_resumed', { method: resumeMethod });
    },
  });

  // Interaction-gap veil: blocks the dashboard for funnel arrivals while the restore
  // is in flight (≤10s); resolves into the modal, the expired screen, or a retry toast.
  const showRestoreVeil = useGuestRestoreVeil(
    fromFunnelArrival,
    restoreStatus,
    autoSplitQueue.length > 0 || guestDocExpired,
    () => toast.error(t('dashboard:auto_split.guest_restore_failed_toast')),
  );

  // ── Auto-split: handle return-from-Stripe intent flow (?from_auto_split=true + sessionStorage intent) ──
  // PAGER-1: lee la URL del chasis, consume `sessionStorage` y hace red. Sólo la raíz activa.
  useEffect(() => {
    if (!activa) return undefined;
    const params = new URLSearchParams(window.location.search);
    const fromAutoSplit = params.get('from_auto_split') === 'true';
    if (!fromAutoSplit) return undefined;

    let intent = null;
    try {
      const raw = sessionStorage.getItem(AUTOSPLIT_INTENT_KEY);
      if (raw) intent = JSON.parse(raw);
    } catch { /* ignored */ }

    if (!intent || !intent.temp_upload_id) {
      // Flag without intent — stale param, just clean up.
      stripAutoSplitUrlParams();
      return undefined;
    }

    const sessionId = params.get('session_id');
    const creditsPurchased = params.get('credits_purchased') === '1';
    let cancelled = false;

    (async () => {
      try {
        // 1. For pack purchase return: poll pack-status until webhook lands (best-effort, no hard stop on failure)
        if (intent.intent_type === 'pack_purchase' && sessionId) {
          for (let attempt = 0; attempt < PACK_POLL_MAX_ATTEMPTS; attempt++) {
            if (cancelled) return;
            try {
              const res = await api.get(`/payments/pack-status/${sessionId}`);
              if (res.data?.status === 'complete') break;
            } catch { /* keep polling */ }
            await new Promise(r => setTimeout(r, PACK_POLL_INTERVAL_MS));
          }
        }

        // 2. Confirm temp_upload still alive (TTL 60min)
        let preview = null;
        try {
          const tempRes = await api.get(`/documents/temp-upload/${intent.temp_upload_id}`);
          preview = tempRes.data;
        } catch (e) {
          if (e.response?.status === 404) {
            toast.error(t('dashboard:auto_split.session_expired_toast'));
            sessionStorage.removeItem(AUTOSPLIT_INTENT_KEY);
            writeAutoSplitQueue([]);
            setAutoSplitQueue([]);
            return;
          }
          throw e;
        }

        // 3. Refresh docs/plan/usage; the modal re-renders off the re-fetched affordability
        //    (which now reflects the just-purchased credits).
        await fetchDocs();

        // 5. Ensure the modal for this temp_upload is at the head of the queue.
        //
        // TANDA I (B1) — LA ÚNICA EXCEPCIÓN FIRMADA QUE SÍ REABRE. Si el usuario había
        // cerrado este modal con la X y luego se fue a pagar POR ese mismo documento,
        // el pago reautoriza el evento: se LEVANTA el cierre en vez de saltárselo. Así
        // la reapertura dura más que este montaje —los tres caminos del restore ven un
        // evento sin cerrar— en vez de volver a silenciarse en la siguiente entrada.
        //
        // APP-1 B4 (#401): la excepción no se pierde, se muda. Antes levantaba la nota
        // del `localStorage` (`undismissAutoSplit`); ahora levanta el bool de la FILA,
        // que es donde vive la decisión desde B3 — y por tanto la reapertura vale
        // también en el aparato desde el que el alumno vuelva de pagar, que puede no
        // ser el mismo desde el que cerró.
        api.post('/documents/ventana-cerrada', {
          temp_upload_id: preview.temp_upload_id,
          ...(preview.documento_base_id ? { documento_base_id: preview.documento_base_id } : {}),
          cerrada: false,
        }).catch(() => { /* best-effort: la cola local ya la pone al frente */ });
        if (!cancelled) {
          setAutoSplitQueue(prev => {
            const others = prev.filter(p => p.temp_upload_id !== preview.temp_upload_id);
            const next = [preview, ...others];
            writeAutoSplitQueue(next);
            return next;
          });
        }

        sessionStorage.removeItem(AUTOSPLIT_INTENT_KEY);
      } catch (err) {
        if (!cancelled) toast.error(t('dashboard:auto_split.pack_verification_timeout'));
        // Leave intent in sessionStorage so a manual refresh can retry.
      } finally {
        if (!cancelled) stripAutoSplitUrlParams();
      }
    })();

    return () => { cancelled = true; };
  }, [fetchDocs, t, activa]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) await uploadFiles(files);
    if (e.target) e.target.value = '';
  };

  // ── R-13 — EL BOTÓN QUE SE QUEDABA MUDO CON EL TOPE LLENO ────────────────────────
  // El censo (expediente 4-B): con los espacios llenos, el `<input type=file>` de la
  // cabecera va `disabled`, así que tocar el botón NO abre el selector. Y el botón
  // TAMPOCO se ve apagado: `Button asChild` pinta un <span>, y la utilidad
  // `disabled:opacity-50` de ui/button.jsx cuelga del pseudo `:disabled`, que en CSS
  // sólo casa con controles de formulario — un <span disabled> no lo enciende nunca.
  // Resultado en el teléfono: un botón con aspecto de sano al que le das y no pasa
  // NADA. (En escritorio el `title` del <label> lo explicaba; en táctil no hay title.)
  //
  // Firma de Carlos (Opción B): con el tope lleno, tocar subir AVISA — y donde el plan
  // tenga menos espacios que uno superior, el aviso además invita a subir de plan.
  //
  // El `{max}` sale SIEMPRE de `planInfo.limits.max_docs`, que viene de
  // GET /auth/plan-info (auth.py:1106) y de ahí de config.PLAN_LIMITS (:91-93). No hay
  // ningún número de tope escrito en el frontend, y esta línea no inaugura el primero.
  //
  // LA LÍNEA DE MEJORA VA POR IDENTIDAD DE PLAN, NO POR NÚMERO: el frontend conoce el
  // tope del usuario, no el de los demás planes, así que comparar números aquí pediría
  // volver a escribir la tabla. La tabla real (config.py:91-93) es guest 3 < free 5 <
  // pro 30: invitado y free tienen un plan por encima con más espacios, pro es el techo
  // (Premium se retiró en 2026-07, config.py:124) y por eso no lleva línea.
  // El invitado no tiene "plan que mejorar": su camino es crear cuenta, que es el
  // registro de la casa ("Crea una cuenta gratis", convert_modal.*).
  //
  // VEHÍCULO: toast de sonner, que es el patrón de avisos de ESTA superficie (formato
  // no soportado, archivo muy grande, lote máximo, duplicado… 20+ sitios en este mismo
  // archivo). Un modal nuevo sería una familia nueva para un aviso de una línea.
  const atDocLimit = !!(planInfo && planInfo.documents_used >= planInfo.limits.max_docs);

  /* ── TANDA 30 (#276a) — LA X DEL AVISO DE LÍMITE DE DOCUMENTOS ──────────────
     El aviso naranja de abajo (`doc-limit-banner`) se puede cerrar, y cerrado
     NO VUELVE en este navegador para este usuario.

     DÓNDE SE RECUERDA: `localStorage`, no Mongo. El aviso es un recordatorio de
     interfaz, no un dato del usuario: no necesita viajar entre dispositivos, no
     entra al sync ni a la bodega, y un campo nuevo en `users` sería firma para
     algo que se resuelve con una llave por navegador.

     POR QUÉ LA LLAVE LLEVA EL user_id: un navegador compartido es normal (la
     compu de la casa, la de la biblioteca). Sin el id, el primero que cerrara
     el aviso se lo callaría a todos los demás. El invitado tiene id propio; el
     `|| 'guest'` es el cinturón para el instante en que el Panel pinta antes de
     que la identidad aterrice.

     EL EFECTO DEPENDE DE LA LLAVE, y eso es lo que hace que cambiar de sesión
     vuelva a mostrar el aviso: al cambiar el usuario cambia la llave, se re-lee
     y el estado se recalcula.

     EL try/catch NO ES DECORACIÓN: en modo privado y con las cookies de sitio
     bloqueadas, `localStorage` no devuelve null — LANZA al acceder. Sin la
     guarda, el Panel entero se caería para esos usuarios. Sin dónde recordar,
     el aviso se comporta como siempre: visible. */
  const claveAvisoLimite = `sb_doc_limit_notice_dismissed:${user?.id || 'guest'}`;
  const [avisoLimiteCerrado, setAvisoLimiteCerrado] = useState(false);
  useEffect(() => {
    let guardado = false;
    try { guardado = localStorage.getItem(claveAvisoLimite) === '1'; } catch { guardado = false; }
    setAvisoLimiteCerrado(guardado);
  }, [claveAvisoLimite]);
  const cerrarAvisoLimite = useCallback(() => {
    // El estado primero: si el guardado truena, el usuario igual ve irse el
    // aviso que acaba de cerrar. Volverá al recargar, que es el comportamiento
    // honesto cuando no hay dónde recordar.
    setAvisoLimiteCerrado(true);
    try { localStorage.setItem(claveAvisoLimite, '1'); } catch { /* sin dónde recordar */ }
  }, [claveAvisoLimite]);
  const notifyDocLimit = useCallback(() => {
    const max = planInfo?.limits?.max_docs;
    const key = planInfo?.is_guest
      ? 'dashboard:doc_limit_toast.body_guest'
      : (planInfo?.plan === 'pro' ? 'dashboard:doc_limit_toast.body' : 'dashboard:doc_limit_toast.body_upgrade');
    toast.error(t(key, { max }));
  }, [planInfo, t]);

  // ── OCR-ALUMNO (§C1/§C2): EL PAQUETE DE FOTOS ─────────────────────────────────────
  const agregarFotos = (imagenes) => {
    const actuales = fotosPanelRef.current;
    if (actuales.length + imagenes.length > MAX_FOTOS) {
      // Si la suma pasa de 100 no se agrega NINGUNA de la tanda nueva.
      setErrorFotos(t('common:errors.doc_too_many_photos'));
      return;
    }
    const nuevas = imagenes.map((file) => {
      const id = `f${++fotoIdRef.current}`;
      let url = null;
      try { url = (typeof URL !== 'undefined' && URL.createObjectURL) ? URL.createObjectURL(file) : null; } catch { url = null; }
      return { id, file, nombre: file.name, url };
    });
    if (actuales.length === 0) {
      setNombreFotos(nombrePrellenado(i18n?.language, t('dashboard:fotos.nombre_prefijo')));
    }
    const siguiente = [...actuales, ...nuevas];
    fotosPanelRef.current = siguiente;
    setFotosPanel(siguiente);
    setOrdenFotos((prev) => [...prev, ...nuevas.map((f) => f.id)]);
    setErrorFotos(null);
  };

  const cerrarPanelFotos = () => {
    for (const f of fotosPanelRef.current) {
      try { if (f.url && URL.revokeObjectURL) URL.revokeObjectURL(f.url); } catch { /* nada */ }
    }
    fotosPanelRef.current = [];
    setFotosPanel([]);
    setOrdenFotos([]);
    setErrorFotos(null);
    setNombreFotos('');
  };

  const subirFotos = async (nombre) => {
    pedirPermisoSiToca();
    const orden = ordenFotos;
    if (orden.length === 0 || !nombre) return;
    setSubiendoFotos(true);
    setErrorFotos(null);
    try {
      const porId = new Map(fotosPanelRef.current.map((f) => [f.id, f]));
      const formData = new FormData();
      for (const id of orden) {
        const foto = porId.get(id);
        if (!foto) continue;
        const listo = await redimensionarFoto(foto.file);
        formData.append('fotos', listo, listo.name || foto.nombre);
      }
      formData.append('nombre', nombre);
      formData.append('turnstile_token', (await getTurnstileToken()) || '');
      formData.append('fp', user?.is_guest ? (await getFingerprint()) : '');
      // estilos-1: el estilo de la pastilla, solo con la perilla encendida (apagada, el cuerpo de hoy).
      const estiloDeLaSubida = cuerpoDeEstilo().estilo;
      if (estiloDeLaSubida) formData.append('estilo', estiloDeLaSubida);
      const res = await api.post('/documents/upload-fotos', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      trackEvent('document_uploaded', { file_name: nombre, status: res.data?.status, credits: 0, fotos: orden.length });
      // post-t7-2-dinero (G1): las fotos se cobran al terminar de leerlas; el aviso ya no dice "1 credit used".
      toast.success(`${t('dashboard:toasts.doc_received_reading')} ${t(llaveDeSalida())}`);
      cerrarPanelFotos();
      fetchDocs();
      document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // post-t7-1 (H1): con 0 creditos el servidor rechaza antes de leer: la ventana "Creditos insuficientes" de hoy
      if (err?.response?.status === 402 && detail && typeof detail === 'object' && detail.error === 'insufficient_credits') {
        logAnalyticsEvent('friction_credits', 'shown');
        setInsufficientCredits(detail);
        return;
      }
      const especifico = (typeof detail === 'string' && detail.trim()) ? detail
        : (detail && typeof detail === 'object' && detail.message) ? detail.message : null;
      setErrorFotos(especifico || t('dashboard:toasts.upload_failed_friendly'));
    } finally {
      setSubiendoFotos(false);
    }
  };

  const uploadFiles = async (files) => {
    pedirPermisoSiToca();
    setJustProcessedIds(new Set());
    // OCR-ALUMNO (§C1): las imágenes abren el panel de fotos SIN subir nada; los demás
    // archivos siguen como hoy. FIRMA #340 (remate de PR #349): el paquete de fotos
    // —recién elegido o pendiente en el panel abierto— cuenta como 1 de los 5, así que
    // con paquete caben 4 documentos; pero el toast dice SIEMPRE el tope (5), nunca 4.
    // (A-7 había quitado la resta y el toast decía el número restado; #340 lo deshace.)
    const imagenes = files.filter(esFoto);
    if (imagenes.length > 0) agregarFotos(imagenes);
    files = files.filter((f) => !esFoto(f));
    if (files.length === 0) return;
    const allowed = ['.pdf', '.docx', '.pptx', '.txt'];
    const hayPaquete = fotosPanelRef.current.length > 0;
    const MAX_BATCH = MAX_BATCH_DOCS - (hayPaquete ? 1 : 0);

    // Filter valid files
    const MAX_UPLOAD_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;
    const validFiles = files.filter(f => {
      const ext = f.name.toLowerCase().slice(f.name.lastIndexOf('.'));
      if (!allowed.includes(ext)) {
        toast.error(t('dashboard:toasts.unsupported_format', { filename: f.name }));
        return false;
      }
      // Client-side size gate — mirrors backend config.MAX_UPLOAD_SIZE_BYTES so oversized files
      // are caught before upload with a localized, recourse-bearing message. Log friction_file_size
      // here (the backend logs it too, but blocked files never reach the backend).
      if (f.size > MAX_UPLOAD_BYTES) {
        toast.error(t('dashboard:toasts.file_too_large', { filename: f.name, max: MAX_UPLOAD_SIZE_MB }));
        logAnalyticsEvent('friction_file_size', 'blocked', { file_size_bytes: f.size, filename: f.name, limit_bytes: MAX_UPLOAD_BYTES });
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    /* post-t7-2-dinero (F1, firma 4): solo suben los que caben en los espacios libres (el paquete de fotos pendiente
       cuenta como uno). Sin `planInfo` no se recorta: decide el servidor, que vuelve a mirar el tope donde nace cada
       documento. El tope de 5 por lote sigue mandando despues. */
    let caben = validFiles;
    if (planInfo && planInfo.limits && Number.isFinite(planInfo.limits.max_docs)) {
      const libres = planInfo.limits.max_docs - (planInfo.documents_used || 0) - (hayPaquete ? 1 : 0);
      if (libres <= 0) {
        notifyDocLimit();
        return;
      }
      if (libres < validFiles.length) {
        toast.info(t('dashboard:toasts.lote_no_cabe', { count: libres }));
        caben = validFiles.slice(0, libres);
      }
    }

    // Cap at MAX_BATCH
    let batch = caben;
    if (batch.length > MAX_BATCH) {
      toast.info(t('dashboard:toasts.max_batch', { max: MAX_BATCH_DOCS }));
      batch = batch.slice(0, MAX_BATCH);
    }

    // Init upload queue
    const queue = batch.map((f, i) => ({ id: i, file: f, name: f.name, status: 'queued', progress: 0, error: null }));
    setUploadQueue(queue);
    setUploading(true);

    const turnstileToken = await getTurnstileToken();
    const fp = user?.is_guest ? (await getFingerprint()) : '';

    // Mark all as uploading up front so the per-item progress bars render immediately
    setUploadQueue(prev => prev.map(item => ({ ...item, status: 'uploading' })));

    // Fire all uploads in parallel via Promise.all + per-promise .catch — never rejects.
    // Per design Decision #22: bulk upload uses Promise.allSettled-style gather so we can
    // collect ALL responses (including preview_split for large docs) before opening any modal.
    // INFRA-3 (#381): cada archivo suelto viaja por REBANADAS de 8 MiB con SHA-256
    // (`iniciar` -> pedazos -> `cerrar`), tambien los chicos (una sola rebanada). Misma
    // secuencia y paralelismo entre archivos que antes; el `cerrar` es SINCRONO y
    // devuelve exactamente la respuesta de hoy de `POST /documents/upload`, asi que la
    // tabla de abajo no cambia. El porcentaje es bytes confirmados + en vuelo, monotono;
    // llega a 100 cuando el servidor confirma la ultima rebanada y se queda ahi durante
    // el cierre. Los errores de `iniciar`/`cerrar` llevan los codigos de hoy; un fallo
    // definitivo del transporte llega sin `response` y cae en el mismo manejo generico.
    // `turnstile_token` y `fp` viajan en `iniciar`, como viajaban en la subida.
    const settled = await Promise.all(
      queue.map((item, idx) => subirPorRebanadas({
        file: item.file,
        urlBase: '/documents/upload',
        prefijo: '[alumno]',
        iniciarBody: { turnstile_token: idx === 0 ? (turnstileToken || '') : '', fp, ...cuerpoDeEstilo() },
        alAvanzar: (pct) => {
          setUploadQueue(prev => prev.map((it, i) => i === idx ? { ...it, progress: pct } : it));
        },
      })
        .then(res => ({ idx, success: true, data: res.data, item }))
        .catch(err => ({ idx, success: false, error: err, item })))
    );

    // Collect preview_split responses + first-occurring error class flags
    const newSplitPreviews = [];
    let firstInsufficientCredits = null;
    let firstDuplicateArchived = null;
    let triggerGuestConvert = false;
    // T7-1B (#476): los renglones de la cola cuyo archivo llegó bien. Salen de la cola
    // al terminar este `for` (abajo), no en medio: las demás escrituras del `for`
    // ubican su renglón por POSICIÓN, y sacar uno a media vuelta las correría.
    const recibidos = new Set();

    for (const result of settled) {
      if (result.success) {
        const data = result.data || {};
        if (data.action === 'preview_split') {
          // Large auto-split path — no credit deducted, modal will handle finalize.
          // Status 'split' (hidden from the upload panels): nothing actually uploaded to
          // the library, so there is NO upload story to show — the modal owns it. This
          // kills the phantom "Uploading…✓" frame that used to flash on modal close.
          //
          // ── REMATE APP-1, R1 (#399) — LA VENTANA SE ABRE UNA VEZ, CON TARJETA ─────
          // AQUÍ VIVÍA LA GUARDA `if (!data.documento_base_id)`, que B2 puso por una
          // instrucción mal escrita («la ventana no se abre sola, ni al terminar la
          // subida»). La firma #399 dice otra cosa: «el texto grande deja tarjeta COMO
          // EL ESCANEADO», y el escaneado abre su ventana UNA vez —en el sondeo en que
          // pasa a «por confirmar»— con la tarjeta detrás. Para el texto ese momento es
          // LA RESPUESTA DE LA SUBIDA: el texto se extrae en el acto, así que cuando
          // esta línea corre el documento ya está «Listo para procesar» en el servidor.
          //
          // `documento_base_id` viaja en el sobre, así que la ventana que se abre es la
          // del PLACEHOLDER: su pie dice «Descartar este documento» (borra de verdad) y
          // su X sólo cierra, dejando la tarjeta. Es la misma ventana que abre el botón
          // «Procesar», ni un campo de diferencia.
          //
          // Y NO SE REABRE SOLA DESPUÉS, por las tres puertas y sin tocar ninguna:
          //   · el sondeo del Panel sigue acotado a `lectura === 'ojos'` (:1112-1118);
          //   · `GET /documents/temp-uploads/mine` salta las filas con
          //     `placeholder_doc_id` (documents.py:2288-2292);
          //   · `writeAutoSplitQueue` NO persiste un sobre con `documento_base_id`
          //     (lib/guestSplitFunnel.js:36), así que el restaurador de la carga
          //     siguiente no tiene nada que revalidar.
          // Recargar, cambiar de aparato o volver al Panel: cero ventanas solas.
          //
          // Sin la marca —el invitado, que no estrena placeholder— esto es byte a byte
          // lo que pasaba antes de B2: el mismo `push` de siempre.
          newSplitPreviews.push(data);
          setUploadQueue(prev => prev.map((it, i) => i === result.idx ? { ...it, status: 'split', progress: 100 } : it));
          trackEvent('document_preview_split', { file_name: result.item.name, total_chunks: data.total_chunks, total_credits: data.total_credits });
        } else {
          const status = data.status;
          const creditsUsed = data.credits_consumed || 1;
          // T7-1B (#476): LA TARJETA PASAJERA, FUERA. Aquí el renglón pasaba a 'done' y
          // se quedaba dos segundos arriba de la lista, con su giro y "Procesamiento
          // iniciado", encima de la tarjeta REAL del documento (que ya trae la recarga de
          // abajo). Hoy el renglón no se marca: sale de la cola, así que un renglón 'done'
          // no existe y no se pinta ni un cuadro. El aviso verde de aquí abajo no cambia.
          recibidos.add(result.item.id);
          // 'ready' = instant (cache hit) → "processed". 'processing' = generating in the
          // background (~90s) → "received, processing…" so we don't claim it's done before it is.
          // The completion ("…is ready!") toast fires later via <NotificationListener /> on the
          // processing→ready transition (see polling effect comment + NotificationListener.js).
          const toastKey = status === 'ready' ? 'dashboard:toasts.doc_processed' : 'dashboard:toasts.doc_received';
          // post-t7-2b (D5, #608): el escaneado que todavia se esta leyendo no se ha cobrado (`credits_consumed: 0`,
          // el `|| 1` de arriba lo volvia "1 credito usado"). Su aviso es el ratificado, sin la coletilla de creditos.
          // Los demas casos, el de siempre. Fotos y YouTube no se tocan aqui (la corrida de dinero).
          if (data.credits_consumed === 0 && data.lectura === 'ojos' && data.lectura_estado === 'leyendo') {
            // Sigue procesando: lleva la misma frase de salida que el aviso de siempre (remates-1-3-0b, P1).
            toast.success(`${t('dashboard:toasts.doc_received_reading')} ${t(llaveDeSalida())}`);
          } else {
            // remates-1-3-0b (P1): solo el que sigue procesando lleva la frase; el instantaneo ya termino.
            toast.success(status === 'ready' ? t(toastKey, { count: creditsUsed }) : `${t(toastKey, { count: creditsUsed })} ${t(llaveDeSalida())}`);
          }
          // post-t7-2-dinero (G3): la analitica lleva el cobro REAL (0 cuando todavia no se cobra), no el `|| 1`.
          trackEvent('document_uploaded', { file_name: result.item.name, status, credits: Number(data.credits_consumed ?? 0) });
        }
      } else {
        const err = result.error;
        const detail = err.response?.data?.detail;
        const status = err.response?.status;
        const hasSpecificDetail = typeof detail === 'string' && detail.trim().length > 0;
        const specificErrorMsg = hasSpecificDetail
          ? detail
          : (typeof detail === 'object' && detail?.message ? detail.message : null);
        const friendlyMsg = t('dashboard:toasts.upload_failed_friendly');
        const errorMsg = specificErrorMsg || friendlyMsg;
        setUploadQueue(prev => prev.map((it, i) => i === result.idx ? { ...it, status: 'error', error: errorMsg } : it));

        if (!err.response) {
          logAnalyticsEvent('friction_upload_error', 'shown', { error_type: 'network', filename: result.item.name });
        } else if (status >= 500) {
          logAnalyticsEvent('friction_upload_error', 'shown', { error_type: '500_server_error', filename: result.item.name, status_code: status });
        }

        // (The 403 [CHAR_LIMIT] guest branch is gone — guests >50K now get a 200
        // preview_split with guest_signup_required, same as auth users. Deploy 116's
        // Char Limit Modal was disconnected with it.)

        if (status === 402 && typeof detail === 'object' && detail.error === 'insufficient_credits') {
          if (!firstInsufficientCredits) {
            logAnalyticsEvent('friction_credits', 'shown');
            firstInsufficientCredits = detail;
          }
          continue;
        }

        // Text too big to stage for auto-split (Mongo 16MB doc limit) — clean message, not generic.
        if (status === 422 && typeof detail === 'object' && detail.error === 'document_too_large') {
          toast.error(t('dashboard:toasts.document_too_large'));
          continue;
        }

        // Password-protected PDF — actionable, localized message (backend sends a stable code).
        if (status === 400 && typeof detail === 'object' && detail?.code === 'pdf_password_protected') {
          toast.error(t('dashboard:toasts.pdf_password_protected'));
          continue;
        }

        // Anti-duplicate: the user already has this exact document (active) — simple toast, no
        // credit charged, no card created. Clear the queue item so it doesn't show as a red error.
        if (status === 409 && typeof detail === 'object' && detail?.error === 'duplicate_document') {
          setUploadQueue(prev => prev.map((it, i) => i === result.idx ? { ...it, status: 'duplicate' } : it));
          toast.info(t('dashboard:toasts.duplicate_document'));
          continue;
        }

        // Anti-duplicate: an ARCHIVED copy exists (recover via upgrade, or delete + re-upload).
        // Decision with consequences → modal (shown once after the loop).
        if (status === 409 && typeof detail === 'object' && detail?.error === 'duplicate_archived') {
          setUploadQueue(prev => prev.map((it, i) => i === result.idx ? { ...it, status: 'duplicate' } : it));
          if (!firstDuplicateArchived) firstDuplicateArchived = detail;
          continue;
        }

        if (user?.is_guest && (status === 403 || status === 429)) {
          const detailStr = typeof detail === 'string' ? detail : errorMsg;
          if (detailStr.includes('free previews')) {
            triggerGuestConvert = true;
            continue;
          }
        }

        if (specificErrorMsg) {
          toast.error(t('dashboard:toasts.upload_error_generic', { filename: result.item.name, error: specificErrorMsg }));
        } else {
          toast.error(friendlyMsg);
        }
      }
    }

    // Push any preview_split previews onto the modal queue (state + sessionStorage)
    if (newSplitPreviews.length > 0) {
      setAutoSplitQueue(prev => {
        const merged = [...prev, ...newSplitPreviews];
        writeAutoSplitQueue(merged);
        return merged;
      });
    }

    // Show first occurrence of each error class (sequential bulk uploads would only ever hit one
    // at a time due to `break`; parallel uploads may hit multiple, but we still only show one modal).
    if (firstInsufficientCredits) setInsufficientCredits(firstInsufficientCredits);
    if (firstDuplicateArchived) setDuplicateArchived(firstDuplicateArchived);
    if (triggerGuestConvert) setConvertModal('lifetime');

    // T7-1B (#476): los que llegaron bien salen de la cola en este mismo paso (antes de
    // que se pinte nada), por el `id` del renglón. Lo que queda a la vista es lo que sí
    // tiene algo que contar: el error en rojo. La tarjeta real la trae `fetchDocs()`,
    // aquí mismo y en el acto, sin esperar al sondeo.
    if (recibidos.size > 0) {
      setUploadQueue(prev => prev.filter(it => !recibidos.has(it.id)));
    }

    // Refresh doc list and clear queue after short delay
    fetchDocs();
    // Re-arm App-level NotificationListener polling (in case it had stopped).
    document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
    // Upload UI may only exist while a REAL upload story exists. preview_split files
    // never enter the library — if the whole batch went to the split modal, drop the
    // upload state NOW (the 2s linger under the modal was the phantom-overlay bug).
    const onlySplits = settled.length > 0 && settled.every(r => r.success && (r.data || {}).action === 'preview_split');
    if (onlySplits) {
      setUploadQueue([]);
      setUploading(false);
    } else {
      setTimeout(() => {
        setUploadQueue([]);
        setUploading(false);
      }, 2000);
    }
  };

  // tutor-1 (C): el archivo que el Tutor no pudo leer de un jalon ("Subirlo al Panel") llega en el estado de la
  // navegacion ya elegido; el Panel lo sube UNA vez por el mismo camino que su selector de archivos.
  const archivoDelTutorRef = useRef(null);
  useEffect(() => {
    const estado = activa && typeof window !== 'undefined' ? window.history.state : null;
    const f = estado?.usr?.archivoDelTutor;
    if (!f || archivoDelTutorRef.current === f) return;
    archivoDelTutorRef.current = f;
    try { window.history.replaceState({ ...estado, usr: { ...estado.usr, archivoDelTutor: undefined } }, ''); } catch (e) { /* nada */ }
    uploadFiles([f]);
  });

  // Map an ISO language code (e.g. "en", "pt-BR") to a readable name in the UI language.
  const langDisplayName = (code) => {
    try {
      const dn = new Intl.DisplayNames([i18n?.language || 'en'], { type: 'language' });
      return dn.of(code) || code;
    } catch {
      return code;
    }
  };

  // ── YouTube import: paste a link → POST /documents/from-youtube → reuse the upload flow ──
  const importFromYoutube = async (rawUrl, lang) => {
    pedirPermisoSiToca();
    const url = (rawUrl || '').trim();
    if (!url || youtubeLoading) return;
    setYoutubeLoading(true);
    setErrorYoutube(null);
    try {
      // Fresh single-use human-filter token + device fingerprint on EVERY call (initial AND
      // the language-pick retry), mirroring the file-upload path. fp only for guests.
      const turnstileToken = await getTurnstileToken();
      const fp = user?.is_guest ? (await getFingerprint()) : '';
      const res = await api.post('/documents/from-youtube', { url, ...(lang ? { lang } : {}), fp, turnstile_token: turnstileToken || '', ...cuerpoDeEstilo() });
      const data = res.data || {};

      if (data.action === 'choose_language') {
        setLangSearch('');
        setLangModal({ url, langs: data.available_langs || [] });
        return; // wait for the user to pick the original language, then re-call with lang
      }

      if (data.action === 'preview_split') {
        // Large transcript (>50K) → same AutoSplitModal as a large PDF (shared temp_uploads).
        setAutoSplitQueue(prev => {
          const merged = [...prev, data];
          writeAutoSplitQueue(merged);
          return merged;
        });
        trackEvent('document_preview_split', { source: 'youtube', total_chunks: data.total_chunks, total_credits: data.total_credits });
      } else {
        // processing / ready — identical toast + flow as a normal upload.
        const status = data.status;
        // post-t7-2-dinero (G2): el numero real del servidor (`|| 1` mentia con 0), en el aviso y en la analitica.
        const creditsUsed = Number(data.credits_consumed ?? 0);
        toast.success(t('dashboard:toasts.doc_processed', { count: creditsUsed }));
        trackEvent('document_uploaded', { source: 'youtube', status, credits: creditsUsed });
      }

      setYoutubeUrl('');
      setLangModal(null);
      fetchDocs();
      document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const code = (detail && typeof detail === 'object') ? detail.error : null;
      // series-7 (#493): el codigo hermano del sobre (`error_code`, junto a `detail`). El
      // servidor nombra con el cada fallo de este camino; sin el (un servidor viejo) todo
      // sigue decidiendose como hoy, por el estado.
      const errorCode = err.response?.data?.error_code || null;

      // Out of credits → reuse the shared insufficient-credits modal (guest branch shows
      // "Create free account" → convertModal('credits'); account branch shows buy pack /
      // upgrade). Same behavior as the file-upload path.
      if (status === 402 && code === 'insufficient_credits') {
        setLangModal(null);
        setInsufficientCredits(detail);
        return;
      }

      // Lifetime-cap 403 ("free previews") → YouTube signup modal. Any OTHER 403 (e.g. the
      // human-filter/turnstile gate) falls through to the generic toast below — not the modal.
      // series-7 (#493, L6 5.3): el interceptor de `lib/api.js` ya TRADUJO el `detail` de
      // este 403, asi que en espanol no dice "free previews" y la ventana no se abria: se
      // reconoce tambien por su codigo. El texto se queda para el servidor viejo.
      if (status === 403 && user?.is_guest) {
        const detailStr = typeof detail === 'string' ? detail : (detail?.message || '');
        if (detailStr.includes('free previews') || errorCode === 'doc_free_previews_used') {
          setLangModal(null);
          setConvertModal('youtube'); // YouTube-specific signup copy (reuses the convert modal)
          return;
        }
      }
      // Anti-duplicate (same as the file-upload path): active dup → toast; archived dup → modal.
      if (status === 409 && code === 'duplicate_document') {
        setLangModal(null);
        toast.info(t('dashboard:toasts.duplicate_document'));
        return;
      }
      if (status === 409 && code === 'duplicate_archived') {
        setLangModal(null);
        setDuplicateArchived(detail);
        return;
      }
      // series-7 (#493, diseno 4): EL AVISO SALE DEL CODIGO, NO DEL ESTADO HTTP. Hasta
      // hoy cualquier 400 de este camino era "link invalido" (tambien un video demasiado
      // corto) y el tope diario del sistema culpaba al servicio de transcripcion. Los tres
      // codigos del camino tienen su aviso; cualquier otro con par en `common:errors` se
      // pinta con ese par (si la frase pide numeros que el sobre no trae, no se pinta a
      // medias: sigue lo de hoy). Sin codigo, todo como hoy.
      if (errorCode && AVISO_DE_YOUTUBE[errorCode]) {
        setErrorYoutube(t(AVISO_DE_YOUTUBE[errorCode]));
        return;
      }
      if (errorCode && typeof i18n?.exists === 'function' && i18n.exists(`common:errors.${errorCode}`)) {
        const texto = t(`common:errors.${errorCode}`, (detail && typeof detail === 'object') ? detail : undefined);
        if (typeof texto === 'string' && texto && !texto.includes('{{')) {
          toast.error(texto);
          return;
        }
      }
      if (status === 400 || code === 'invalid_url') {
        setErrorYoutube(t('dashboard:youtube.invalid_url'));
      } else if (code === 'document_too_large') {
        toast.error(t('dashboard:toasts.document_too_large'));
      } else if (status === 422 || code === 'no_transcript_available') {
        setErrorYoutube(t('dashboard:youtube.no_transcript'));
      } else if (status === 503 || code === 'service_unavailable') {
        setErrorYoutube(t('dashboard:youtube.service_unavailable'));
      } else {
        toast.error(t('dashboard:toasts.upload_failed_friendly'));
      }
    } finally {
      setYoutubeLoading(false);
    }
  };

  // ── Auto-split modal: shift the head off the queue (advances to next pending modal if any) ──
  const dequeueAutoSplit = useCallback((tempUploadId) => {
    setAutoSplitQueue(prev => {
      const next = prev.filter(p => p.temp_upload_id !== tempUploadId);
      writeAutoSplitQueue(next);
      return next;
    });
  }, []);

  // ── Auto-split: re-fetch a temp_upload's preview (fresh affordability) and replace it
  //    in the queue. Used whenever balances changed (409) or the user freed a slot. ──
  // Remate R3: espejo de la cola en una ref, para que `refetchAutoSplitHead` pueda
  // preguntar por el sobre SIN depender de la cola (si dependiera, el efecto que lo
  // llama al cambiar la cabeza se dispararía en bucle: refetch → reemplaza → refetch).
  const autoSplitQueueRef = useRef([]);
  useEffect(() => { autoSplitQueueRef.current = autoSplitQueue; }, [autoSplitQueue]);

  const refetchAutoSplitHead = useCallback(async (tempUploadId, documentoBaseIdExplicito) => {
    // ── #367 / 4.2 — UN PLACEHOLDER TAMBIÉN REFRESCA SUS NÚMEROS ──────────────────
    // Hasta hoy este camino salía de inmediato: el sobre de un escaneado no vive en
    // `temp_uploads` (el remate borra esa copia porque el modal puede llegar días
    // después), así que no había a quién preguntarle. El resultado era una ventana con
    // los créditos y los cupos del día de la lectura — números viejos sobre los que el
    // alumno decidía. Ahora hay endpoint propio: mismo sobre, `affordability` de ahora.
    //
    // `documentoBaseIdExplicito` existe porque `autoSplitQueueRef` se actualiza en un
    // efecto: quien acaba de meter un sobre al frente todavía no lo ve en la ref, y sin
    // este argumento tendría que esperar un ciclo para poder preguntar.
    const enCola = autoSplitQueueRef.current.find((p) => p.temp_upload_id === tempUploadId);
    const documentoBaseId = documentoBaseIdExplicito || enCola?.documento_base_id
      || (String(tempUploadId || '').startsWith('ph_') ? String(tempUploadId).slice(3) : null);
    if (documentoBaseId) {
      try {
        const res = await api.get(`/documents/${documentoBaseId}/preview-split`);
        const fresh = res.data;
        setAutoSplitQueue(prev => prev.map(p => (p.temp_upload_id === tempUploadId
          // Fusión, no reemplazo: `documento_base_id`, `temp_upload_id` y
          // `guest_signup_required` los pone el frontend y el servidor no los conoce.
          // Reemplazar en bloque le quitaría a la cabeza justo lo que la hace escaneada.
          ? { ...p, ...fresh, temp_upload_id: p.temp_upload_id,
              documento_base_id: p.documento_base_id, guest_signup_required: p.guest_signup_required }
          : p)));
      } catch { /* 404/red: se pinta el sobre que había, que es lo que pasaba siempre */ }
      setAutoSplitHeadStale(false);
      return null;
    }
    try {
      const res = await api.get(`/documents/temp-upload/${tempUploadId}`);
      const fresh = res.data;
      setAutoSplitQueue(prev => {
        const next = prev.map(p => (p.temp_upload_id === tempUploadId ? fresh : p));
        writeAutoSplitQueue(next);
        return next;
      });
      setAutoSplitHeadStale(false);   // fresh affordability applied → safe to render numbers
      return fresh;
    } catch (e) {
      if (e.response?.status === 404) {
        toast.error(t('dashboard:auto_split.session_expired_toast'));
        dequeueAutoSplit(tempUploadId);
        // Keep stale=true: the NEXT head (if any) must also refetch before showing. The
        // head-change effect refetches it; the empty-queue branch clears the gate.
      } else {
        // Network/5xx: don't trap the modal in a spinner — fall back to the cached numbers.
        setAutoSplitHeadStale(false);
      }
      return null;
    }
  }, [dequeueAutoSplit, t]);

  // ── Auto-split stale fix: dequeueAutoSplit only filters the queue, so the NEXT doc would
  //    render its cached (stale) affordability — not reflecting what the prior doc just
  //    consumed (credits/slots/pending). Re-fetch the head's preview every time the modal
  //    shows a (new) doc: on advance, open and reopen. Keyed on the head id, so the refetch
  //    replacing its data (same id) doesn't re-trigger this effect. ──
  const autoSplitHeadId = autoSplitQueue[0]?.temp_upload_id;
  // post-t7-1 (H3): el aviso del documento entero no tiene sobre en el servidor que refrescar (sus numeros vienen
  // del resumen de pendientes): no se pide nada por el
  const cabezaEsAvisoEntero = !!autoSplitQueue[0]?.aviso_entero;
  useEffect(() => {
    if (autoSplitHeadId && cabezaEsAvisoEntero) setAutoSplitHeadStale(false);
    else if (autoSplitHeadId) refetchAutoSplitHead(autoSplitHeadId);
    else setAutoSplitHeadStale(false);   // queue empty → no modal → clear the gate
  }, [autoSplitHeadId, cabezaEsAvisoEntero, refetchAutoSplitHead]);

  // ── #367 — EL BOTÓN «PROCESAR» DE LA TARJETA DE UN ESCANEADO ──────────────────
  // El efecto automático abre la ventana UNA sola vez, en la transición a «por
  // confirmar». Cerrarla dejaba al alumno sin ninguna forma de volver a ella salvo
  // recargar. Éste es el camino de vuelta —y desde la tanda 2, EL ÚNICO—, y arma el
  // sobre con EXACTAMENTE la misma forma que el efecto (misma clave sintética, mismo
  // `documento_base_id`, mismo recálculo de `guest_signup_required` con el usuario
  // de ahora) para que la ventana no distinga de dónde vino.
  //
  // Al FRENTE, no al final: el alumno acaba de pedir ESTE documento. Si había otro
  // abierto, ése no se pierde — se queda en la cola, detrás.
  //
  // TANDA 2: NO toca `porConfirmarAbiertosRef` (D-3 de la tanda 1). Borrar el id de
  // ahí devolvía al documento a la casilla de «nunca mostrado», y el siguiente sondeo
  // —≤ 3 s después— reabría la ventana que el alumno acababa de cerrar con la X. La
  // apertura a mano no es una transición: no le devuelve al efecto ningún derecho.
  const abrirTroceoDePlaceholder = useCallback((doc) => {
    const sobreGuardado = doc?.preview_split;
    if (!sobreGuardado) return;
    const tempId = sobreGuardado.temp_upload_id || `ph_${doc.id}`;
    setAutoSplitQueue((prev) => ([
      { ...sobreGuardado, temp_upload_id: tempId, documento_base_id: doc.id, guest_signup_required: esInvitado },
      ...prev.filter((p) => p.temp_upload_id !== tempId),
    ]));
    // Los números que se van a enseñar son los de AHORA, no los del día de la lectura.
    setAutoSplitHeadStale(true);
    refetchAutoSplitHead(tempId, doc.id);
  }, [esInvitado, refetchAutoSplitHead]);

  // Success toast with a link to the (future) pending section.
  const showSavedPendingToast = useCallback(() => {
    toast.success(t('dashboard:auto_split.saved_pending_toast'), {
      action: { label: t('dashboard:auto_split.saved_pending_link'), onClick: () => navigate(PENDING_ROUTE) },
    });
  }, [navigate, t]);

  // ── Auto-split modal: Procesar — finalize-split (optional process_count for partial) ──
  const handleAutoSplitProcess = useCallback(async (processCount) => {
    pedirPermisoSiToca();
    const head = autoSplitQueue[0];
    if (!head) return;
    setAutoSplitConfirmLoading(true);
    try {
      // ── REMATE APP-1, R3 — UN SOLO CAMINO, EL DEL DOCUMENTO ───────────────────
      // Hasta hoy el cuerpo llevaba SIEMPRE `temp_upload_id` y le añadía
      // `documento_base_id` cuando lo había, así que el servidor recibía los dos ids
      // a la vez y decidía él. En el escaneado eso no se notaba —su fila temporal está
      // borrada— pero el texto grande de B2 la conserva 60 minutos, así que el sobre
      // llegaba con los dos VIVOS y la misma ventana tenía dos maneras de finalizar.
      //
      // Con placeholder detrás, el documento es la única fuente de verdad: tiene el
      // texto entero y su candado (`por_confirmar` → `confirmando`) es más fuerte que
      // el flag del temporal. Mandar sólo su id cierra la ambigüedad en el origen y
      // hace que el día 6 —con el temporal ya muerto— sea el mismo camino que el día 1.
      // El redirect de `_finalize_split_impl` (documents.py:2498-2512) se queda donde
      // está: lo necesita el build viejo de la app, que sigue finalizando por el temp.
      const body = head.documento_base_id
        ? { documento_base_id: head.documento_base_id }
        : { temp_upload_id: head.temp_upload_id };
      if (typeof processCount === 'number') body.process_count = processCount;
      // series-7 (#492): si la ventana dice que no caben todas en Pendientes, se pide
      // guardar lo que quepa (el servidor procesa las de ahora, guarda las que quepan y
      // las demas no se crean: volver a subir el archivo las ofrece). Sin `series7` o sin
      // desborde el cuerpo es el de hoy. En el carril de ojos no hay desborde
      // (`sobreParaLaVentana`, arriba): la ventana es la de hoy y el cuerpo tambien.
      const sobreDeLaVentana = sobreParaLaVentana(head, docs);
      if (desbordaPendientes(sobreDeLaVentana) && sobreDeLaVentana.sin_desborde !== true) {
        body.guardar_lo_que_quepa = true;
      }
      // estilos-1: el estilo de la pastilla, solo con la perilla encendida (apagada, el cuerpo de hoy).
      Object.assign(body, cuerpoDeEstilo());
      const res = await api.post('/documents/finalize-split', body);
      const processed = res.data?.processed_count ?? (res.data?.documents || []).length;
      const pending = res.data?.pending_count ?? 0;
      if (pending > 0) {
        toast.success(`${t('dashboard:auto_split.partial_done_toast', { k: processed, count: pending })} ${t(llaveDeSalida())}`);
      } else {
        toast.success(`${t('dashboard:auto_split.processing_toast', { count: processed, n: processed })} ${t(llaveDeSalida())}`);
      }
      trackEvent('auto_split_finalized', {
        parent_doc_id: res.data?.parent_doc_id, processed_count: processed, pending_count: pending,
      });
      // This just consumed balances; if a next popup is queued, it must NOT flash its
      // pre-consumption numbers — gate it on a fresh refetch (driven by the head-change effect).
      if (autoSplitQueue.length > 1) setAutoSplitHeadStale(true);
      dequeueAutoSplit(head.temp_upload_id);
      fetchDocs();
      notifyPendingChanged();
      document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (head.documento_base_id && (status === 404 || status === 409)) {
        // Remate R3.8: otra pestaña ya confirmó o borró ese documento. El mismo aviso
        // de siempre y la lista dice la verdad.
        toast.error(insufficientCreditsCopy(detail, t) || t('dashboard:auto_split.finalize_failed_toast'));
        dequeueAutoSplit(head.temp_upload_id);
        await fetchDocs();
      } else if (status === 410) {
        toast.error(t('dashboard:auto_split.session_expired_toast'));
        dequeueAutoSplit(head.temp_upload_id);
      } else if (status === 409 && detail?.error === 'duplicate_pending') {
        // X1: the rest would duplicate an existing pending group. Re-fetch flips the
        // modal into its simplified duplicate variant, que es la que anuncia el hecho.
        //
        // COS-G B2 — AQUÍ VIVÍA UN `toast.info(auto_split.duplicate_notice)` CON LA
        // MISMA CADENA QUE LA TARJETA. Dos superficies diciendo lo mismo, a la vez y
        // palabra por palabra: la tarjeta del modal (`auto-split-duplicate-notice`,
        // AutoSplitModal.js:210-218) y un toast encima. Se queda la TARJETA porque es
        // la más informativa de las dos: no se desvanece, viaja con el link "Ver en
        // Pendiente de procesar" y con el estado del grupo existente
        // (`auto-split-dup-group-status`) — el toast era la misma frase sin nada de eso.
        await refetchAutoSplitHead(head.temp_upload_id);
      } else if (status === 409 || status === 402 || status === 403) {
        // Balances/tope changed since preview — never process with stale numbers. Re-fetch
        // the preview (fresh affordability) and re-render with "your balances changed".
        //
        // ── TANDA L / L4.2 (censo #20) — CUANDO EL RECHAZO ES POR SALDO, SE DICEN LOS
        //    NÚMEROS ──
        // "Tus saldos cambiaron. Actualizamos los números." es honesto pero mudo: el 402
        // de finalize-split trae `credits_required` y `credits_available` en la mano
        // (documents.py:1246-1251) y este aviso los tiraba. Ahora el rechazo por saldo
        // habla con la MISMA frase numerada que el Reintentar. Los otros dos códigos —409
        // (tope de pendientes) y 403— no traen números y conservan su copy de siempre,
        // igual que un 402 de un backend viejo que llegara sin el sobre completo.
        await fetchDocs();
        await refetchAutoSplitHead(head.temp_upload_id);
        toast.error(insufficientCreditsCopy(detail, t) || t('dashboard:auto_split.balances_changed'));
      } else {
        toast.error(t('dashboard:auto_split.finalize_failed_toast'));
      }
    } finally {
      setAutoSplitConfirmLoading(false);
    }
  }, [autoSplitQueue, dequeueAutoSplit, fetchDocs, refetchAutoSplitHead, t, docs]);

  // ── Auto-split modal: explicit "Guardar en pendiente" (k=0 primary button) ──
  const handleAutoSplitSavePending = useCallback(async () => {
    const head = autoSplitQueue[0];
    if (!head) return;
    if (head.documento_base_id) {
      // Remate R3: para un placeholder, «guardar en pendiente» es finalizar con cero
      // partes procesadas (la primera pendiente cae sobre el placeholder).
      return handleAutoSplitProcess(0);
    }
    // series-7 (#492): con desborde, `save-pending` no sabe guardar a medias (rechaza con
    // 409 si no caben todas); guardar es finalizar con cero ahora y el booleano, que
    // `handleAutoSplitProcess` agrega. Sin desborde, lo de hoy.
    if (desbordaPendientes(head)) return handleAutoSplitProcess(0);
    try {
      await api.post(`/documents/temp-upload/${head.temp_upload_id}/save-pending`);
      showSavedPendingToast();
      // Consumed a pending slot; gate the next popup on a fresh refetch (no stale flash).
      if (autoSplitQueue.length > 1) setAutoSplitHeadStale(true);
      dequeueAutoSplit(head.temp_upload_id);
      fetchDocs();
      notifyPendingChanged();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409 && detail?.error === 'duplicate_pending') {
        // COS-G B2 — sin toast: la tarjeta del modal (variante duplicado) lo dice, y
        // decirlo dos veces con la misma cadena era el aviso duplicado. Ver el bloque
        // gemelo de `handleAutoSplitProcess`.
        await refetchAutoSplitHead(head.temp_upload_id);
      } else if (status === 409) {
        // L4.2: el gemelo de arriba. Aquí sólo llega el 409 del tope (guardar en pendiente
        // no cobra créditos), así que no hay números que decir — pero la clasificación se
        // escribe igual en los dos para que no vuelvan a divergir.
        await fetchDocs();
        await refetchAutoSplitHead(head.temp_upload_id);
        toast.error(insufficientCreditsCopy(detail, t) || t('dashboard:auto_split.balances_changed'));
      } else if (status === 410) {
        toast.error(t('dashboard:auto_split.session_expired_toast'));
        dequeueAutoSplit(head.temp_upload_id);
      } else {
        toast.error(t('dashboard:auto_split.finalize_failed_toast'));
      }
    }
  }, [autoSplitQueue, dequeueAutoSplit, fetchDocs, refetchAutoSplitHead, showSavedPendingToast, t, handleAutoSplitProcess]);

  // ── APP-1 B3 (#400) — EL CIERRE VIAJA EN LA FILA DEL SERVIDOR ───────────────────
  // «Ya cerré ésta» dejaba de ser una nota en el `localStorage` de ESTE navegador
  // (`sb_auto_split_dismissed`, que se retira en #401) para ser un bool en la fila:
  // así vale en todos los aparatos y entre la app y la web del mismo teléfono, que
  // son dos orígenes distintos y por eso nunca compartieron esa memoria.
  //
  // Se marcan LAS DOS puertas por las que una ventana vuelve sola: la fila temporal
  // (que `GET /documents/temp-uploads/mine` reabre en cada carga del Panel) y, si el
  // sobre venía de una tarjeta, su documento (que el sondeo reabre en la transición a
  // «por confirmar»). Un cierre que sólo calla una de las dos no cierra nada.
  //
  // Se manda y se olvida: la ruta es idempotente y muda —ni borra, ni cobra, ni
  // devuelve texto— así que un fallo de red no puede producir un letrero que el
  // alumno no pidió. Lo peor que pasa entonces es lo de hoy: que la ventana vuelva.
  const marcarVentanaCerrada = useCallback((head) => {
    if (!head) return;
    const cuerpo = {};
    // La clave sintética `ph_<id>` la inventa el propio Panel para las tarjetas: esa
    // no existe en el servidor y mandarla sólo sería ruido.
    if (head.temp_upload_id && !String(head.temp_upload_id).startsWith('ph_')) {
      cuerpo.temp_upload_id = head.temp_upload_id;
    }
    if (head.documento_base_id) cuerpo.documento_base_id = head.documento_base_id;
    if (!cuerpo.temp_upload_id && !cuerpo.documento_base_id) return;
    api.post('/documents/ventana-cerrada', cuerpo).catch(() => { /* vuelve a pasar lo de hoy */ });
  }, []);

  // ── Auto-split modal: close (X / Cancel). NEVER saves — closing = nothing happened.
  //    Saving only ever goes through the explicit buttons (process / save-pending). ──
  const handleAutoSplitClose = useCallback(() => {
    const head = autoSplitQueue[0];
    if (!head) return;
    marcarVentanaCerrada(head);
    if (head.documento_base_id) {
      // ── #368 — CERRAR YA NO BORRA ────────────────────────────────────────────
      // Hasta hoy la X y «Cancelar» caían las dos aquí y llamaban a
      // `borrarPlaceholderPorConfirmar`: la salida más fácil de la ventana era la
      // destructiva, sin confirmación y sin aviso, y se llevaba el texto ya leído
      // con los ojos (que el alumno tendría que volver a pagar). (Esc no cierra
      // esta ventana: no hay listener de teclado, y eso no cambia aquí.)
      //
      // Ahora cerrar es cerrar. El documento sigue `por_confirmar` —a salvo hasta
      // los 7 días— y su tarjeta lo dice con el chip nuevo y el botón. Borrar
      // existe, pero por el enlace explícito del pie y con su confirmación.
      //
      // El toast reusa `guest_waiting_note` («Tu documento te estará esperando en
      // tu cuenta»), que es exactamente lo que acaba de pasar y ya está traducido.
      // `porConfirmarAbiertosRef` NO se toca: cerrar es cerrar. (Esta nota decía
      // «no se reabre sola hasta recargar»; desde la tanda 2 recargar tampoco la
      // reabre. El botón de la tarjeta es la única vuelta.)
      dequeueAutoSplit(head.temp_upload_id);
      toast.info(t('dashboard:auto_split.guest_waiting_note'));
      return;
    }
    const aff = head.affordability;
    // Duplicate variant: the document already lives in Pending to process — closing is
    // just closing. A "nothing saved" toast here would contradict reality.
    if (head.duplicate_of_pending) {
      trackEvent('modal_closed', { k: aff?.k, n: aff?.n, saved: false, duplicate: true });
      dequeueAutoSplit(head.temp_upload_id);
      return;
    }
    // Anything not fully processable: make the consequence explicit — nothing was saved.
    // Full (k=n): closing is a plain cancel — no toast (matrix v1: nothing is at stake).
    if (aff && aff.k < aff.n) {
      toast.info(t('dashboard:auto_split.nothing_saved_toast'));
    }
    trackEvent('modal_closed', { k: aff?.k, n: aff?.n, saved: false });
    dequeueAutoSplit(head.temp_upload_id);
    // APP-1 B4 (#401): AQUI IBA `dismissAutoSplit`, la nota en el `localStorage` de
    // ESTE navegador. Se retira entera —lectura y escritura— porque la decisión de
    // reapertura la manda ahora el servidor (`ventana_cerrada`, B3), que sí cruza de
    // aparato y de la app a la web. El valor que ya exista en los navegadores de la
    // gente se ignora sin migración: nadie vuelve a leerlo.
    // #368: `borrarPlaceholderPorConfirmar` ya no es dependencia de este handler —
    // cerrar dejó de borrar. Sigue viva y la usa «Descartar», aquí abajo.
  }, [autoSplitQueue, dequeueAutoSplit, t, marcarVentanaCerrada]);

  // ── post-t7-1 (H3): EL AVISO DEL DOCUMENTO ENTERO GUARDADO EN PENDIENTES ─────────────
  // Se abre solo, UNA vez: el resumen de pendientes trae los avisos sin ver y entran a la MISMA cola de la ventana
  // (con su "Documento 1 de 2"). Cerrarlo por cualquier via lo da por visto EN EL SERVIDOR (no vuelve en ningun
  // aparato); este registro solo evita que el resumen viejo lo vuelva a meter antes de refrescarse.
  const avisosEnteroVistosRef = useRef(new Set());
  useEffect(() => {
    if (!activa || !avisosDocumentoEntero || avisosDocumentoEntero.length === 0) return;
    setAutoSplitQueue((prev) => {
      const ids = new Set(prev.map((p) => p.temp_upload_id));
      const nuevos = avisosDocumentoEntero
        .filter((a) => !avisosEnteroVistosRef.current.has(a.id) && !ids.has(`entero_${a.id}`))
        .map((a) => ({ documento_entero: true, aviso_entero: true, aviso_id: a.id, temp_upload_id: `entero_${a.id}`,
          original_filename: a.filename, credits_required: a.credits_required, credits_available: a.credits_available,
          credits_reset_at: a.credits_reset_at, plan: a.plan }));
      return nuevos.length ? [...prev, ...nuevos] : prev;
    });
  }, [activa, avisosDocumentoEntero]);
  // Un documento que se acaba de guardar entero en Pendientes deja la lista con su aviso sin ver: el resumen se
  // refresca para traerlo (en ese momento si estaba mirando).
  const enteroAvisadoRef = useRef(new Set());
  useEffect(() => {
    const nuevos = docs.filter((d) => d.status === 'pending' && d.documento_entero
      && d.aviso_pendiente && d.aviso_pendiente.visto === false && !enteroAvisadoRef.current.has(d.id));
    if (nuevos.length === 0) return;
    for (const d of nuevos) enteroAvisadoRef.current.add(d.id);
    notifyPendingChanged();
  }, [docs]);
  // H2.4: un documento entero que se quedo en el Panel (Pendientes lleno, o de un invitado que ya creo su cuenta) se
  // manda a Pendientes en cuanto hay lugar. Nada se cobra; una vez por documento en esta visita.
  const aPendientesPedidoRef = useRef(new Set());
  useEffect(() => {
    if (!activa || esInvitado || !pendingSnapshot || !(pendingSnapshot.pending_free > 0)) return;
    const listos = docs.filter((d) => d.lectura_estado === 'por_confirmar' && d.preview_split
      && d.preview_split.documento_entero && !aPendientesPedidoRef.current.has(d.id));
    for (const d of listos) {
      aPendientesPedidoRef.current.add(d.id);
      api.post(`/documents/${d.id}/a-pendientes`).then((r) => {
        if (r?.data?.estado === 'pendiente') {
          dequeueAutoSplit(`ph_${d.id}`);
          fetchDocs();
          notifyPendingChanged();
        }
      }).catch(() => { /* se queda como estaba: la ventana y la tarjeta de siempre */ });
    }
  }, [activa, esInvitado, pendingSnapshot, docs, dequeueAutoSplit, fetchDocs]);
  const cerrarAvisoEntero = useCallback((head) => {
    if (!head) return;
    avisosEnteroVistosRef.current.add(head.aviso_id);
    api.post(`/documents/${head.aviso_id}/aviso-visto`).catch(() => { /* el resumen lo volvera a traer */ });
    dequeueAutoSplit(head.temp_upload_id);
  }, [dequeueAutoSplit]);

  // ── Auto-split modal: top-plan-maxed / doc-too-big → existing support page ──
  const handleAutoSplitContact = useCallback(() => {
    navigate('/support?subject=custom-plan');
  }, [navigate]);

  // ── Auto-split modal: go to the pending section. With a parentDocId (ONLY the duplicate
  //    variant's primary) the upload is RESOLVED: drop it from the restore queue so the
  //    modal does NOT reopen on return (the temp expires via its own TTL). Without an id
  //    (overcap "free space") the queue persists — the user is meant to come back. ──
  const handleAutoSplitGoPending = useCallback((parentDocId) => {
    if (parentDocId) {
      const head = autoSplitQueue[0];
      if (head) {
        trackEvent('modal_closed', { k: head.affordability?.k, n: head.affordability?.n, saved: false, duplicate: true, resolved: true });
        // APP-1 B4 (#401): el cierre por aparato se sustituye por el del servidor.
        // Esta salida SÍ resuelve la subida (el alumno se va al grupo que ya existe),
        // así que la ventana no debe volver en ningún aparato.
        marcarVentanaCerrada(head);
        dequeueAutoSplit(head.temp_upload_id);
      }
      navigate(`${PENDING_ROUTE}?group=${parentDocId}`);
      return;
    }
    navigate(PENDING_ROUTE);
  }, [autoSplitQueue, dequeueAutoSplit, navigate, marcarVentanaCerrada]);

  // ── Auto-split modal: explicit "Discard" — close WITHOUT saving (k0/partial only). ──
  const handleAutoSplitDiscard = useCallback(async () => {
    const head = autoSplitQueue[0];
    if (!head) return;
    if (head.documento_base_id) {
      // #368: descartar SIGUE borrando el documento — es lo que el enlace promete y
      // el alumno ya lo confirmó en dos pasos.
      //
      // APP-1 B4 (#401): Y EL TOAST ESPERA AL SERVIDOR. Hasta hoy se pintaba en la
      // misma vuelta, sin mirar si el DELETE había ido bien: con la red caída el
      // alumno leía «Documento eliminado» en verde y el documento seguía en su Panel.
      // Ahora el verde sólo sale con un 2xx; si no, el error que ya existe para este
      // caso (`toasts.delete_failed`, el mismo del bote de la tarjeta).
      dequeueAutoSplit(head.temp_upload_id);
      const borrado = await borrarPlaceholderPorConfirmar(head);
      if (borrado) toast.success(t('dashboard:auto_split.deleted_doc_toast'));
      else toast.error(t('dashboard:toasts.delete_failed'));
      return;
    }
    trackEvent('modal_closed', { k: head.affordability?.k, n: head.affordability?.n, saved: false });
    // APP-1 B4 (#401) — AQUÍ VIVÍA EL TOAST QUE MÁS MENTÍA DEL CARRIL.
    // #372 le había dado al muro `full` de texto el toast «Documento eliminado», y en
    // este camino no se elimina NADA: no hay documento en el servidor (sólo una copia
    // temporal que caduca sola) y ni siquiera se llama a `api.delete` — una prueba de
    // la casa lo aseguraba explícitamente. Era verde, era el color que más confianza
    // da, y estaba sobre la acción que más se parece a lo que el alumno recuerda haber
    // visto en el smoke del 11-sep.
    //   VERBATIM ANTES:
    //       if (classifyWall(head.affordability, 'upload') === 'full') {
    //         toast.success(t('dashboard:auto_split.deleted_doc_toast'));
    //       } else {
    //         toast.info(t('dashboard:auto_split.discard_toast'));
    //       }
    // Con B2 este camino sólo lo pisa quien NO estrena documento (el invitado), y para
    // él `discard_toast` —«Descartado. No se guardó…»— ya dice la verdad en los cuatro
    // muros. Cero llaves nuevas.
    toast.info(t('dashboard:auto_split.discard_toast'));
    dequeueAutoSplit(head.temp_upload_id);
  }, [autoSplitQueue, dequeueAutoSplit, t, borrarPlaceholderPorConfirmar]);

  // ── Auto-split guest mode (F1): prepare-signup → persist claim token + funnel flag
  //    in sessionStorage (NEVER in a URL) → /signup. The queue is already persisted by
  //    the upload path; the restore layer picks everything up after registration. ──
  const handleAutoSplitGuestSignup = useCallback(async () => {
    const head = autoSplitQueue[0];
    if (!head) return;
    if (head.documento_base_id) {
      // REMATES (PR #349, A-6 / #339): el sobre de un escaneado largo vive en el
      // placeholder, que ya es del invitado (el fondo borró su `temp_upload`, así que
      // `prepare-signup` daría 404 → «tu sesión expiró»). No hay nada que reclamar: la
      // conversión es un `update` del mismo usuario y el documento sigue siendo suyo.
      // Se deja la bandera del funnel (para el velo del regreso) y se va a /signup.
      try { sessionStorage.setItem(GUEST_FUNNEL_FLAG_KEY, 'true'); } catch { /* sin almacén: da igual */ }
      trackEvent('guest_signup_cta_click', { parts_count: head.total_chunks });
      navigate('/signup');
      return;
    }
    try {
      await prepareGuestSignup(api, head);
      trackEvent('guest_signup_cta_click', { parts_count: head.total_chunks });
      navigate('/signup');
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(t('dashboard:auto_split.session_expired_toast'));
        dequeueAutoSplit(head.temp_upload_id);
      } else {
        toast.error(t('dashboard:auto_split.finalize_failed_toast'));
      }
    }
  }, [autoSplitQueue, dequeueAutoSplit, navigate, t]);

  // ── ajustes-1-3-0 (Bloque B, ajuste 11, #503): EN LA APP, UNA OFERTA CIERRA LA VENTANA Y
  //    EL REGRESO LA REABRE UNA VEZ. Regla de #399 a #401: la memoria de "cerrada" vive en
  //    el servidor; aqui NO se anota `ventana_cerrada` (una oferta no es la "X"). Lo que se
  //    recuerda hasta el regreso es el sobre de la cabeza, en una ref de ESTE montaje: en la
  //    app el Panel sigue montado mientras la compra esta al frente (pager de raices), asi que
  //    la ref vive lo que dura la compra y nada mas; nada en localStorage, ni sessionStorage,
  //    ni variable de modulo. Si la app se cierra a medias no hay regreso: la tarjeta "Listo
  //    para procesar" sigue ahi y su boton reabre, como siempre.
  //    Al volver (compra completada, cancelada o cerrada, los tres): se LEVANTA
  //    `ventana_cerrada` en el servidor por el MISMO camino que la web al volver de Stripe
  //    (`cerrada: false`), y la ventana se reabre UNA vez con `GET preview-split` fresco (la
  //    cabeza entra rancia y el refetch la repinta; el efecto de cambio de cabeza pide lo
  //    mismo, como con el boton de la tarjeta: dos GET, declarado). Si en los 5 s siguientes
  //    cambian el plan o los creditos que el Panel ya observa (plan-info / usage), se pide
  //    UNA vez mas y se repinta sin cerrar; si no cambian, se queda. La "X" despues del
  //    regreso anota `ventana_cerrada` (handleAutoSplitClose, sin cambio) y nada mas la
  //    reabre: el sondeo ya tiene el id en su registro, el foco solo refresca, y un segundo
  //    regreso sin oferta tocada encuentra la ref vacia. En la WEB nada de esto corre.
  const regresoDeOfertaRef = useRef(null);
  const vigilanciaDeRegresoRef = useRef(null);
  const usageRef = useRef(usage);
  useEffect(() => { usageRef.current = usage; }, [usage]);
  const salirAComprarEnApp = useCallback((head) => {
    if (!IS_APP_BUILD || !head) return false;
    regresoDeOfertaRef.current = { sobre: head };
    dequeueAutoSplit(head.temp_upload_id);
    return true;
  }, [dequeueAutoSplit]);
  const regresarDeLaOferta = useCallback(() => {
    const marca = regresoDeOfertaRef.current;
    if (!marca) return;
    regresoDeOfertaRef.current = null;
    const head = marca.sobre;
    const cuerpo = {};
    if (head.temp_upload_id && !String(head.temp_upload_id).startsWith('ph_')) {
      cuerpo.temp_upload_id = head.temp_upload_id;
    }
    if (head.documento_base_id) cuerpo.documento_base_id = head.documento_base_id;
    if (cuerpo.temp_upload_id || cuerpo.documento_base_id) {
      api.post('/documents/ventana-cerrada', { ...cuerpo, cerrada: false })
        .catch(() => { /* best-effort, como en la web: la cola local ya la pone al frente */ });
    }
    const tempId = head.temp_upload_id;
    setAutoSplitQueue((prev) => [head, ...prev.filter((p) => p.temp_upload_id !== tempId)]);
    setAutoSplitHeadStale(true);
    refetchAutoSplitHead(tempId, head.documento_base_id);
    if (vigilanciaDeRegresoRef.current) clearTimeout(vigilanciaDeRegresoRef.current.temporizador);
    const vigilancia = {
      tempId, docId: head.documento_base_id, plan: planSnapRef.current?.plan,
      creditos: creditosDe(usageRef.current),
    };
    vigilancia.temporizador = setTimeout(() => {
      if (vigilanciaDeRegresoRef.current === vigilancia) vigilanciaDeRegresoRef.current = null;
    }, REGRESO_VIGILANCIA_MS);
    vigilanciaDeRegresoRef.current = vigilancia;
  }, [refetchAutoSplitHead]);
  useEffect(() => {
    const v = vigilanciaDeRegresoRef.current;
    if (!v) return;
    if (planInfo?.plan === v.plan && creditosDe(usage) === v.creditos) return;
    clearTimeout(v.temporizador);
    vigilanciaDeRegresoRef.current = null;
    setAutoSplitHeadStale(true);
    refetchAutoSplitHead(v.tempId, v.docId);
  }, [planInfo, usage, refetchAutoSplitHead]);
  useEffect(() => {
    if (activa && IS_APP_BUILD) regresarDeLaOferta();
  }, [activa, regresarDeLaOferta]);
  useEffect(() => () => {
    if (vigilanciaDeRegresoRef.current) clearTimeout(vigilanciaDeRegresoRef.current.temporizador);
  }, []);

  // ── Auto-split: persist intent before redirecting to Stripe pack checkout ──
  const handleAutoSplitBuyPack = useCallback(() => {
    const head = autoSplitQueue[0];
    if (!head) return;
    // ajustes-1-3-0 (Bloque B1): en la app la ventana se cierra (sin anotar `ventana_cerrada`)
    // y la compra se abre exactamente como hoy; sin intent ni cortina: no hay regreso de Stripe.
    if (salirAComprarEnApp(head)) {
      setPackPurchaseFromAutoSplit(true);
      setShowPackModal(true);
      return;
    }
    sessionStorage.setItem(AUTOSPLIT_INTENT_KEY, JSON.stringify({
      temp_upload_id: head.temp_upload_id,
      intent_type: 'pack_purchase',
      timestamp: Date.now(),
    }));
    setPackPurchaseFromAutoSplit(true);
    // Hide AutoSplitModal so CreditPackModal isn't obstructed; restored on pack-modal close.
    setAutoSplitModalSuppressed(true);
    setShowPackModal(true);
  }, [autoSplitQueue, salirAComprarEnApp]);

  // ── Auto-split: persist intent before navigating to plan upgrade ──
  const handleAutoSplitUpgrade = useCallback(() => {
    const head = autoSplitQueue[0];
    if (!head) return;
    // ajustes-1-3-0 (Bloque B1): en la app, igual que con el paquete; el regreso lo da la
    // raiz al volver a estar activa (pager).
    if (salirAComprarEnApp(head)) {
      navigate('/upgrade');
      return;
    }
    sessionStorage.setItem(AUTOSPLIT_INTENT_KEY, JSON.stringify({
      temp_upload_id: head.temp_upload_id,
      intent_type: 'subscription_upgrade',
      timestamp: Date.now(),
    }));
    navigate('/upgrade');
  }, [autoSplitQueue, navigate, salirAComprarEnApp]);

  // ── Auto-split: delete an active doc to free a slot → re-fetch preview so affordability
  //    (k) reflects the freed slot. ──
  const handleAutoSplitDeleteDoc = useCallback(async (docId) => {
    const head = autoSplitQueue[0];
    try {
      await api.delete(`/documents/${docId}`);
      toast.success(t('dashboard:auto_split.deleted_doc_toast'));
      await fetchDocs();
      if (head) await refetchAutoSplitHead(head.temp_upload_id);
    } catch {
      toast.error(t('dashboard:toasts.delete_failed'));
    }
  }, [autoSplitQueue, fetchDocs, refetchAutoSplitHead, t]);

  // TANDA 11 (firma #178) — el bote arranca la despedida, no el salto.
  //
  // EL ORDEN IMPORTA Y ESTÁ ELEGIDO:
  //
  // 1. El id entra al conjunto ANTES de pedirle nada al servidor. Ése es todo el
  //    contrato de "se siente inmediato": el dedo levanta y la tarjeta ya se está
  //    yendo. No es un borrado optimista de la LISTA —`docs` no se toca aquí—,
  //    sólo la despedida visual; la lista sigue cambiando únicamente cuando el
  //    servidor contesta, que es la doctrina de este Panel.
  // 2. Se esperan LAS DOS COSAS a la vez, no una detrás de la otra: la respuesta
  //    del servidor y la animación. Si el servidor tarda 800 ms, la tarjeta ya se
  //    fue hace rato y sólo se espera a él; si contesta en 40 ms, se espera a que
  //    la animación termine para que nadie vea el corte a media despedida. El
  //    tiempo total es el del más lento, nunca la suma.
  // 3. En el éxito el id NO sale del conjunto todavía: sale cuando la lista fresca
  //    ya llegó. Si saliera antes, la tarjeta —que sigue en `docs` hasta el
  //    repintado— reaparecería un instante antes de desaparecer, que es un
  //    parpadeo peor que el salto que esta tanda vino a quitar.
  // 4. `fetchDocs()` se lanza SIN esperar y el aviso del banner se dispara justo
  //    después, exactamente como antes: la espera se hace sobre la promesa ya
  //    guardada, así que el orden de esas dos llamadas no cambia ni un tick.
  // 5. En el fallo el id sale de inmediato y la tarjeta vuelve con la misma
  //    transición, en sentido contrario. El toast de error es el de siempre.
  const handleDelete = async (docId) => {
    setSaliendo((prev) => new Set(prev).add(docId));
    const quitarDeSalida = () => setSaliendo((prev) => {
      if (!prev.has(docId)) return prev;
      const siguiente = new Set(prev);
      siguiente.delete(docId);
      return siguiente;
    });
    try {
      await Promise.all([
        api.delete(`/documents/${docId}`),
        new Promise((resolve) => setTimeout(resolve, DOC_EXIT_MS)),
      ]);
      toast.success(t('dashboard:toasts.delete_success'));
      // dashboard-rediseno (4.9): la Preparacion se vuelve a pedir despues de borrar.
      invalidarPreparacion();
      // ── TANDA 15 (#190) — Y LA BODEGA LOCAL SE BARRE AQUÍ MISMO ───────────
      //
      // EL HUECO: el servidor ya no tiene el documento, pero el teléfono
      // conserva su pool ENTERO hasta que la siguiente foto de `/sync/state` lo
      // eche de menos. En esa ventana, sin señal, el documento RESUCITA — la
      // lista offline lo pinta y su material se puede estudiar.
      //
      // DÓNDE VA, Y POR QUÉ EXACTAMENTE AQUÍ: ya con el 200 en la mano (si el
      // DELETE hubiera fallado estaríamos en el `catch` y no habría nada que
      // barrer) y ANTES de `notifyDocsChanged()`, que es el aviso que manda a
      // las pantallas a preguntar de nuevo — y sin señal la respuesta sale de
      // esta misma bodega. Avisar antes de barrer sería repintar al muerto.
      //
      // Y ANTES DE `fetchDocs()` para no meter un tick entre la lista fresca y
      // el aviso: el punto 4 de arriba fija que esas dos van pegadas. Lo que se
      // retrasa es una lectura de red que el servidor ya contesta sin este
      // documento; barrer es SQLite local, no viaja a ningún lado.
      //
      // SILENCIOSA: gate de build (en web no hay bodega y el bundle jamás pide
      // ese chunk), y el `catch` se traga el error a propósito — si la escoba
      // falla, la siguiente foto repara, que es el colchón que ya existía. Un
      // borrado que el servidor aceptó no puede acabar en toast de error por un
      // tropiezo local.
      if (IS_APP_BUILD) {
        try {
          const dl = await import('../lib/warehouse/downloader');
          await dl.barrerDocBorrado(user?.id, docId);
        } catch { /* la próxima foto de /sync/state repara */ }
      }
      const listaFresca = fetchDocs();
      // A delete can drop the user back under the new tope — refresh the downgrade banner.
      notifyDocsChanged();
      await listaFresca;
      quitarDeSalida();
    } catch {
      quitarDeSalida();
      toast.error(t('dashboard:toasts.delete_failed'));
    }
  };

  // ── dashboard-rediseno (4.8, H1): RENAME ─────────────────────────────────────────────────
  // La ventana vive aqui (y no en la tarjeta) porque la abren dos sitios: el menu de un documento
  // y el del encabezado de un documento dividido. `renombrar` (lib/renombrarDocumento.js) hace el
  // POST y pone el nombre nuevo en las caches y en la bodega; con su respuesta, la lista del
  // Panel cambia en su lugar, sin recargar (las partes de un dividido traen su nombre nuevo y la
  // tarjeta padre lo lee de ellas). Sin aviso de exito: la ventana se cierra y el nombre cambia.
  const [renombrando, setRenombrando] = useState(null);
  const abrirRenombrar = useCallback(({ documentId, nombre, volverA }) => {
    setRenombrando({ documentId, parentDocId: null, nombre, volverA });
  }, []);
  const abrirRenombrarPadre = useCallback((padre, volverA) => {
    setRenombrando({ documentId: null, parentDocId: padre.marca, nombre: padre.nombre, volverA });
  }, []);
  const cerrarRenombrar = useCallback(() => setRenombrando(null), []);
  const aplicarNombres = useCallback((respuesta) => {
    const lista = respuesta && Array.isArray(respuesta.documentos) ? respuesta.documentos : [];
    const nuevos = new Map(lista.filter((d) => d && d.id && typeof d.filename === 'string').map((d) => [d.id, d.filename]));
    if (nuevos.size === 0) return;
    setDocs((prev) => prev.map((d) => (nuevos.has(d.id) ? { ...d, filename: nuevos.get(d.id) } : d)));
  }, []);
  const guardarNombre = useCallback(async (nombre) => {
    const r = renombrando;
    if (!r) return;
    const respuesta = await renombrar({ user, documentId: r.documentId, parentDocId: r.parentDocId, nombre });
    aplicarNombres(respuesta);
    setRenombrando(null);
    // Lo mismo que hoy tras borrar: las pantallas premontadas en el carril de la app (Centro de
    // Estudio, Analiticas) se repintan con el nombre nuevo.
    notifyDocsChanged();
  }, [renombrando, user, aplicarNombres]);
  // Sin red en la app, "Save" avisa y la ventana se queda: se pregunta al tocar (la red puede
  // perderse con la ventana abierta).
  const sinRedAhoraRef = useRef(false);
  sinRedAhoraRef.current = IS_APP_BUILD && isOffline;
  const sinRedAhora = useCallback(() => sinRedAhoraRef.current, []);

  // series-7 (#490, C.4): EL BOTE DE LA TARJETA PADRE. Una sola peticion
  // (`DELETE /documents/grupo/<marca>`) que borra en el servidor TODAS las partes del
  // documento, las de la tarjeta y las de Pendientes, cada una con la misma cascada que
  // el bote de una parte. La tarjeta ya pidio confirmacion (su ventana), y aqui se hace
  // lo mismo que `handleDelete` despues del 200: el aviso, el barrido de la bodega en la
  // app, la lista fresca y los avisos a las demas pantallas (tambien a Pendientes, que
  // perdio partes). Si no se borraron todas (`borradas < de`) el aviso es el de fallo y
  // la lista fresca ensena lo que quedo; en la app solo se barren las que SI se borraron
  // (`fallidas` trae las que no). Un fallo de red: el aviso de fallo, y la lista se
  // vuelve a pedir en silencio por si el servidor alcanzo a borrar algo. Sin salida
  // suave: la tarjeta se va con la lista fresca, mientras la ventana sigue en vuelo.
  const handleBorrarPartes = async (padre) => {
    let respuesta = null;
    try {
      const res = await api.delete(`/documents/grupo/${encodeURIComponent(padre.marca)}`);
      respuesta = (res && res.data) || {};
    } catch {
      toast.error(t('dashboard:toasts.delete_failed'));
      await fetchDocs(true);
      return;
    }
    const todas = typeof respuesta.borradas === 'number' && respuesta.borradas === respuesta.de;
    if (todas) toast.success(t('dashboard:toasts.delete_success'));
    else toast.error(t('dashboard:toasts.delete_failed'));
    invalidarPreparacion();
    if (IS_APP_BUILD) {
      const fallidas = new Set(Array.isArray(respuesta.fallidas) ? respuesta.fallidas : []);
      const barrer = (padre.hijas || []).map((d) => d.id).filter((id) => !fallidas.has(id));
      if (!todas && !Array.isArray(respuesta.fallidas)) barrer.length = 0;
      try {
        const dl = await import('../lib/warehouse/downloader');
        // Una por una, como el bote de una parte: la bodega es SQLite local.
        for (const id of barrer) await dl.barrerDocBorrado(user?.id, id);
      } catch { /* la proxima foto de /sync/state repara */ }
    }
    const listaFresca = fetchDocs();
    notifyDocsChanged();
    notifyPendingChanged();
    await listaFresca;
  };

  // Map HTTP error codes from /retry or /process into i18n toast keys so the user gets
  // actionable guidance instead of a generic "Retry failed".
  //
  // R-17 (remate F) — LA CLASIFICACIÓN SE MUDÓ A lib/retryErrorToast.js, VERBATIM.
  // Aquí vivía COS-G B1/B7 completo; la página de Pendientes tenía SU propia versión
  // recortada ("402 o genérico"), y esa asimetría entre dos superficies que llaman a la
  // misma cadena /retry → /process es la que produjo el bicho del smoke v5 (el 429 del
  // freno de ráfaga leyéndose como "Reintento falló, contacta a soporte"). Una sola
  // fuente, dos copys de 402: el del Panel habla del documento, el de Pendientes de la
  // parte. `serverDetailFallback: true` conserva EXACTAMENTE el comportamiento de esta
  // superficie: un `detail` de texto se pinta tal cual antes de caer al genérico.
  const _retryErrorToast = (err) => retryErrorToast(err, t, { serverDetailFallback: true });

  const handleRetry = async (docId) => {
    setProcessing(prev => ({ ...prev, [docId]: true }));
    try {
      const r1 = await api.post(`/documents/${docId}/retry`);
      // For `error` docs the retry endpoint full-resets to status='uploaded' WITHOUT
      // triggering processing (credit-charging lives in /process). Chain here so a
      // single user click completes the full retry-then-process flow.
      // For `partial` docs the retry endpoint already kicks off the background retry,
      // returning status='processing' — no chaining needed.
      if (r1.data?.status === 'uploaded') {
        await api.post(`/documents/${docId}/process`);
      }
      toast.success(t('dashboard:toasts.retry_started'));
      fetchDocs();
      // LISTO-LOCAL B2 — el MISMO aviso que dan los cuatro caminos de subida, y
      // por la misma razón: acaba de empezar a cocinar algo. Sin él, reintentar
      // con la biblioteca en reposo dejaba el documento sin quien lo sondeara
      // (el poller de avisos se apaga solo cuando no queda nada en proceso), y
      // con el sondeo muerto tampoco corre la convergencia: el material no se
      // pedía al terminar.
      document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
    } catch (err) {
      toast.error(_retryErrorToast(err));
    } finally {
      setProcessing(prev => ({ ...prev, [docId]: false }));
    }
  };

  const handleProcess = async (docId) => {
    // Defensive entry-point: docs that landed in 'uploaded' (e.g., the /retry chain
    // above broke partway, or any other route to that state) get a direct Process
    // button. This goes straight to /process, which handles credit-charging and all
    // pre-checks. Same error handling as handleRetry.
    setProcessing(prev => ({ ...prev, [docId]: true }));
    try {
      await api.post(`/documents/${docId}/process`);
      toast.success(t('dashboard:toasts.retry_started'));
      fetchDocs();
      // LISTO-LOCAL B2: el gemelo del de `handleRetry`, misma razón.
      document.dispatchEvent(new CustomEvent('studybuddy:doc-uploaded'));
    } catch (err) {
      toast.error(_retryErrorToast(err));
    } finally {
      setProcessing(prev => ({ ...prev, [docId]: false }));
    }
  };

  // F6.7b (fase 1) — REINTENTAR LA ENTREGA. Es lo que hace la tarjeta con aviso
  // honesto cuando se toca, y NO tiene nada que ver con handleRetry de arriba:
  // aquí NUNCA se reprocesa el documento. El servidor ya terminó su trabajo; lo
  // que falta es JALARLO al teléfono. Por eso lo único que se llama es la
  // convergencia de F6.7a con un motivo propio ('card-retry'), que compara
  // servidor contra dispositivo y, si hay hueco y hay con qué, despierta al
  // cartero. Cero endpoints de proceso, cero créditos, cero riesgo.
  //
  // Si el reintento vuelve a fallar (lo esperable sin señal), el aviso PERMANECE
  // y no se emite ningún toast nuevo: el estado ya lo dice todo y un toast por
  // tap sería ruido sobre una situación que el usuario ya entiende.
  const handleDeliveryRetry = async (docId) => {
    if (!IS_APP_BUILD || retryingDoc[docId]) return;
    setRetryingDoc(prev => ({ ...prev, [docId]: true }));
    try {
      const c = await import('../lib/warehouse/converge');
      await c.convergeWarehouse('card-retry');
    } catch (e) {
      // el reintento jamás rompe la pantalla
    } finally {
      // La presencia se vuelve a leer siempre: si el material SÍ bajó, el aviso
      // desaparece solo. (El downloader además emite `local-material-ready`,
      // que ya repinta; esta lectura cubre el caso en que la corrida no
      // committeó nada nuevo.)
      await refreshPresence();
      setRetryingDoc(prev => { const n = { ...prev }; delete n[docId]; return n; });
    }
  };

  /* t7-3 (A1 y A3, #557 y #558): la tarjeta del grupo en la APP. Sin red, una card cuya
     fecha ya paso (contra el reloj del telefono) se pinta caducada; con red manda el servidor.
     La entrega de cada card activa sale de la MISMA regla que la de un documento
     (`computeCardDelivery`), mirando su paquete `pub:<id>` en la bodega, y su reintento es
     el mismo. En la web, los grupos tal cual y sin entrega. */
  const gruposParaPintar = IS_APP_BUILD && isOffline ? gruposSinRed(gruposDelAlumno) : gruposDelAlumno;
  const entregaDeCard = IS_APP_BUILD
    ? (card) => {
      if (!card || card.estado === 'caducada') return null;
      const d = computeCardDelivery({ status: 'ready' }, presenceById.get(idLocalDeCard(card.id)), {
        offline: isOffline, appBuild: true, presenceRead,
      });
      return d === CARD_DELIVERY.NORMAL ? null : d;
    }
    : null;
  const reintentarCard = IS_APP_BUILD ? (card) => handleDeliveryRetry(idLocalDeCard(card.id)) : null;

  const handleRegenerateFlashcards = async (docId) => {
    setRegeneratingFlashcards(prev => ({ ...prev, [docId]: true }));
    toast.info(t('dashboard:toasts.regen_flashcards_started'));
    try {
      const res = await api.post(`/documents/${docId}/regenerate-flashcards`);
      const newCount = res.data?.flashcard_count || 0;
      if (newCount > 0) {
        toast.success(t('dashboard:toasts.regen_flashcards_success', { count: newCount }));
        setDocs(prev => prev.map(d => d.id === docId
          ? { ...d, flashcard_count: newCount, status: res.data?.status || d.status }
          : d));
      } else {
        toast.error(t('dashboard:toasts.regen_flashcards_empty'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('dashboard:toasts.regen_flashcards_failed'));
    } finally {
      setRegeneratingFlashcards(prev => ({ ...prev, [docId]: false }));
    }
  };

  // dashboard-rediseno (A14): las figuras se piden al ABRIR su panel flotante (la misma peticion
  // y el mismo aviso de fallo de hoy). Decia `toggleImagePreview`, que ademas abria el desplegable
  // dentro de la tarjeta; el panel ahora es de cada gemela (components/panel/Flotante.js).
  // Devuelve false si fallo, y el panel se cierra.
  const cargarFiguras = async (docId) => {
    if (docImages[docId]) return true;
    setLoadingImages(prev => ({ ...prev, [docId]: true }));
    try {
      const res = await api.get(`/documents/${docId}/images`);
      setDocImages(prev => ({ ...prev, [docId]: res.data.images }));
      return true;
    } catch {
      toast.error(t('dashboard:toasts.image_load_failed'));
      return false;
    } finally {
      setLoadingImages(prev => ({ ...prev, [docId]: false }));
    }
  };

  const readyDocs = docs.filter(d => d.status === 'ready' || d.status === 'partial');
  /* t7-3 remate 8 (#591, decision 17; C53 y C99): "Estudiar" de la cabecera se enciende tambien
     con material de un grupo, aunque no haya documentos propios listos. Cuenta una publicacion
     ACTIVA (no caducada: con red manda el servidor; sin red en la app, su fecha contra el reloj
     del telefono, que es lo que `gruposParaPintar` ya trae) y, en la app, solo si ya esta en la
     bodega: la MISMA senal con que se pinta su tarjeta (`entregaDeCard` en null = su paquete
     `pub:<id>` esta copiado; mientras baja o se prepara sin red, no cuenta), y nunca antes de leer
     la presencia. En la web, con estar activa basta. El invitado no tiene grupos. */
  const hayPublicacionParaEstudiar = !!user && !user.is_guest
    && cardsDeLosGrupos(gruposParaPintar).some((card) => card.estado !== 'caducada'
      && (!IS_APP_BUILD || (presenceRead && !entregaDeCard(card))));
  const puedeEstudiar = readyDocs.length > 0 || hayPublicacionParaEstudiar;
  // Upload panels show only items with a REAL upload story ('split' items belong to the
  // auto-split modal, not to the upload UI).
  const visibleUploadQueue = uploadQueue.filter(it => it.status !== 'split' && it.status !== 'duplicate');
  // Library = active documents only. Pending parts live in /pending and never count here
  // (header counter, empty state, list height, section visibility all use this). Archived
  // (plan-downgrade-locked) docs are pulled OUT into their own "Locked" section below.
  const visibleDocs = sortLibraryDocs(docs.filter(d => d.status !== 'pending' && d.status !== 'pending_credits' && d.status !== 'archived'));
  // series-7 (#490): LO QUE LA LISTA PINTA. Las partes de un mismo troceo (misma marca
  // `parent_doc_id`) dejan de ir sueltas: van juntas en UNA tarjeta padre, en el lugar de
  // su parte mas reciente (`lib/partes.js`). Sale de la MISMA lista `docs`, en el MISMO
  // render que todo lo demas (la del servidor, la semilla de la cache o la foto local de
  // la app): ninguna llamada nueva y la misma frescura. Un documento suelto viaja tal
  // cual; una padre, como la entrada que la derivacion arma. Sin partes, esta lista y
  // `visibleDocs` traen los mismos documentos en el mismo orden (lo fija
  // `Dashboard.sinPartesIgual.test.js`). Los contadores, la pestana y los botones siguen
  // contando documentos (`visibleDocs`): la padre no es un documento.
  const entradasDelPanel = agruparPartes(docs).map((e) => (e.tipo === 'doc' ? e.doc : e));
  // Locked docs: shown in a separate collapsible section. Split parts archive individually,
  // so a group can be split between the active library and here — each section groups its own.
  // ajustes-1-3-0 (Bloque C7, #505): una PARTE bloqueada (con `parent_doc_id`) ya no se repite
  // aqui: se ve UNA sola vez, dentro de su padre. La pestana y su contador cuentan solo los
  // documentos sueltos bloqueados.
  const archivedDocs = sortLibraryDocs(docs.filter(d => d.status === 'archived' && !d.parent_doc_id));
  // Map parent_doc_id → pending group, to show a discreet "continue" line on a ready part
  // whose siblings are still pending/failed (only groups WITH pending parts are in summary).
  const pendingByParent = React.useMemo(() => {
    const m = {};
    (pendingGroups || []).forEach(g => { if (g.parent_doc_id) m[g.parent_doc_id] = g; });
    return m;
  }, [pendingGroups]);

  // dashboard-rediseno (E1): LAS PESTANAS "Active, 5" Y "Locked, 3". Solo con documentos
  // bloqueados, arriba de la lista y en el lugar del contador (que se quita: las pestanas ya lo
  // dicen). Control de dos botones sobre #111111; el elegido en blanco con letra negra; 36 de
  // alto (44 y a lo ancho en el telefono). Lo que hace cada pestana no cambia.
  // LAPIDA dashboard-rediseno: aqui vivia `statusColor`, la familia de colores de las insignias
  // de estado; la tarjeta nueva pinta sus estados con `components/panel/ChipDeEstado.js`.
  const renderDocTabs = (wrapperClass = '') => (archivedDocs.length > 0 ? (
    <div className={`items-center gap-1 p-1 bg-[#111111] border border-[#222222] rounded-xl shrink-0 ${wrapperClass}`} data-testid="doc-tabs">
      <button
        onClick={() => setDocTab('active')}
        className={`flex-1 md:flex-none flex items-center justify-center gap-1.5 px-3 sm:px-4 min-h-[44px] md:min-h-[36px] rounded-lg text-[13px] font-semibold transition-colors ${docTab === 'active' ? 'bg-white text-black' : 'text-[#9A9A9A] hoverable:text-white'}`}
        data-testid="doc-tab-active">
        {t('dashboard:doc_tabs.active', { count: visibleDocs.length })}
      </button>
      <button
        onClick={() => setDocTab('locked')}
        className={`flex-1 md:flex-none flex items-center justify-center gap-1.5 px-3 sm:px-4 min-h-[44px] md:min-h-[36px] rounded-lg text-[13px] font-semibold transition-colors ${docTab === 'locked' ? 'bg-white text-black' : 'text-[#9A9A9A] hoverable:text-white'}`}
        data-testid="doc-tab-locked">
        <Lock className="w-3.5 h-3.5" /> {t('dashboard:doc_tabs.locked', { count: archivedDocs.length })}
      </button>
    </div>
  ) : null);

  /* COS-I — EL SWIPE ENTRE PESTAÑAS. Ésta es la PRIMERA pestaña del orden, así
     que hacia la derecha no tiene vecina: el propio hook se encarga (sin dar la
     vuelta), aquí no hay nada que declarar.

     EL CENSO DE CAPAS DE ESTA PANTALLA, completo y en un solo sitio: con
     cualquiera de ellas encima el dedo es de la capa y no nace ningún gesto.
     Se lee al `touchstart`, así que un gesto ya en vuelo termina en paz.
     `autoSplitModalSuppressed` no aparece porque su cortina sólo existe con
     `autoSplitQueue.length > 0`, que ya está. El banner de suscripción tampoco:
     no es una capa —no bloquea nada, y vive FUERA de este contenedor.

     PAGER-2 — EL CENSO SE QUEDA AQUÍ, EL GESTO NO. Esta pantalla montaba el
     gesto de pestañas y esparcía sus props en su contenedor; eran cuatro juegos
     de listeners táctiles, uno por raíz. Ahora lo monta UNA vez el carril
     (`components/PagerRaices.js`) y lo único que sube de aquí es este booleano,
     que sólo el carril puede necesitar y sólo él sabe cuándo. Sin carril —web,
     montaje suelto, arnés— `useReportarCapa` no hace absolutamente nada. */
  const capaEncima = isDragging || !!previewImage || showPackModal
    || autoSplitQueue.length > 0 || !!insufficientCredits || !!duplicateArchived
    || !!langModal || !!convertModal || showRestoreVeil || guestDocExpired
    || packSuccess || !!renombrando || fotosPanel.length > 0;
  useReportarCapa(capaEncima);
  // dashboard-rediseno: la Preparacion de cada tarjeta (el mismo numero que Analiticas) y el
  // reacomodo de la rejilla al irse una tarjeta (A15). Los dos van antes de la salida temprana
  // del esqueleto: son ganchos.
  const preparacion = usePreparacionDelPanel(user);
  const rejillaRef = useRef(null);
  useReacomodo(rejillaRef, entradasDelPanel.map((e) => (e.tipo === 'padre' ? `p:${e.marca}` : e.id)).join('|'));

  // i18n cargando O dato sin cache fresco → la silueta del panel (F4.3b:
  // nunca spinner, nunca dato viejo). dashboard-rediseno (I1): la silueta copia el acomodo nuevo.
  if (!ready || loading) return <DashboardSkeleton />;

  // dashboard-rediseno: "sin documentos" es la zona grande (con 0 documentos la zona pasa a ser
  // lo principal). Una padre con todas sus partes bloqueadas cuenta como documento a la vista.
  const sinDocumentos = visibleDocs.length === 0 && entradasDelPanel.length === 0;
  // Study se oculta con 0 documentos; con material de un grupo que se pueda estudiar se queda
  // (la regla de hoy de "Estudiar" con grupos, t7-3 remate 8, no cambia), y durante I2 tambien
  // (no se sabe si hay documentos).
  const conStudy = fallaDeCarga || !sinDocumentos || hayPublicacionParaEstudiar;
  const enTope = !!(planInfo && planInfo.documents_used >= planInfo.limits.max_docs);
  const campoDeYoutube = {
    valor: youtubeUrl,
    alCambiar: (v) => { setYoutubeUrl(v); if (errorYoutube) setErrorYoutube(null); },
    alImportar: () => importFromYoutube(youtubeUrl),
    cargando: youtubeLoading,
    campoApagado: youtubeLoading || isOffline || enTope,
    botonApagado: youtubeLoading || isOffline || !youtubeUrl.trim() || enTope,
    error: errorYoutube,
    alTocarConTope: () => {
      if (!atDocLimit || isOffline || youtubeLoading) return;
      notifyDocLimit();
    },
    tituloDelBoton: isOffline ? t('common:offline.need_connection') : undefined,
  };
  // El toque en "Upload" con el tope lleno avisa (R-13): devuelve true si se encargo.
  const tocarSubirConTope = () => {
    if (!atDocLimit || isOffline || uploading) return false;
    notifyDocLimit();
    return true;
  };

  // dashboard-rediseno (4.5): la tarjeta de UN documento, con la anatomia fija. Es una FUNCION que
  // devuelve elementos y no un componente (la leccion de #406: un componente declarado aqui dentro
  // se re-monta en cada vuelta y se traga los clics). `gemela` ("movil" o "escritorio") pinta solo
  // esa gemela, para las partes de un documento dividido; sin ella, las dos, como siempre.
  const listaDelPanel = (lista, gemela, extraDe, temaDe, cuantosTemas) => lista.map((doc) => {
    // series-7 (#490): una entrada de partes es la tarjeta padre. Sus hijas vuelven por esta
    // misma funcion.
    if (doc.tipo === 'padre' && Array.isArray(doc.hijas)) {
      return (
        <TarjetaPadreDePartes key={`partes-${doc.marca}`} padre={doc} t={t}
          pintarHija={(hija, gem, extra, tema, cuantos) => listaDelPanel([hija], gem, extra, tema, cuantos)}
          onBorrarTodo={handleBorrarPartes} sinConexion={isOffline} userId={user?.id}
          pedirTemas={pedirTemasDeDocumentos} enApp={IS_APP_BUILD}
          onRenombrar={abrirRenombrarPadre} alIrAPendientes={(m) => navigate(`${PENDING_ROUTE}?group=${m}`)}
          pendientesDelGrupo={pendingByParent[doc.marca] ? pendingByParent[doc.marca].pending_count : 0}
          menuApagado={IS_APP_BUILD && !presenceRead} />
      );
    }
    // F6.7b: la fila de la tabla de estados que le toca a ESTA tarjeta (sin cambio).
    const delivery = computeCardDelivery(doc, presenceById.get(doc.id), {
      offline: isOffline, appBuild: IS_APP_BUILD, presenceRead,
    });
    const isPreparing = delivery === CARD_DELIVERY.PREPARING;
    // #412: lo que baja al telefono se pinta como procesando (statusPintado) y #421: toda la
    // tarjeta junta (sinDisfraz). La logica no cambia; ver los letreros de antes en el historial.
    const bajando = delivery === CARD_DELIVERY.DOWNLOADING;
    const statusPintado = bajando ? 'processing' : doc.status;
    const sinDisfraz = !bajando;
    // #412, ajuste 1 (A18): ni un cuadro de "listo" antes de preguntarle al telefono.
    const presenciaPendiente = IS_APP_BUILD && !presenceRead
      && (doc.status === 'ready' || doc.status === 'partial');
    // #367 / #506: el escaneado (o texto grande) que espera que el alumno decida.
    const porConfirmar = doc.status === 'processing' && doc.lectura_estado === 'por_confirmar';
    // #505: una parte bloqueada dentro de su padre.
    const parteBloqueada = !!gemela && doc.status === 'archived';
    const parte = !!gemela;
    const avisarSinConexion = () => toast.error(t('common:offline.need_connection'), { id: 'sin-conexion-borrar' });
    const navigateIfReady = () => {
      if (porConfirmar) { abrirTroceoDePlaceholder(doc); return; }
      if (parteBloqueada) { toast.error(t('dashboard:partes_padre.parte_bloqueada_aviso'), { id: 'parte-bloqueada' }); return; }
      if (isPreparing || bajando) { handleDeliveryRetry(doc.id); return; }
      if (doc.status === 'ready' || doc.status === 'partial') { setJustProcessedIds(prev => { const s = new Set(prev); s.delete(doc.id); return s; }); localStorage.setItem('sb_doc_studied_' + doc.id, '1'); navigate(`/study?doc=${doc.id}`); }
    };
    const abrirConTeclado = (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      abrirTroceoDePlaceholder(doc);
    };
    // #165: el letrero de la tarjeta roja, traducido (sin cambio).
    const codigoEnComun = doc.error_code && typeof i18n?.exists === 'function'
      && i18n.exists(`common:errors.${doc.error_code}`, { ...(doc.error_detail || {}), count: doc.error_detail?.credits_required });
    const textoDeError = (doc.error_code && CODIGOS_DE_ERROR_TRADUCIDOS.has(doc.error_code))
      ? t(`dashboard:errors.${doc.error_code}`)
      : codigoEnComun
        ? t(`common:errors.${doc.error_code}`, { ...(doc.error_detail || {}), count: doc.error_detail?.credits_required })
        : doc.error_message;
    const avisoLectura = doc.status === 'processing' && !!doc.aviso_lectura;
    const esPpt = doc.file_type === 'pptx' || doc.file_type === 'ppt';
    const cardCursor = (porConfirmar || isPreparing || statusPintado === 'ready' || statusPintado === 'partial') ? 'cursor-pointer hoverable:border-[#3A2414] active:border-[#3A2414] active:bg-[#1B1B1B] active:duration-0 transition-colors' : '';
    // #154 / #370a / #405: la fila y el reloj (sin cambio).
    const sinReloj = doc.status === 'processing' && !doc.processing_started_at;
    const esperandoTurno = doc.status === 'processing' && doc.esperando === true;
    const enFila = sinReloj || esperandoTurno;
    const proc = (doc.status === 'processing' && !sinReloj) ? computeHonesto(doc, nowTick, localStartRef, honestoRef) : null;
    const procPctCrudo = processProgress[doc.id] === 100 ? 100 : (proc ? proc.pct : 5);
    const procPctVivo = (doc.lectura_estado === 'leyendo' && procPctCrudo !== 100 && !(proc && proc.etapa)) ? Math.min(procPctCrudo, 20) : procPctCrudo;
    const congelador = esperandoTurno || bajando;
    const quieto = pctQuietoRef.current;
    if (!congelador || quieto[doc.id] == null) quieto[doc.id] = procPctVivo;
    const procPct = (congelador && procPctCrudo !== 100) ? quieto[doc.id] : procPctVivo;
    const sinReintento = doc.lectura === 'ojos' && !doc.text_length;
    const procStale = !!(proc && proc.stale) && doc.lectura_estado !== 'por_confirmar' && !bajando;
    const reintentando = doc.status === 'error' && !!processing[doc.id];
    const conPista = (statusPintado === 'ready' || statusPintado === 'partial')
      && justProcessedIds.has(doc.id) && !localStorage.getItem('sb_doc_studied_' + doc.id);
    const conFiguras = user?.plan === 'pro' && doc.image_count > 0;
    const sinFlashcards = sinDisfraz && doc.status === 'ready' && (doc.flashcard_count || 0) === 0 && (doc.question_count || 0) > 0;
    const terminado = sinDisfraz && !isPreparing && (doc.status === 'ready' || doc.status === 'partial');
    const despidiendose = saliendo.has(doc.id);
    // El estado que pinta la tarjeta, para `data-estado` (la medicion de Chrome lo lee).
    const estado = despidiendose ? 'A15' : presenciaPendiente ? 'A18' : parteBloqueada ? 'B3'
      : isPreparing ? 'A16' : porConfirmar ? 'A5' : statusPintado === 'processing'
        ? (enFila ? 'A2' : procStale ? 'A3' : avisoLectura ? 'A4' : 'A1')
        : doc.status === 'uploaded' ? 'A9'
          : doc.status === 'error' ? (reintentando ? 'A8' : sinReintento ? 'A7' : 'A6')
            : doc.status === 'partial' ? 'A10'
              : sinFlashcards ? 'A11' : conPista ? 'A12' : conFiguras ? 'A14' : 'A13';

    // ── El encabezado: el icono del tipo, el titulo (o "Part n of N") y el menu ──
    const claveTipo = claveDelTipo(doc);
    const tipo = nombreDelTipo(claveTipo, t, doc);
    const totalDeLaParte = Number(doc.total_chunks) || (leerNombreDeParte(doc.filename) || {}).total || null;
    const titulo = parte
      ? t('dashboard:panel.parte_de', { n: numeroDeParte(doc), total: totalDeLaParte })
      : nombreConCortes(doc.filename);
    const conConteos = sinDisfraz && !isPreparing && (doc.status === 'ready' || doc.status === 'partial');
    const conteos = conConteos ? conteosDe(doc.question_count || 0, doc.flashcard_count || 0, t) : [];
    // estilos-1 (4.4): con un examen, su nombre corto en naranja en lugar del tipo (en las partes, nada cambia).
    const cortoDelEstilo = parte ? null : nombreCortoDelDocumento(estilos, doc);
    const renglonUno = parte
      ? (conteos.length > 0 ? conteos.join(SEPARADOR_DEL_RENGLON) : tipo)
      : cortoDelEstilo ? renglonConEstilo(cortoDelEstilo, conteos)
        : renglonDelTipo(tipo, doc.question_count || 0, doc.flashcard_count || 0, t, conConteos);

    const menuDe = (sufijo) => {
      if (despidiendose) return <span className="w-10 h-10 -mr-2 shrink-0" aria-hidden="true" />;
      const opciones = [];
      // tutor-2 (D.1): antes de "Rename", para los que el tutor puede leer (los de `documentosElegibles`), sin partes.
      if (verTutorEnElPanel && !parte && documentosElegibles([doc]).length > 0) {
        opciones.push({
          testid: `tutor-${doc.id}${sufijo}`, texto: t('study:tutor.panel.ask_tutor'), icono: Sparkles,
          alTocar: () => navigate(`/tutor?doc=${encodeURIComponent(doc.id)}`),
        });
      }
      if (terminado && !parte) {
        opciones.push({
          testid: `renombrar-${doc.id}${sufijo}`, texto: t('dashboard:panel.renombrar'), icono: Pencil,
          alTocar: (ref) => abrirRenombrar({ documentId: doc.id, nombre: doc.filename, volverA: ref }),
        });
      }
      opciones.push({
        testid: `delete-${doc.id}${sufijo}`, texto: parte ? t('dashboard:panel.borrar_parte') : t('dashboard:pending.delete_confirm'),
        icono: Trash2, peligro: true, separadorAntes: opciones.length > 0, alTocar: () => handleDelete(doc.id),
      });
      return (
        <MenuDeTresPuntos testid={`menu-${doc.id}${sufijo}`}
          etiqueta={t(parte ? 'dashboard:panel.opciones_parte' : 'dashboard:panel.opciones_documento')}
          opciones={opciones} apagado={presenciaPendiente} sinRed={boteSinConexion} alSinRed={avisarSinConexion}
          tactil={sufijo === '-mobile'} />
      );
    };

    // ── El detalle: el renglon del tipo y el renglon 2, o un aviso largo de dos renglones ──
    const detalleDe = (sufijo) => {
      if (isPreparing) {
        return (
          <p className={`${AVISO_LARGO} text-[#9A9A9A]`} data-testid={`preparing-notice-${doc.id}${sufijo}`}>
            {t('common:offline.preparing_local')}
          </p>
        );
      }
      if (doc.status === 'error' && textoDeError) {
        // A6 y A7: el motivo en rojo ocupa los dos renglones; si no cabe se corta con "..." y se
        // lee completo en un panel flotante debajo de la tarjeta al tocarlo.
        return (
          <Flotante testid={`panel-motivo-${doc.id}${sufijo}`}
            boton={(p) => (
              /* Tercera vuelta (D15): el corte de dos renglones esconde lo que se sale de su caja, y
                 con el se comia el area de 44. El corte va en el texto de adentro y el boton (36 de alto)
                 lleva el area: 4 arriba y 4 abajo, dentro de los huecos de 10 y 12 de la tarjeta. */
              <button type="button" {...p} title={textoDeError}
                className="relative block h-9 text-left w-full cursor-pointer"
                data-testid={`doc-error-${doc.id}${sufijo}`}><AreaDe44 /><span className={`${AVISO_LARGO} text-[#F87171]`}>{textoDeError}</span></button>
            )}
            contenido={() => <p className="text-[13px] leading-[18px] text-[#F87171] break-words">{textoDeError}</p>} />
        );
      }
      if (avisoLectura) {
        // A4: el aviso del escaneado; la frase que no cabe (la de la letra a mano) va en el panel
        // del boton de informacion, sin repetir lo que ya dice la tarjeta.
        const [enLaTarjeta, enElPanel] = partirAvisoDeLectura(t('dashboard:fotos.banner_lectura'));
        return (
          <div className="flex items-start gap-1.5 min-w-0">
            <p className={`${AVISO_LARGO} text-[#F4F4F3] flex-1 min-w-0`} data-testid={`doc-lectura-${doc.id}${sufijo}`}>{enLaTarjeta}</p>
            {enElPanel ? (
              <Flotante testid={`panel-lectura-${doc.id}${sufijo}`}
                boton={(p) => (
                  <button type="button" {...p} aria-label={t('dashboard:panel.mas_info')}
                    className="relative w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[#9A9A9A] hoverable:text-white"
                    data-testid={`info-lectura-${doc.id}${sufijo}`}>
                    {/* Segunda vuelta (D16): sube solo 4 px, asi no pisa el area del menu de tres puntos. */}
                    <AreaDe44 arriba={4} />
                    <Info className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
                contenido={() => <p className="text-[13px] leading-[18px] text-[#D4D4D4]">{enElPanel}</p>} />
            ) : null}
          </div>
        );
      }
      // A14 (solo Pro): la liga "N images" abre la tira de miniaturas en un panel flotante
      // debajo de la tarjeta, no dentro (regla 4). Las figuras se ven en TODOS los estados (#396):
      // si el renglon 2 ya lleva otra cosa (A10, A11, A12, B4), va compacta al final (el icono y
      // el numero, con "N images" para el lector de pantalla).
      const figurasDe = (compacta) => {
        const figuras = docImages[doc.id] || [];
        const nota = (sinDisfraz && doc.status === 'ready') ? 'dashboard:image_preview.figures_used' : 'dashboard:image_preview.ai_analysis_note';
        return (
            <Flotante testid={`image-preview-${doc.id}${sufijo}`} alAbrir={() => cargarFiguras(doc.id)}
              boton={(p) => (
                <button type="button" {...p} className={`${LIGA_RENGLON}${compacta ? ' shrink-0' : ''}`}
                  aria-label={compacta ? t('dashboard:doc_card.image_count', { count: doc.image_count }) : undefined}
                  data-testid={`toggle-images-${doc.id}${sufijo}`}>
                  <AreaDe44 abajo={BAJA_DEL_RENGLON_DOS} />
                  {loadingImages[doc.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden="true" /> : <ImageIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
                  {compacta
                    ? <span className="tabular-nums" aria-hidden="true">{doc.image_count}</span>
                    : <span className="truncate">{t('dashboard:doc_card.image_count', { count: doc.image_count })}</span>}
                </button>
              )}
              contenido={() => (figuras.length > 0 ? (
                <>
                  <p className="text-[13px] leading-[18px] text-[#9A9A9A] mb-3">{t(nota)}</p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {figuras.map((img, idx) => (
                      <button key={idx} type="button" onClick={() => setPreviewImage({ ...img, esPpt })}
                        className="relative w-20 h-20 shrink-0 rounded-lg overflow-hidden bg-[#141414] border border-[#2C2C2C] hoverable:border-[#F27A2E]"
                        data-testid={`image-thumb-${doc.id}${sufijo}-${idx}`}>
                        <img src={`data:image/jpeg;base64,${img.base64}`} alt={t(esPpt ? 'dashboard:image_preview.slide_alt' : 'dashboard:image_preview.page_alt', { page: img.page })}
                          className="w-full h-full object-cover" />
                        {img.page > 0 && (
                          <span className="absolute bottom-1 right-1 text-[12px] leading-4 bg-black/70 text-white px-1.5 py-0.5 rounded">p.{img.page}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              ) : loadingImages[doc.id] ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#9A9A9A]" aria-hidden="true" />
              ) : (
                <p className="text-[13px] leading-[18px] text-[#6A6A6A]">{t('dashboard:image_preview.previews_available')}</p>
              ))} />
        );
      };
      let renglonDos = null;
      let conBoton = false;
      let esParcial = false;
      let conFigurasSolas = false;
      if (parteBloqueada) {
        renglonDos = (
          <span className="truncate" data-testid={`parte-bloqueada-${doc.id}${sufijo}`}>
            {t('dashboard:partes_padre.parte_bloqueada', { count: Array.isArray(doc.topics) ? doc.topics.length : 0 })}
          </span>
        );
      } else if (temaDe) {
        // B4 (#501): la hija que entro a la busqueda por tema dice cual, con el tema en naranja.
        renglonDos = (
          <span className="truncate" data-testid={`doc-tema-${doc.id}${sufijo}`}>
            {t('dashboard:tarjeta_plegable.tema_coincide', { topic: temaDe, count: cuantosTemasDelResumen(temaDe, cuantosTemas) })}
          </span>
        );
      } else if (sinDisfraz && doc.status === 'partial') {
        // A10: el chip naranja chico del parcial y, junto, el boton chico que reintenta solo lo
        // que fallo (#421: sale con el chip, no antes). Tercera vuelta (D9): el renglon es
        // `RenglonDelParcial`, que deja el boton solo con su icono cuando el chip entero y el boton
        // con texto no caben juntos (medidos de verdad, no por un ancho fijo).
        esParcial = true;
      } else if (sinFlashcards) {
        // A11: el boton chico "Generate Flashcards"; al tocarlo se apaga y dice "Generating...".
        conBoton = true;
        renglonDos = (
          <button type="button" onClick={(e) => { e.stopPropagation(); handleRegenerateFlashcards(doc.id); }} disabled={regeneratingFlashcards[doc.id]}
            className={BOTON_CHICO} data-testid={`generate-flashcards-${doc.id}${sufijo}`}>
            <AreaDe44 abajo={BAJA_DEL_RENGLON_DOS} />
            {regeneratingFlashcards[doc.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> : <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />}
            {regeneratingFlashcards[doc.id] ? t('dashboard:doc_card.generating_flashcards_btn') : t('dashboard:doc_card.generate_flashcards_btn')}
          </button>
        );
      } else if (conPista) {
        // A12: la pista se queda, en naranja, hasta la primera vez que se abre (REMATE R3 de #412:
        // lee `statusPintado`).
        renglonDos = (
          <span className={LIGA_RENGLON} data-testid={`first-doc-study-prompt${sufijo}`}>
            <span className="truncate">{t(esTactil ? 'dashboard:panel.toca_para_estudiar' : 'dashboard:doc_card.click_to_study')}</span>
            {esTactil ? null : <ArrowRight className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
          </span>
        );
      } else if (conFiguras) {
        renglonDos = figurasDe(false);
        conFigurasSolas = true;
      }
      if (conFiguras && renglonDos && !parteBloqueada && !conFigurasSolas) {
        renglonDos = (<>{renglonDos}{figurasDe(true)}</>);
      }
      if (esParcial) {
        const chipTexto = doc.question_count === 0 ? t('dashboard:doc_card.status.partial_questions_failed') : t('dashboard:doc_card.status.partial_flashcards_failed');
        return (
          <>
            <p className={`${RENGLON} pointer-events-none`} data-testid={`doc-tipo-${doc.id}${sufijo}`}>{renglonUno}</p>
            <RenglonDelParcial testid={`tarjeta-${doc.id}-renglon2${sufijo}`}
              chipTexto={chipTexto} chipTestid={`status-${doc.id}${sufijo}`}
              botonTexto={t('dashboard:doc_card.retry_btn')} botonTestid={`retry-partial-${doc.id}${sufijo}`}
              alTocar={(e) => { e.stopPropagation(); handleRetry(doc.id); }} apagado={processing[doc.id]} cargando={processing[doc.id]}>
              {conFiguras ? figurasDe(true) : null}
            </RenglonDelParcial>
          </>
        );
      }
      // Quinta vuelta (D20): con un boton chico en el renglon 2, su area de 44 sube sobre el renglon del
      // tipo; ese renglon (texto sin accion) deja pasar el toque, asi el area queda entera. El toque en el
      // texto sigue llegando a la tarjeta.
      return (
        <>
          <p className={conBoton ? `${RENGLON} pointer-events-none` : RENGLON} data-testid={`doc-tipo-${doc.id}${sufijo}`}>{renglonUno}</p>
          <div className={conBoton ? RENGLON_DOS_CON_BOTON : RENGLON_DOS} data-testid={`tarjeta-${doc.id}-renglon2${sufijo}`}>{renglonDos}</div>
        </>
      );
    };

    // ── El pie: el medidor a la izquierda y un solo boton a la derecha ──
    const chipDeProceso = (sufijo) => {
      const texto = enFila ? t('dashboard:doc_card.status.queued')
        : procStale ? t('dashboard:doc_card.status.taking_longer')
          : t(`dashboard:doc_card.status.${STATUS_KEY_MAP[statusPintado] || statusPintado}`);
      const tono = enFila ? 'gris' : procStale ? 'naranja' : 'neutro';
      const icono = enFila || procStale ? 'reloj' : 'giro';
      // #406 / B1: el chip abraza su texto y el hueco lo guarda un envoltorio invisible del ancho
      // del texto mas largo de los dos idiomas: cambiar de texto no empuja nada.
      return (
        <span className="inline-flex items-center min-w-[160px]" data-testid={`status-hueco-${doc.id}${sufijo}`}>
          <ChipDeEstado tono={tono} icono={icono} testid={`status-${doc.id}${sufijo}`}>{texto}</ChipDeEstado>
        </span>
      );
    };
    const pieDe = (sufijo) => {
      if (presenciaPendiente) {
        // A18: un rectangulo gris de carga, sin texto, sin barra y sin boton.
        return (
          <div className="flex-1 min-w-0 flex items-center" data-testid={`status-esperando-${doc.id}${sufijo}`}>
            <SkeletonBlock className="h-[22px] w-28 rounded-full" />
          </div>
        );
      }
      if (parteBloqueada) return null;
      if (isPreparing) {
        // A16: se ve como procesando (regla 6) y el boton reintenta la entrega.
        return (
          <>
            <Medidor izquierda={<ChipDeEstado tono="neutro" icono="giro" testid={`status-${doc.id}${sufijo}`}>{t('dashboard:doc_card.status.processing')}</ChipDeEstado>}
              pct={procPct} valor={`${procPct}%`} color={COLOR_BARRA_PROCESANDO} testid={`progreso-${doc.id}${sufijo}`} />
            <button type="button" onClick={(e) => { e.stopPropagation(); handleDeliveryRetry(doc.id); }}
              className={BOTON_PIE_SECUNDARIO} data-testid={`retry-hint-${doc.id}${sufijo}`}>
              {t('dashboard:doc_card.tap_to_retry')}
            </button>
          </>
        );
      }
      if (porConfirmar || doc.status === 'uploaded') {
        // A5 y A9: el chip naranja suave y el boton naranja (A5 abre la ventana; A9 procesa, P-F9b).
        return (
          <>
            <div className="flex-1 min-w-0 flex items-center">
              <ChipDeEstado tono="naranja" testid={`status-${doc.id}${sufijo}`}>{t('dashboard:doc_card.status.uploaded')}</ChipDeEstado>
            </div>
            {porConfirmar ? (
              <button type="button" onClick={(e) => { e.stopPropagation(); abrirTroceoDePlaceholder(doc); }}
                className={BOTON_PIE_PRINCIPAL} data-testid={`por-confirmar-process-${doc.id}${sufijo}`}>
                {t(esTactil ? 'dashboard:doc_card.actions.tap_to_process' : 'dashboard:doc_card.actions.click_to_process')}
              </button>
            ) : (
              <button type="button" onClick={(e) => { e.stopPropagation(); handleProcess(doc.id); }} disabled={processing[doc.id]}
                className={`${BOTON_PIE_PRINCIPAL} min-w-[96px]`} data-testid={`process-${doc.id}${sufijo}`}>
                {processing[doc.id] ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : t('dashboard:doc_card.actions.process')}
              </button>
            )}
          </>
        );
      }
      if (statusPintado === 'processing') {
        // A1 a A4: el chip de estado y, si ya empezo, el valor y la barra (#154: en fila sin
        // reloj no hay barra; #405: esperando turno la barra se queda quieta y palida).
        const conBarra = !sinReloj;
        const leyendoPaginas = proc && proc.etapa === 'leyendo' && proc.total > 0 && !bajando;
        return (
          <Medidor izquierda={chipDeProceso(sufijo)}
            pct={conBarra ? procPct : null}
            valor={conBarra ? (leyendoPaginas ? `${proc.n} / ${proc.total}` : `${procPct}%`) : null}
            color={esperandoTurno ? COLOR_BARRA_QUIETA : COLOR_BARRA_PROCESANDO}
            testid={`progreso-${doc.id}${sufijo}`} />
        );
      }
      if (doc.status === 'error') {
        // A6 a A8: el chip rojo y "Retry" (sin reintento, A7, sin boton). Reintentando, el boton
        // se apaga y su icono gira sin cambiar de ancho.
        return (
          <>
            <div className="flex-1 min-w-0 flex items-center">
              <ChipDeEstado tono="rojo" icono="triangulo" testid={`status-${doc.id}${sufijo}`}>{t('dashboard:doc_card.status.error')}</ChipDeEstado>
            </div>
            {!sinReintento ? (
              <button type="button" onClick={(e) => { e.stopPropagation(); handleRetry(doc.id); }} disabled={processing[doc.id]}
                className={BOTON_PIE_SECUNDARIO} data-testid={`retry-${doc.id}${sufijo}`}>
                {processing[doc.id] ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <RotateCw className="w-4 h-4" aria-hidden="true" />}
                {t('dashboard:doc_card.retry_btn')}
              </button>
            ) : null}
          </>
        );
      }
      if (doc.status === 'ready' || doc.status === 'partial') {
        // A10 a A14: la Preparacion SOLO si ya existe, y "View topics" a la derecha.
        return (
          <>
            <MedidorDePreparacion valor={preparacion.documentos[doc.id]} t={t} sufijoId={`${doc.id}${sufijo}`} />
            {extraDe || verTemasDoc(sufijo)}
          </>
        );
      }
      return null;
    };
    // remates-1-3-0b (P3, #514): "View topics" de un documento normal listo o parcial; abre la
    // MISMA ventana. Nada se pide al cargar.
    function verTemasDoc(sufijo) {
      if (gemela || !sinDisfraz || !(doc.status === 'ready' || doc.status === 'partial')) return null;
      return (
        <button type="button"
          onClick={(e) => { e.stopPropagation(); volverATemas.current = e.currentTarget; setTemasDeDoc(doc); }}
          className={BOTON_PIE_SECUNDARIO} data-testid={`doc-ver-temas-${doc.id}${sufijo}`}>
          <ListChecks className={ICONO_VER_TEMAS} aria-hidden="true" />
          {t('dashboard:partes_padre.ver_temas')}
        </button>
      );
    }

    // Cada gemela se esconde en el ancho que no es suyo con su propia clase (sin envoltura): bajo la
    // marca `doc-<id>` los hijos son las dos tarjetas, como antes del rediseno.
    const visibilidad = (sufijo) => (gemela ? '' : sufijo === '-mobile' ? 'md:hidden' : 'hidden md:flex');
    const tarjetaDe = (sufijo) => (
      <CascaronDeTarjeta id={doc.id} sufijo={sufijo} estado={estado} interna={parte} parte={parte}
        atenuada={parteBloqueada}
        icono={parteBloqueada ? <Lock className={parte ? TRAZO_ICONO_INTERNA : TRAZO_ICONO} aria-hidden="true" /> : <IconoDelTipo clave={claveTipo} className={parte ? TRAZO_ICONO_INTERNA : TRAZO_ICONO} />}
        rotuloIcono={parteBloqueada ? undefined : t(ROTULO_DEL_TIPO[tipoDeMaterial(doc)] || ROTULO_DEL_TIPO.documento)}
        testidIcono={parteBloqueada ? undefined : `doc-icono-${doc.id}${sufijo}`} tipoIcono={parteBloqueada ? undefined : tipoDeMaterial(doc)}
        titulo={titulo} tituloTitle={doc.filename}
        menu={menuDe(sufijo)}
        detalle={detalleDe(sufijo)}
        pie={pieDe(sufijo)}
        className={`${visibilidad(sufijo)} ${cardCursor}`.trim()}
        style={(porConfirmar || doc.status === 'uploaded') ? { borderColor: BORDE_POR_PROCESAR } : undefined}
        onClick={navigateIfReady}
        role={(isPreparing || porConfirmar) ? 'button' : undefined}
        tabIndex={porConfirmar ? 0 : undefined}
        onKeyDown={porConfirmar ? abrirConTeclado : undefined}
        ariaLabel={isPreparing ? t('dashboard:doc_card.preparing_retry_aria') : undefined}
        ariaBusy={isPreparing && !!retryingDoc[doc.id] ? true : undefined} />
    );
    // series-7 (#490): con gemela, solo la que toca; sin ella, las dos.
    return (
      <SalidaSuave key={doc.id} testid={`doc-${doc.id}`} celda={doc.id} saliendo={despidiendose}>
        {gemela !== 'escritorio' && tarjetaDe('-mobile')}
        {gemela !== 'movil' && tarjetaDe('')}
      </SalidaSuave>
    );
  });

  // dashboard-rediseno (E2): la tarjeta de un documento bloqueado, atenuada, con candado, su
  // renglon de tipo y "Locked. Upgrade to unlock."; sin Preparacion ni boton. Su menu: Delete.
  const tarjetaBloqueada = (doc) => (
    <SalidaSuave key={doc.id} celda={doc.id} saliendo={saliendo.has(doc.id)} testid={`locked-doc-${doc.id}`}>
      <CascaronDeTarjeta id={doc.id} estado="E2" atenuada
        icono={<Lock className={TRAZO_ICONO} aria-hidden="true" />}
        titulo={nombreConCortes(doc.filename)} tituloTitle={doc.filename}
        menu={saliendo.has(doc.id) ? <span className="w-10 h-10 -mr-2 shrink-0" aria-hidden="true" /> : (
          <MenuDeTresPuntos testid={`menu-${doc.id}`} etiqueta={t('dashboard:panel.opciones_documento')} tactil
            opciones={[{ testid: `delete-${doc.id}`, texto: t('dashboard:pending.delete_confirm'), icono: Trash2, peligro: true, alTocar: () => handleDelete(doc.id) }]}
            sinRed={boteSinConexion} alSinRed={() => toast.error(t('common:offline.need_connection'), { id: 'sin-conexion-borrar' })} />
        )}
        detalle={(
          <>
            <p className={RENGLON}>{nombreCortoDelDocumento(estilos, doc) ? renglonConEstilo(nombreCortoDelDocumento(estilos, doc)) : nombreDelTipo(claveDelTipo(doc), t)}</p>
            <p className={`${RENGLON} group-data-[estrecha]/tarjeta:text-[12px]`} title={t('dashboard:locked_section.card_hint')}
              data-testid={`status-${doc.id}`}>{t('dashboard:locked_section.card_hint')}</p>
          </>
        )}
        pie={null} />
    </SalidaSuave>
  );

  return (
    <>
    {/* F4.3d: columna de alto completo en TODOS los breakpoints: el chrome (saludo, avisos,
        tarjeta para agregar material) queda fijo y SOLO la lista de documentos se desliza. T2:
        en la app llega hasta el FONDO (PANE_FULL_BLEED_HEIGHT_CLASS). dashboard-rediseno: el
        contenido va centrado con ancho maximo de 1200, y entre bloques 18 px en el telefono y
        28 desde 768. */}
    <div className={`${CHROME_VGAP} animate-fade-in-up relative ${PANE_FULL_BLEED_HEIGHT_CLASS} flex flex-col w-full max-w-[1200px] mx-auto`}
      data-testid="dashboard-page">

      {/* F1: la capa de arrastrar, sobre toda la pagina (la oye el listener de `document`). */}
      {isDragging && (
        <div className="fixed inset-0 z-50 bg-[#0A0A0A]/90 flex p-4 md:p-6"
          data-testid="drag-overlay">
          {/* Segunda vuelta (D17): el recuadro punteado cubre la pagina, como el PDF (pag. 50). */}
          <div className="flex-1 flex flex-col items-center justify-center rounded-[24px] p-12 text-center border-2 border-dashed border-[#F27A2E] pointer-events-none">
            <Upload className="w-16 h-16 text-[#F27A2E] mx-auto mb-4" />
            <h3 className="text-[22px] font-bold text-[#F4F4F3] mb-2">{t('dashboard:drag_overlay.title')}</h3>
            <p className="text-[14px] text-[#9A9A9A]">{t('dashboard:drag_overlay.formats')}</p>
          </div>
        </div>
      )}

      {/* LAPIDA del remate de post-t7-2c (R1): aqui vivia el cuadro de opinion (post-t7-2c E2);
          se fue a su pantalla propia, "Gana creditos gratis". */}

      {/* marca-2 (#741 y #742): el aviso de la mudanza, un renglon arriba del saludo. Solo en la web
          y solo en studybuddy.vc; en quizessa.com y en la app no pinta nada. */}
      <AvisoMudanza invitado={!!user?.is_guest} t={t} />

      {/* marca-3 (#736): el aviso de 30 dias del nombre nuevo, con su "x". En studybuddy.vc no (ahi va
          el de arriba); en quizessa.com y en la app, hasta AVISO_MARCA_HASTA o hasta que se cierra. */}
      <AvisoMarca t={t} />

      {/* Encabezado: el saludo y Study, el unico boton principal de la pantalla. */}
      <div className="flex items-center justify-between gap-3 shrink-0">
        <div className="min-w-0">
          {/* R-1: ademas de saludo, es el ancla neutra a la que va el foco cuando se cierra un
              aviso (ver `dismissPendingBanner`). */}
          <h1 ref={focoNeutroRef} tabIndex={-1}
            className="text-[22px] leading-7 md:text-[34px] md:leading-[42px] font-bold text-[#F4F4F3] focus:outline-none break-words" data-testid="dashboard-title">
            {user?.name ? t('dashboard:welcome.greeting_with_name', { name: user.name }) : t('dashboard:welcome.greeting_no_name')}
          </h1>
          <p className="hidden md:block text-[#9A9A9A] mt-1 text-[14px]">{t('dashboard:welcome.subtitle')}</p>
        </div>
        {conStudy && (
          <Button onClick={() => { setJustProcessedIds(new Set()); navigate('/study'); }} disabled={!puedeEstudiar}
            className="bg-[#F27A2E] hover:bg-[#F27A2E] active:bg-[#E86F22] active:scale-[0.98] active:duration-0 text-[#160A02] rounded-[14px] h-11 md:h-[52px] px-4 md:px-6 text-[14px] md:text-[15px] font-semibold shrink-0 disabled:opacity-45" data-testid="start-study-btn">
            <Play className="w-4 h-4 mr-1.5 fill-current" /> {t('dashboard:header.study_btn')}
          </Button>
        )}
      </div>

      {/* Hidden Turnstile container — must be renderable (not display:none) for token generation */}
      <div ref={turnstileRef} style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0 }} />

      {/* ── D. LOS AVISOS DE ARRIBA, entre el saludo y la tarjeta para agregar material ──
          Cada uno sale cuando sale hoy (misma logica); cambia su lugar y su aspecto. */}
      {user?.is_guest && (
        <AvisoDelPanel tono="naranja" icono={UserPlus} testid="guest-convert-banner"
          titulo={t('dashboard:guest_banner.title')} texto={t('dashboard:guest_banner.body')}
          acciones={(
            <button type="button" onClick={() => setConvertModal('save')} className={BOTON_AVISO_PRINCIPAL} data-testid="guest-convert-btn">
              {t('dashboard:guest_banner.cta')}
            </button>
          )} />
      )}

      {/* D2: el de creditos agotados, EL QUE GANA cuando los dos son elegibles (TANDA I delta;
          ver `zeroCreditsBannerVisible`), con su X por ciclo (B3). */}
      {zeroCreditsBannerVisible && (
        <AvisoDelPanel tono="naranja" icono={AlertCircle} testid="zero-credits-banner" accionesTestid="zero-credits-actions"
          titulo={t('dashboard:zero_credits_banner.title')} texto={t('dashboard:zero_credits_banner.body')}
          acciones={(
            <button type="button" onClick={() => setShowPackModal(true)} className={BOTON_AVISO_PRINCIPAL} data-testid="zero-credits-buy-btn">
              {t('dashboard:zero_credits_banner.cta')}
            </button>
          )}
          alCerrar={dismissZeroCreditsBanner} etiquetaCerrar={t('dashboard:downgrade_banner.dismiss_aria')} cerrarTestid="zero-credits-dismiss" />
      )}

      {/* D3: el limite de documentos, con "upgrade your plan" como liga naranja subrayada y su X
          de TANDA 30 (#276a). */}
      {enTope && !avisoLimiteCerrado && (
        <AvisoDelPanel tono="rojo" icono={AlertTriangle} testid="doc-limit-banner"
          texto={(
            <span className="text-[#F4F4F3] text-[14px] font-semibold break-normal">
              <Trans
                i18nKey={planInfo.documents_used > planInfo.limits.max_docs
                  ? 'dashboard:doc_limit_banner.over_limit_body'
                  : 'dashboard:doc_limit_banner.at_limit_body'}
                components={{
                  1: <button key="1" onClick={() => { navigate('/upgrade'); }} disabled={isOffline}
                    title={isOffline ? t('common:offline.need_connection') : undefined}
                    className="inline-block py-[13px] -my-[13px] underline text-[#F7A06A] font-semibold disabled:opacity-45 disabled:cursor-not-allowed" />,
                }}
              />
            </span>
          )}
          alCerrar={cerrarAvisoLimite} etiquetaCerrar={t('common:close')} cerrarTestid="doc-limit-banner-close" />
      )}

      {/* D4: la bajada de plan programada (falla en silencio; no pinta nada sin `show:true`). */}
      <DowngradeBanner />

      {/* El de pendientes que continuar: sale cuando sale hoy (y se aparta cuando el de creditos
          es elegible: TANDA I delta y N3), con su X de COS-31. */}
      {hasProcessable && !uploading && !pendingBannerSilenced && !zeroCreditsEligible && (
        <AvisoDelPanel tono="naranja" icono={Clock} testid="pending-reactivation-banner"
          titulo={t('dashboard:pending.reactivation_title', { count: processableGroupCount })}
          texto={t('dashboard:pending.reactivation_body')}
          acciones={(
            <button type="button" onClick={() => navigate('/pending')} className={BOTON_AVISO_PRINCIPAL} data-testid="pending-reactivation-cta">
              {t('dashboard:pending.reactivation_cta')}
            </button>
          )}
          alCerrar={dismissPendingBanner} etiquetaCerrar={t('dashboard:pending.reactivation_dismiss_aria')} cerrarTestid="pending-reactivation-dismiss" />
      )}

      {/* La tarjeta para agregar material (solo si hay documentos). */}
      {!sinDocumentos && (
        <TarjetaParaAgregar t={t}
          campo={campoDeYoutube}
          subida={{
            apagado: uploading || isOffline || atDocLimit, porTope: atDocLimit, subiendo: uploading,
            alSubir: handleUpload, inputRef: headerUploadInputRef, accept: ACCEPT_SUBIDA,
            alTocarConTope: tocarSubirConTope,
            tituloDeSubida: isOffline ? t('common:offline.need_connection') : (atDocLimit ? t('dashboard:header.upload_blocked_tooltip') : undefined),
          }}
          cola={visibleUploadQueue}
          estilo={estilos.on ? <SelectorDeEstilo t={t} idioma={i18n?.language} /> : null} />
      )}

      {/* La region de los documentos. ONE scroll per viewport en TODOS los breakpoints (F4.3d). */}
      <div className="flex flex-col gap-3 flex-1 min-h-0">

      {/* La fila de arriba de la lista: "Your documents" y el contador (rojo en el limite); con
          documentos bloqueados, las pestanas de E1 ocupan ese lugar y el contador se quita. */}
      {!sinDocumentos && !fallaDeCarga && (
        <div className={`flex ${archivedDocs.length > 0 ? 'flex-col md:flex-row md:items-center' : 'flex-row items-center'} justify-between gap-2 shrink-0`}
          data-testid="lista-fila">
          {/* Segunda vuelta (D5): sin pestanas, el contador va a la derecha del titulo en todo ancho (PDF pag. 5). */}
          <h2 className="text-[19px] font-semibold text-[#F4F4F3] min-w-0" data-testid="lista-titulo">{t('dashboard:panel.tus_documentos')}</h2>
          {archivedDocs.length > 0 ? renderDocTabs('flex w-full md:w-fit') : planInfo ? (
            <span className={`text-[13px] font-semibold whitespace-nowrap shrink-0 ${enTope ? 'text-[#F87171]' : 'text-[#9A9A9A]'}`} data-testid="doc-counter">
              {t('dashboard:plan_info.doc_counter', { used: planInfo.documents_used, max: planInfo.limits.max_docs })}
            </span>
          ) : null}
        </div>
      )}

      {fallaDeCarga ? (
        /* I2: el recuadro en lugar de la lista (y del aviso flotante de antes). */
        <RecuadroNoCargaron t={t} reintentando={reintentandoCarga} alReintentar={reintentarCarga} />
      ) : docTab === 'active' && sinDocumentos ? (
        <>
        {/* t7-1b (T4): sin documentos propios, los grupos del alumno van encima de la zona grande. */}
        {verGruposDelAlumno && gruposDelAlumno.length > 0 && (
          <div className={REJILLA_DEL_PANEL}>
            <GruposDelAlumno grupos={gruposParaPintar} t={t} idioma={i18n?.language}
              entregaDeCard={entregaDeCard} alReintentar={reintentarCard} preparacion={preparacion.publicaciones} />
          </div>
        )}
        {/* LA ZONA GRANDE (sin documentos): toda la zona acepta arrastrar archivos y abre el
            selector al hacer clic. F3: al subir el primer documento cambia a giro, "Uploading &
            processing...", el nombre del archivo y una barra. */}
        <div className={`rounded-[22px] border-[1.5px] border-dashed bg-[#121212] px-5 py-8 md:px-10 md:py-12 text-center ${uploading ? 'border-[#F27A2E]/60' : 'border-[#343434] hoverable:border-[#F27A2E]/60 active:border-[#F27A2E]/60 active:duration-0 cursor-pointer'}`}
          onClick={() => !uploading && fotosPanel.length === 0 && fileInputRef.current?.click()} data-testid="empty-docs">
          <input ref={fileInputRef} type="file" className="hidden" accept={ACCEPT_SUBIDA} multiple onChange={handleUpload}
            data-testid="upload-input" />
          {/* LAPIDA segunda vuelta (D18): aqui el panel de fotos reemplazaba a la zona mientras habia
              fotos (OCR-ALUMNO, s.C2); ahora va en su ventana (F5), al final del Panel. */}
          {uploading ? (
            <>
              <Loader2 className="w-14 h-14 text-[#F27A2E] mx-auto mb-4 animate-spin" />
              <h3 className="text-[22px] font-bold text-[#F4F4F3] mb-2">{t('dashboard:empty_state.uploading_title')}</h3>
              <div className="space-y-3 max-w-xs mx-auto mt-4">
                {visibleUploadQueue.map((item) => (
                  <div key={item.id} className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] text-[#9A9A9A] truncate flex-1">{item.name}</span>
                      {item.status === 'uploading' && <span className="text-[12px] text-[#D4D4D4] tabular-nums">{item.progress}%</span>}
                      {item.status === 'error' && <X className="w-4 h-4 text-[#F87171]" />}
                    </div>
                    {item.status === 'uploading' && (
                      <div className={`mt-1.5 ${PISTA}`}>
                        <div className="h-full rounded-full bg-[#D4D4D4] transition-[width] duration-300" style={{ width: `${item.progress}%` }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-[#1C130D] flex items-center justify-center mx-auto mb-4">
                <Upload className="w-8 h-8 text-[#F7A06A]" />
              </div>
              <h3 className="text-[22px] font-bold text-[#F4F4F3] mb-2">{t('dashboard:empty_state.drop_title')}</h3>
              {/* OCR-ALUMNO (§C1): la linea de formatos suma las fotos. */}
              <p className="text-[14px] text-[#9A9A9A] max-w-[520px] mx-auto">{t('dashboard:empty_state.formats_hint', { max: MAX_BATCH_DOCS, max_fotos: MAX_FOTOS })}</p>
              <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                className="mt-5 h-11 px-6 rounded-xl bg-[#F27A2E] text-[#160A02] text-[14px] font-semibold inline-flex items-center gap-2"
                data-testid="zona-elegir-archivos">
                {t('dashboard:panel.elegir_archivos')}
              </button>
              <p className="mt-5 text-[13px] text-[#6A6A6A]">{t('dashboard:panel.o_pega_link')}</p>
              <div className="mt-2 mx-auto max-w-[560px] text-left" onClick={(e) => e.stopPropagation()}>
                <CampoDeYoutube t={t} {...campoDeYoutube} alto="zona" />
                {/* estilos-1 (4.1): la misma pastilla debajo de los botones de la zona, solo con la perilla encendida. */}
                {estilos.on ? <div className="mt-3 flex min-w-0" data-testid="estilo-fila-zona"><SelectorDeEstilo t={t} idioma={i18n?.language} testid="estilo-pastilla-zona" /></div> : null}
              </div>
              <p className="mt-4 text-[13px] text-[#6A6A6A]" data-testid="char-limit">
                {t('dashboard:panel.hasta_50k')}
                {planInfo ? (
                  <>{SEPARADOR_DEL_RENGLON}<span data-testid="doc-counter">{t('dashboard:plan_info.doc_counter', { used: planInfo.documents_used, max: planInfo.limits.max_docs })}</span></>
                ) : null}
              </p>
            </>
          )}
        </div>
        </>
      ) : docTab === 'active' ? (
        <div
          className={`flex-1 min-h-[96px] md:min-h-0 overflow-y-auto pr-1 ${INNER_SCROLL_CLEARANCE_CLASS} dashboard-doc-scroll`}
          data-testid="dashboard-doc-list"
        >
          {/* La rejilla: los grupos primero y a lo ancho, el panel de fotos (si lo hay) y luego los
              documentos en el orden de hoy. El acomodo denso de la rejilla: un padre abierto a lo ancho no
              deja hueco. */}
          <div ref={rejillaRef} className={REJILLA_DEL_PANEL} data-testid="rejilla-documentos">
            {verGruposDelAlumno && (
              <GruposDelAlumno grupos={gruposParaPintar} t={t} idioma={i18n?.language}
                entregaDeCard={entregaDeCard} alReintentar={reintentarCard} preparacion={preparacion.publicaciones} />
            )}
            {/* LAPIDA segunda vuelta (D18): aqui iba el panel de fotos como tarjeta a lo ancho dentro de
                la rejilla (OCR-ALUMNO, PR #348); ahora va en su ventana (F5), al final del Panel. */}
            {listaDelPanel(entradasDelPanel, undefined, null, null)}
          </div>
          {/* LAPIDA post-t7-2c (D1, #640): aqui vivia la tarjeta de la app, ultima de la lista. */}
        </div>
      ) : (
        /* E2: la pestana de bloqueados: titulo, explicacion, la franja naranja con "Upgrade to
           unlock" y las tarjetas en la misma rejilla (borrar sigue la cascada normal). */
        <div className="flex-1 min-h-0 flex flex-col gap-3" data-testid="locked-list">
          <div className="shrink-0">
            <h3 className="text-[15px] font-semibold text-[#F4F4F3]">{t('dashboard:locked_section.title')}</h3>
            <p className="text-[13px] leading-[18px] text-[#9A9A9A] mt-0.5">
              {t(`dashboard:locked_section.subtitle_${archivedDocs.length === 1 ? 'one' : 'other'}`, { count: archivedDocs.length })}
            </p>
          </div>
          {/* Segunda vuelta (D8 y D2): en el telefono el texto y el boton van uno debajo del otro
              (PDF pag. 49) y el boton mide 44; desde 768, en un renglon con el boton de 36. */}
          <div className="shrink-0 min-h-11 flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-3 py-2.5 md:py-1.5 rounded-xl bg-[#241710] border border-[#3A2414]"
            data-testid="locked-franja">
            <p className="text-[13px] font-semibold text-[#F7A06A] flex items-start md:items-center gap-1.5 min-w-0">
              <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5 md:mt-0" />
              <span className="break-words md:truncate">{t(`dashboard:locked_section.bar_${archivedDocs.length === 1 ? 'one' : 'other'}`, { count: archivedDocs.length })}</span>
            </p>
            <button type="button" onClick={() => { navigate('/upgrade'); }}
              className="h-11 md:h-9 w-full md:w-auto px-3 rounded-lg bg-[#F27A2E] text-[#160A02] text-[13px] font-semibold shrink-0 whitespace-nowrap inline-flex items-center justify-center"
              data-testid="locked-upgrade-btn">
              {t('dashboard:locked_section.cta_upgrade')}
            </button>
          </div>
          <div
            className={`flex-1 min-h-[96px] md:min-h-0 overflow-y-auto pr-1 ${INNER_SCROLL_CLEARANCE_CLASS} dashboard-doc-scroll`}
            data-testid="locked-doc-list">
            <div className={REJILLA_DEL_PANEL}>
              {archivedDocs.map(tarjetaBloqueada)}
            </div>
          </div>
        </div>
      )}
      </div>{/* /documents region (flex-1 scroll area) */}
    </div>

    {/* Segunda vuelta (D18): F5, el panel de fotos en su ventana (720 en computadora y tablet; hoja
        desde abajo con el pie fijo en el telefono). Mismo contenido y misma conducta: la X es Cancel.
        Si la zona grande no esta montada, el selector de "+" vive aqui. */}
    {fotosPanel.length > 0 && (
      <VentanaDeFotos alCerrar={cerrarPanelFotos} cerrarApagado={subiendoFotos}
        etiqueta={t('dashboard:fotos.tipo')} etiquetaCerrar={t('common:cancel')}>
        {!(docTab === 'active' && sinDocumentos && !fallaDeCarga) && (
          <input ref={fileInputRef} type="file" className="hidden" accept={ACCEPT_SUBIDA} multiple onChange={handleUpload} />
        )}
        <PanelFotos fotos={fotosPanel} orden={ordenFotos} nombreInicial={nombreFotos}
          subiendo={subiendoFotos} error={errorFotos} t={t} enVentana
          onAlternar={(id) => setOrdenFotos((prev) => alternarEnOrden(prev, id))}
          onOrden={setOrdenFotos}
          onAgregarMas={() => fileInputRef.current?.click()}
          onCancelar={cerrarPanelFotos}
          onSubir={subirFotos} />
      </VentanaDeFotos>
    )}

    {/* Image Preview Modal */}
    {previewImage && (
      <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={() => setPreviewImage(null)} data-testid="image-preview-modal">
        <div className="relative max-w-3xl max-h-[80dvh] w-full" onClick={(e) => e.stopPropagation()}>
          {/* R-10 (censo de X): esta X vive ARRIBA de la imagen, fuera de la
              tarjeta, así que nunca se encimó con nada — sólo le faltaba blanco
              para el dedo (24px). La zona de 44pt se sale un poco hacia el
              telón, y ahí un toque ya cerraba el modal de todos modos. */}
          <button onClick={() => setPreviewImage(null)}
            className="modal-close-tap absolute -top-10 right-0 text-white/70 hover:text-white transition-colors"
            data-testid="close-preview-modal">
            <X className="w-6 h-6" />
          </button>
          <img src={`data:image/jpeg;base64,${previewImage.base64}`} alt={t(previewImage.esPpt ? 'dashboard:image_preview.slide_alt' : 'dashboard:image_preview.page_alt', { page: previewImage.page })}
            className="w-full h-auto max-h-[80dvh] object-contain rounded-xl" />
          {previewImage.page > 0 && (
            <p className="text-center text-xs text-[#6a6a6a] mt-2">{t(previewImage.esPpt ? 'dashboard:image_preview.slide_caption' : 'dashboard:image_preview.page_caption', { page: previewImage.page })}</p>
          )}
        </div>
      </div>
    )}

    {/* REMATE-E — AQUÍ VIVÍA EL AVISO DE SUSCRIPCIÓN (pro_nudge), y se eliminó.
        El censo del remate D lo demostró código muerto: su guarda pedía
        `user.show_subscription_nudge`, y ningún endpoint pone esa bandera en el objeto
        `user` de AuthContext (el backend la devuelve en la respuesta de créditos, o sea
        en `usage`, que es OTRO objeto). Nunca se pintó en producción, ni una vez.
        Firma de Carlos: se elimina, NO se reconecta. La bandera que stripe_routes.py
        escribe en la fila del usuario queda huérfana e inerte a propósito: no se mete
        mano a código de pagos para una limpieza sin efecto visible.
        Con el bloque se fueron su estado (`nudgeDismissed`), sus claves i18n
        (`pro_nudge.*` en EN y ES) y la constante de precio que sólo él usaba. */}

    {/* R3: curtain continuity — while the split modal is suppressed in favor of the pack
        modal, keep a backdrop mounted under both so the swap (both directions) never
        shows a frame without an overlay. */}
    {autoSplitModalSuppressed && autoSplitQueue.length > 0 && (
      <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" data-testid="modal-transition-backdrop" />
    )}

    {/* Credit Pack Modal */}
    <CreditPackModal
      open={showPackModal}
      onClose={() => {
        setShowPackModal(false);
        setPackPurchaseFromAutoSplit(false);
        // Restore AutoSplitModal visibility if user cancelled the pack purchase.
        // (No-op when pack was opened from non-auto-split flow — flag was already false.)
        setAutoSplitModalSuppressed(false);
        // ajustes-1-3-0 (Bloque B3): en la app, cerrar la compra de paquetes (comprada,
        // cancelada o cerrada) es el regreso al Panel.
        if (IS_APP_BUILD) regresarDeLaOferta();
      }}
      fromAutoSplit={packPurchaseFromAutoSplit}
    />

    {/* FESTEJO-PACKS — la palomita del retorno de Stripe (web). En la app la pinta
        el propio CreditPackModal al confirmar el recibo de Apple. */}
    {packSuccess && <PackSuccessModal onClose={() => setPackSuccess(false)} />}

    {/* Auto-Split Preview Modal — head of queue is currently shown; advance by dequeue */}
    {autoSplitQueue.length > 0 && (() => {
      const head = autoSplitQueue[0];
      // Active docs the user can delete to free a SLOT (pending parts never consume a slot).
      const activeDocsForDelete = docs
        .filter(d => d.status !== 'pending_credits' && d.status !== 'pending')
        .map(d => ({ id: d.id, filename: d.filename, status: d.status }));
      // post-t7-1 (H3): el aviso del documento entero: cada boton (y la X) lo da por visto y hace lo suyo
      if (head.aviso_entero) {
        return (
          <AutoSplitModal
            isOpen={!autoSplitModalSuppressed}
            preview={head}
            onClose={() => cerrarAvisoEntero(head)}
            onBuyPack={() => { cerrarAvisoEntero(head); setShowPackModal(true); }}
            onUpgrade={() => { cerrarAvisoEntero(head); navigate('/upgrade'); }}
            onGoPending={() => { cerrarAvisoEntero(head); navigate(PENDING_ROUTE); }}
            queuePosition={{ current: 1, total: autoSplitQueue.length }}
          />
        );
      }
      return (
        <AutoSplitModal
          isOpen={!autoSplitModalSuppressed}
          preview={sobreParaLaVentana(head, docs)}
          activeDocs={activeDocsForDelete}
          onProcess={handleAutoSplitProcess}
          onSavePending={handleAutoSplitSavePending}
          onClose={handleAutoSplitClose}
          onBuyPack={handleAutoSplitBuyPack}
          onUpgrade={handleAutoSplitUpgrade}
          onDeleteDoc={handleAutoSplitDeleteDoc}
          onContactSupport={handleAutoSplitContact}
          onGoPending={handleAutoSplitGoPending}
          onDiscard={handleAutoSplitDiscard}
          onGuestSignup={handleAutoSplitGuestSignup}
          duplicateGroup={head.duplicate_of_pending ? (pendingByParent[head.duplicate_of_pending.parent_doc_id] || null) : null}
          queuePosition={{ current: 1, total: autoSplitQueue.length }}
          confirmLoading={autoSplitConfirmLoading}
          affordabilityLoading={autoSplitHeadStale}
        />
      );
    })()}

    {/* remates-1-3-0b (P3, #514): la ventana "Ver temas" de un documento normal: la misma de las
        partes, montada solo mientras esta abierta. */}
    {temasDeDoc && (
      <TemasDeLaParteModal open onClose={() => setTemasDeDoc(null)} documento={temasDeDoc}
        userId={user?.id} t={t} volverA={volverATemas} />
    )}

    {/* dashboard-rediseno (H1): la ventana de Rename. */}
    {renombrando && (
      <VentanaRenombrar key={renombrando.documentId || `p:${renombrando.parentDocId}`}
        nombreActual={renombrando.nombre} alGuardar={guardarNombre} alCerrar={cerrarRenombrar}
        sinRed={sinRedAhora} t={t} existe={i18n?.exists ? (k) => i18n.exists(k) : undefined}
        volverA={renombrando.volverA} />
    )}

    {/* Insufficient Credits Modal */}
    {insufficientCredits && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="insufficient-credits-modal">
        <div className="bg-[#111] border border-[#222] rounded-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-md w-full mx-4 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-white">{t('dashboard:insufficient_credits.title')}</h3>
          </div>
          {/* ── TANDA L / L4.2 (censo #20) — AQUÍ SE PINTABA INGLÉS CRUDO DEL SERVIDOR ──
              `insufficientCredits.message` es la frase que el backend arma en inglés
              ("This document requires 3 credits but you only have 1.", documents.py:533)
              y llegaba a la pantalla TAL CUAL, también para un usuario en español. Los dos
              números que esa frase dice viajan aparte en el mismo sobre
              (`credits_required` / `credits_available`), así que traducirla no cuesta un
              dato: es la MISMA voz —y las mismas dos claves— que el Reintentar ya usaba.
              El `message` sigue siendo la red de abajo por si un sobre viejo llegara sin
              los números (ventana de despliegue), y sólo entonces. */}
          <p className="text-[#aaa] text-sm mb-4" data-testid="insufficient-credits-message">
            {insufficientCreditsCopy(insufficientCredits, t) || insufficientCredits.message}
          </p>
          {insufficientCredits.per_document && (
            <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl p-3 mb-4 max-h-40 overflow-y-auto">
              {insufficientCredits.per_document.map((doc, idx) => (
                <div key={idx} className="flex justify-between text-xs text-[#888] py-1 border-b border-[#1a1a1a] last:border-0">
                  <span className="truncate min-w-0 mr-2">{doc.filename}</span>
                  <span className="whitespace-nowrap text-white shrink-0">{t('dashboard:insufficient_credits.per_doc_line', { chars: doc.chars?.toLocaleString(), count: doc.credits })}</span>
                </div>
              ))}
            </div>
          )}
          {user?.is_guest ? (
            <>
              <Button
                data-testid="guest-create-account-btn"
                onClick={() => { logAnalyticsEvent('friction_credits', 'create_account_clicked'); setInsufficientCredits(null); setConvertModal('credits'); }}
                className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white h-11 rounded-xl font-medium"
              >
                {t('dashboard:insufficient_credits.guest_create_btn')}
              </Button>
              <p className="text-sm text-[#888] text-center mt-2">{t('dashboard:insufficient_credits.guest_caption', { free_limit: PLAN_LIMIT_LABELS.free })}</p>
              <p className="text-center text-xs text-[#555] mt-3">
                {t('dashboard:insufficient_credits.already_have_account')}
                <button
                  onClick={() => { logAnalyticsEvent('friction_credits', 'login_clicked'); setInsufficientCredits(null); navigate('/login'); }}
                  className="text-[#FF6B00] hover:text-[#ff8533] underline underline-offset-2"
                  data-testid="guest-login-link"
                >
                  {t('common:nav.log_in')}
                </button>
              </p>
            </>
          ) : (
            <div className="flex gap-3">
              <Button
                data-testid="buy-credit-packs-btn"
                onClick={() => { setInsufficientCredits(null); setShowPackModal(true); }}
                className="flex-1 bg-[#FF6B00] hover:bg-[#e55f00] text-white"
              >
                <ShoppingCart className="w-4 h-4 mr-2" /> {t('dashboard:insufficient_credits.buy_pack_btn')}
              </Button>
              {user?.plan !== 'pro' && (
                <Button
                  data-testid="upgrade-plan-btn"
                  onClick={() => { setInsufficientCredits(null); navigate('/upgrade'); }}
                  variant="outline"
                  className="flex-1 border-[#333] text-white hover:bg-[#1a1a1a]"
                >
                  <Crown className="w-4 h-4 mr-2" /> {t('dashboard:insufficient_credits.upgrade_btn')}
                </Button>
              )}
            </div>
          )}
          <button
            onClick={() => { setInsufficientCredits(null); logAnalyticsEvent('friction_credits', 'dismissed'); }}
            className="mt-3 w-full text-center text-xs text-[#666] hover:text-[#aaa] transition"
            data-testid="dismiss-insufficient-credits"
          >
            {t('dashboard:insufficient_credits.dismiss_btn')}
          </button>
        </div>
      </div>
    )}

    {/* Duplicate Archived Modal — the user re-uploaded a doc that's already in their library,
        archived from a plan change. Two exits: recover via upgrade (dominant), or delete the
        archived copy and re-upload (discreet). Closing (X / click-outside) is non-destructive. */}
    {duplicateArchived && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
        data-testid="duplicate-archived-modal"
        onClick={() => setDuplicateArchived(null)}
      >
        <div
          className="bg-[#111] border border-[#222] rounded-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-md w-full mx-4 shadow-2xl relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setDuplicateArchived(null)}
            className="modal-close-tap absolute top-4 right-4 text-[#555] hover:text-white transition"
            data-testid="duplicate-archived-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
              <Lock className="w-5 h-5 text-[#FF6B00]" />
            </div>
            <h3 className="text-lg font-semibold text-white pr-6">{t('dashboard:duplicate_archived.title')}</h3>
          </div>
          <p className="text-sm text-[#aaa] mb-5">{t('dashboard:duplicate_archived.body', { count: duplicateArchived.credits_cost ?? 1 })}</p>
          <Button
            data-testid="duplicate-archived-upgrade-btn"
            onClick={() => { setDuplicateArchived(null); navigate('/upgrade'); }}
            className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white h-11 rounded-xl font-medium"
          >
            <Crown className="w-4 h-4 mr-2" /> {t('dashboard:duplicate_archived.upgrade_btn')}
          </Button>
          <button
            onClick={() => { setDuplicateArchived(null); setDocTab('locked'); }}
            className="mt-3 w-full text-center text-xs text-[#FF6B00] hover:text-[#ff8533] underline underline-offset-2"
            data-testid="duplicate-archived-delete-link"
          >
            {t('dashboard:duplicate_archived.delete_link', { count: duplicateArchived.credits_cost ?? 1 })}
          </button>
          <p className="text-center text-[11px] text-[#555] mt-4">{t('dashboard:duplicate_archived.micro_note')}</p>
        </div>
      </div>
    )}

    {/* Guest Conversion Modal */}
    {langModal && (
      /* Quinta vuelta (D23): con el telefono acostado la ventana no cabia (el titulo y la X quedaban
         fuera arriba). Ahora mide como mucho lo que deja la pantalla menos su zona segura; el titulo, la
         explicacion, el buscador y la X se quedan a la vista y solo la lista se desplaza por dentro. */
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" data-testid="youtube-lang-modal">
        <div className="bg-[#111] border border-[#222] rounded-2xl p-6 w-full max-w-[420px] max-h-full flex flex-col shadow-2xl relative" data-testid="idiomas-caja">
          <button
            onClick={() => { setLangModal(null); setLangSearch(''); }}
            className="modal-close-tap absolute top-4 right-4 text-[#555] hover:text-white transition"
            data-testid="youtube-lang-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 mb-3 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
              <Video className="w-5 h-5 text-[#FF6B00]" />
            </div>
            <h3 className="text-lg font-semibold text-white pr-6">{t('dashboard:youtube.choose_language_title')}</h3>
          </div>
          <p className="text-sm text-[#888] mb-4 shrink-0">{t('dashboard:youtube.choose_language_desc')}</p>
          {langModal.langs.length > 8 && (
            <div className="relative mb-3 shrink-0">
              <Search className="w-4 h-4 text-[#555] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={langSearch}
                onChange={e => setLangSearch(e.target.value)}
                placeholder={t('dashboard:youtube.choose_language_search')}
                className="bg-[#0a0a0a] border-[#262626] text-white text-base h-11 w-full pl-9"
                data-testid="youtube-lang-search"
              />
            </div>
          )}
          {/* Tercera vuelta (D21, F4): renglones de 48 y el buscador de 44 con letra de 16 (caja de texto
              tactil). La lista crece a 312 para seguir ensenando seis renglones y medio. */}
          <div className="flex-1 min-h-0 max-h-[312px] overflow-y-auto space-y-1 pr-1" data-testid="idiomas-lista">
            {/* post-t7-1 (I1, #562): Ingles, Espanol, Frances y los demas en su orden de llegada */}
            {ordenarIdiomas(langModal.langs)
              .map(code => ({ code, name: langDisplayName(code) }))
              .filter(({ code, name }) => {
                const q = langSearch.trim().toLowerCase();
                return !q || code.toLowerCase().includes(q) || (name || '').toLowerCase().includes(q);
              })
              .map(({ code, name }) => (
                <button
                  key={code}
                  disabled={youtubeLoading}
                  onClick={() => importFromYoutube(langModal.url, code)}
                  className="w-full text-left px-3 py-2 min-h-12 rounded-lg text-sm text-[#ccc] hover:bg-[#1f1f1f] hover:text-white transition flex items-center justify-between disabled:opacity-50"
                  data-testid={`youtube-lang-${code}`}
                >
                  <span>{name}</span>
                  <span className="text-xs text-[#555] uppercase">{code}</span>
                </button>
              ))}
          </div>
          {youtubeLoading && (
            <div className="flex items-center justify-center gap-2 text-xs text-[#888] mt-3 shrink-0">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('dashboard:youtube.loading')}
            </div>
          )}
        </div>
      </div>
    )}

    {convertModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="guest-convert-modal">
        <div className="bg-[#111] border border-[#222] rounded-2xl p-6 w-full max-w-[420px] mx-4 shadow-2xl relative">
          {convertStep === 'form' ? (<>
          <button
            onClick={() => {
              if (convertStep === 'verify') cancelGuestConversion();
              setConvertModal(null);
              setConvertStep('form');
              setConvertForm({ name: '', email: '', password: '' });
              logAnalyticsEvent(convertModal === 'lifetime' ? 'friction_guest_lifetime' : 'friction_credits', 'dismissed');
            }}
            className="modal-close-tap absolute top-4 right-4 text-[#555] hover:text-white transition"
            data-testid="convert-modal-close"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
              <UserPlus className="w-5 h-5 text-[#FF6B00]" />
            </div>
            <h3 className="text-lg font-semibold text-white pr-6" data-testid="convert-modal-heading">
              {convertModal === 'lifetime'
                ? t('dashboard:convert_modal.heading_lifetime', { cap: GUEST_LIFETIME_CAP })
                : convertModal === 'credits'
                  ? t('dashboard:convert_modal.heading_credits')
                  : convertModal === 'youtube'
                    ? t('dashboard:youtube.guest_signup_title')
                    : t('dashboard:convert_modal.heading_save')}
            </h3>
          </div>

          <p className="text-sm text-[#888] mb-5" data-testid="convert-modal-subtext">
            {convertModal === 'lifetime'
              ? t('dashboard:convert_modal.body_lifetime', { free_limit: PLAN_LIMIT_LABELS.free })
              : convertModal === 'credits'
                ? t('dashboard:convert_modal.body_credits', { free_limit: PLAN_LIMIT_LABELS.free })
                : convertModal === 'youtube'
                  ? t('dashboard:youtube.guest_signup_desc')
                  : t('dashboard:convert_modal.body_save')}
          </p>

          <form className="space-y-3" onSubmit={async (e) => {
            e.preventDefault();
            if (converting || !convertForm.email || !convertForm.password || convertForm.password.length < 8) return;
            if (!EMAIL_REGEX.test(convertForm.email.trim())) { toast.error(t('dashboard:toasts.convert_form.invalid_email')); return; }
            setConverting(true);
            try {
              const res = await convertGuest({ ...convertForm, fingerprint: fingerprint || '' });
              setConvertPendingEmail(res.email || convertForm.email);
              setConvertExpectedCode(res.verification_code || '');
              setConvertCodigoNoSalio(res.email_sent === false && !res.verification_code);
              setConvertStep('verify');
            } catch (err) {
              const detail = err.response?.data?.detail;
              if (detail?.code === 'email_recently_deleted') {
                toast.error(t('auth:blocked.recently_deleted', {
                  date: formatDateLong(detail.blocked_until),
                }), { duration: 10000 });
              } else {
                toast.error(avisoDelFreno(err, t) || (typeof detail === 'string' && detail) || t('dashboard:toasts.convert_form.failed'));
              }
            } finally { setConverting(false); }
          }}>
            <Input
              id="convert-name"
              name="name"
              autoComplete="name"
              placeholder={t('dashboard:convert_modal.name_placeholder')}
              value={convertForm.name}
              onChange={e => setConvertForm(f => ({...f, name: e.target.value}))}
              className="bg-[#0a0a0a] border-[#262626] text-white text-sm h-10 w-full"
              data-testid="convert-name"
            />
            <Input
              type="email"
              name="email"
              autoComplete="username"
              placeholder={t('dashboard:convert_modal.email_placeholder')}
              value={convertForm.email}
              onChange={e => setConvertForm(f => ({...f, email: e.target.value}))}
              className="bg-[#0a0a0a] border-[#262626] text-white text-sm h-10 w-full"
              data-testid="convert-email"
            />
            <Input
              type="password"
              name="new_password"
              autoComplete="new-password"
              placeholder={t('dashboard:convert_modal.password_placeholder')}
              value={convertForm.password}
              onChange={e => setConvertForm(f => ({...f, password: e.target.value}))}
              className="bg-[#0a0a0a] border-[#262626] text-white text-sm h-10 w-full"
              data-testid="convert-password"
            />
            <Button type="submit"
              disabled={converting || !convertForm.email || !convertForm.password || convertForm.password.length < 8}
              className="w-full bg-[#FF6B00] hover:bg-[#E05A00] text-white h-10 rounded-xl font-medium"
              data-testid="convert-submit-btn"
            >
              {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('dashboard:convert_modal.submit_btn')}
            </Button>
            <LegalAcceptance variant="create" />
          </form>

          <p className="text-center text-xs text-[#555] mt-4">
            {t('dashboard:convert_modal.already_have_account')}
            <button
              onClick={() => { setConvertModal(null); setConvertStep('form'); navigate('/login'); }}
              className="text-[#FF6B00] hover:text-[#ff8533] underline underline-offset-2"
              data-testid="convert-login-link"
            >
              {t('common:nav.log_in')}
            </button>
          </p>

          {/* Same third-party entries as /signup — convert the guest IN-PLACE via the
              backend callbacks (preserves their documents). Reuse /signup's i18n keys
              (auth:signup.*) and button styling.
              WEB-ONLY: hidden in app builds (WebView can't run the OAuth redirect).
              The "or" divider goes with it — it exists solely to set them off.
              AS-5 (D-2): Apple ARRIBA de Google, igual que en las otras dos superficies.
              La entrada NATIVA de los dos va en el bloque de abajo, gateado al revés. */}
          {!IS_APP_BUILD && (<>
          <div className="relative mt-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#262626]" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#111] px-2 text-[#444]">{t('auth:signup.or_divider')}</span></div>
          </div>
          <Button type="button" variant="outline" onClick={() => irAProveedorWeb('apple')}
            disabled={!!pendienteWeb}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12 mt-4"
            data-testid="convert-apple-btn">
            {pendienteWeb === 'apple'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>}
            {t('auth:signup.apple_btn')}
          </Button>
          <Button type="button" variant="outline" onClick={() => irAProveedorWeb('google')}
            disabled={!!pendienteWeb}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12 mt-4"
            data-testid="convert-google-btn">
            {pendienteWeb === 'google'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
            {t('auth:signup.google_btn')}
          </Button>
          </>)}
          {/* AS-5 — la conversión de invitado por la puerta NATIVA. El bearer del invitado
              viaja solo (la instancia de axios lo adjunta en todo request del build de
              app) y el backend lo reconoce por la sesión, igual que la rama web lo
              reconoce por la cookie: la conversión es EN SITIO y conserva sus documentos.
              El divisor entra con ellos, como en la rama web. */}
          {IS_APP_BUILD && (<>
          <div className="relative mt-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#262626]" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#111] px-2 text-[#444]">{t('auth:signup.or_divider')}</span></div>
          </div>
          <Button type="button" variant="outline" onClick={entrarConApple} disabled={!!pendienteSocial}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12 mt-4"
            data-testid="convert-apple-native-btn">
            {pendienteSocial === 'apple'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>}
            {t('auth:signup.apple_btn')}
          </Button>
          <Button type="button" variant="outline" onClick={entrarConGoogle} disabled={!!pendienteSocial}
            className="w-full border-[#262626] text-[#e5e5e5] hover:text-white hover:bg-[#181818] rounded-xl h-12 mt-4"
            data-testid="convert-google-native-btn">
            {pendienteSocial === 'google'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>}
            {t('auth:signup.google_btn')}
          </Button>
          </>)}
          </>) : (
            <ConvertVerifyStep
              email={convertPendingEmail}
              expectedCode={convertExpectedCode}
              codigoNoSalio={convertCodigoNoSalio}
              onSuccess={() => {
                setConvertModal(null);
                setConvertStep('form');
                setConvertForm({ name: '', email: '', password: '' });
                toast.success(t('dashboard:toasts.convert_verify.success'));
              }}
              onBack={async () => {
                await cancelGuestConversion();
                setConvertStep('form');
                setConvertForm({ name: '', email: '', password: '' });
              }}
              onFailLimit={() => {
                setConvertStep('form');
                setConvertForm({ name: '', email: '', password: '' });
              }}
              verifyGuestConversion={verifyGuestConversion}
              resendGuestConversion={resendGuestConversion}
            />
          )}
        </div>
      </div>
    )}

    {/* Guest split funnel: restoration veil — funnel arrivals only. Blocks the 1-2s
        interaction gap between landing and the modal reopening; the 10s timeout in
        useGuestRestoreVeil guarantees it can never stick. */}
    {showRestoreVeil && (
      <div
        className="fixed inset-0 z-[80] flex flex-col items-center justify-center gap-3 bg-black/80 backdrop-blur-sm"
        data-testid="guest-restore-overlay"
        aria-busy="true"
      >
        <Loader2 className="w-8 h-8 text-[#FF6B00] animate-spin" />
        <p className="text-sm text-[#cccccc]">{t('dashboard:auto_split.guest_restoring_overlay')}</p>
      </div>
    )}

    {/* Guest split funnel F3: staged document expired before the signup round-trip
        finished. Friendly recovery — re-upload now processes in parts (they have an
        account). Only reachable when the funnel flag was present (lib gates it). */}
    {guestDocExpired && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="guest-doc-expired-modal">
        <div className="bg-[#111] border border-[#222] rounded-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] max-w-md w-full mx-4 shadow-2xl relative">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B00]/10 flex items-center justify-center shrink-0">
              <Clock className="w-5 h-5 text-[#FF6B00]" />
            </div>
            <h3 className="text-lg font-semibold text-white pr-6" data-testid="guest-doc-expired-title">
              {t('dashboard:auto_split.guest_expired_title')}
            </h3>
          </div>
          <p className="text-sm text-[#888] mb-5" data-testid="guest-doc-expired-body">
            {t('dashboard:auto_split.guest_expired_body')}
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <button
              onClick={() => setGuestDocExpired(false)}
              className="flex-1 border border-[#333] text-[#a0a0a0] hover:text-white hover:border-[#555] h-11 rounded-xl text-sm transition-colors"
              data-testid="guest-doc-expired-dismiss">
              {t('dashboard:auto_split.cancel_button')}
            </button>
            <button
              onClick={() => { setGuestDocExpired(false); (headerUploadInputRef.current || fileInputRef.current)?.click(); }}
              className="flex-1 bg-[#FF6B00] hover:bg-[#E05A00] text-white font-medium h-11 rounded-xl text-sm transition-colors"
              data-testid="guest-doc-expired-upload">
              {t('dashboard:auto_split.guest_expired_cta')}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
